export enum PermissionType {
	DENIED = "denied",
	GRANTED = "granted",
	UNSELECTED = "unselected",
	WITH_OWNER = "with-owner",
}

function isPermissionType(value: string): value is PermissionType {
	return Object.values(PermissionType).includes(value as PermissionType);
}
function toPermissionType(
	value: string,
	defaultValue: PermissionType = PermissionType.UNSELECTED,
): PermissionType {
	if (Object.values(PermissionType).includes(value as PermissionType)) {
		return value as PermissionType;
	}
	return defaultValue;
}
export class Address {
	constructor(
		public country: string = "",
		public gid: string = "",
		public locality: string = "",
		public number: string = "",
		public postal_code: string = "",
		public raw: string = "",
		public region: string = "",
		public street: string = "",
		public unit: string = "",
		public location?: Location,
	) {}
}
export interface APIError {
	message: string;
}
export interface Area {
	areaSquareMeters: number;
	geometry: GeoJSON.Geometry | null;
	min: ServiceAreaLocation;
	max: ServiceAreaLocation;
}

export interface TegolaURLs {
	nidus: string;
	rmo: string;
}
export interface Version {
	build_time: string;
	is_modified: boolean;
	revision: string;
}
export interface APIProperties {
	environment: string;
	sentry_dsn: string;
	tegola: TegolaURLs;
	version: Version;
}
export interface BoundsDTO {
	min: Location;
	max: Location;
}

// One cached USDA NAIP reference AOI (GET /api/sudo/naip/areas), in WGS84.
export interface NaipArea {
	name: string;
	epsg: number;
	minLng: number;
	minLat: number;
	maxLng: number;
	maxLat: number;
}

export class Bounds {
	min: Location;
	max: Location;
	constructor(
		min: Location = new Location(90, 180),
		max: Location = new Location(-90, -180),
	) {
		this.min = min;
		this.max = max;
	}
	addLocation(l: Location) {
		this.min.latitude = Math.min(this.min.latitude, l.latitude);
		this.min.longitude = Math.min(this.min.longitude, l.longitude);
		this.max.latitude = Math.max(this.max.latitude, l.latitude);
		this.max.longitude = Math.max(this.max.longitude, l.longitude);
	}
	isEmpty() {
		return (
			this.min.latitude == 90 &&
			this.min.longitude == 180 &&
			this.max.latitude == -90 &&
			this.max.longitude == -180
		);
	}
}
/** One address a contact is reachable at, as carried on the contact payload.
 * Contacts have no address column of their own: these are the addresses of the
 * Sites the contact is linked to, carried in components so the list can match a
 * partial street line. */
export interface ContactAddressDTO {
	number: string;
	street: string;
	unit: string;
	locality: string;
	region: string;
	postal_code: string;
}
/** One address a contact filed a public report about. Distinct from
 * ContactAddressDTO, which is a place the contact is linked to through a Site:
 * a reported address is only a place they reported a problem at. */
export interface ContactReportedAddressDTO extends ContactAddressDTO {
	/** The report's free-text address, set only when it never resolved to an
	 * address row; the components are then empty. */
	raw: string;
	report_type: string;
	report_public_id: string;
	/** How many reports named this same place; the label carries the newest. */
	report_count: number;
}
export class Contact {
	addresses: ContactAddressDTO[];
	emails: string[];
	id: string;
	messages: Message[];
	name: string;
	phones: Phone[];
	reported_addresses: ContactReportedAddressDTO[];
	uri: string;
	constructor(options?: ContactOptions) {
		this.addresses = options?.addresses ?? [];
		this.emails = options?.emails ?? [];
		this.id = options?.id ?? "";
		this.messages = options?.messages ?? [];
		this.name = options?.name ?? "";
		this.phones = options?.phones ?? [];
		this.reported_addresses = options?.reported_addresses ?? [];
		this.uri = options?.uri ?? "";
	}
	static fromJSON(json: ContactDTO): Contact {
		return new Contact({
			addresses: json.addresses ?? [],
			emails: json.emails,
			id: json.id.toString(),
			messages: json.messages.map((m: MessageDTO) => Message.fromJSON(m)),
			name: json.name,
			phones: json.phones,
			reported_addresses: json.reported_addresses ?? [],
			uri: json.uri,
		});
	}
}
export interface ContactDTO {
	addresses: ContactAddressDTO[];
	emails: string[];
	id: number;
	messages: MessageDTO[];
	name: string;
	phones: Phone[];
	reported_addresses: ContactReportedAddressDTO[];
	uri: string;
}
export interface ContactOptions {
	addresses?: ContactAddressDTO[];
	emails?: string[];
	id?: string;
	messages?: Message[];
	name?: string;
	phones?: Phone[];
	reported_addresses?: ContactReportedAddressDTO[];
	uri?: string;
}
export interface ContactSimpleOptions {
	email?: string;
	name?: string;
	phone?: PhoneSimple;
	uri?: string;
}
export class ContactSimple {
	email: string;
	name: string;
	phone: PhoneSimple;
	uri: string;
	constructor(options?: ContactSimpleOptions) {
		this.email = options?.email ?? "";
		this.name = options?.name ?? "";
		this.phone = options?.phone ?? {
			can_sms: true,
			number: "",
		};
		this.uri = options?.uri ?? "";
	}
}
export interface District {
	id: number;
	name: string;
	phone_office: string;
	slug: string;
	uri: string;
	url_logo: string;
	url_website: string;
}
export class Location {
	accuracy?: number;
	latitude: number;
	longitude: number;
	constructor(latitude: number = 0, longitude: number = 0, accuracy?: number) {
		this.accuracy = accuracy;
		this.latitude = latitude;
		this.longitude = longitude;
	}
}
export interface GeocodeSuggestion {
	detail: string;
	gid: string;
	locality: string;
	type: string;
}
export interface Geocode {
	address: Address;
	cell: number;
	lng: number;
	lat: number;
}

/**
 * One entry in the hierarchical address search (`/api/search/address`).
 * kind distinguishes the visual tier: situs (parcel-correlated address, with a
 * map location from its parcel) vs geocode (external geocoding suggestion).
 */
export interface AddressSearchResult {
	kind: "situs" | "geocode";
	id?: number;
	address?: string;
	locality?: string;
	poolCount?: number | null;
	lng?: number | null;
	lat?: number | null;
	gid?: string;
	detail?: string;
	type?: string;
	/** The org Site this result resolves to, when the situs tier matched one.
	 * The workbench opens that Site's popup; a result without it only flies
	 * to the location. */
	site_id?: number | null;
}
export interface LogEntryDTO {
	created: string;
	message: string;
	type: string;
	user_id: number;
}
export interface CSVPoolDetailCount {
	existing: number;
	new: number;
	outside: number;
}
export interface CSVPoolError {
	column: number;
	line: number;
	message: string;
}
export interface Followup {
	description: string;
	id: number;
	title: string;
}
export interface ComplianceReportRequest {
	id: number;
	public_id: string;
}
export class LogEntry {
	constructor(
		public created: Date,
		public message: string,
		public type: string,
		public user_id: number,
	) {}
	static fromJSON(json: LogEntryDTO): LogEntry {
		return new LogEntry(
			new Date(json.created),
			json.message,
			json.type,
			json.user_id,
		);
	}
}
export interface Exif {
	created: string;
	make: string;
	model: string;
}
export interface Image {
	distance_from_reporter_meters?: number;
	exif: Exif;
	location?: Location;
	report_id: number;
	url_content: string;
	uuid: string;
}
export interface ComplianceUpdate {
	access_instructions?: string;
	address?: Address;
	availability_notes?: string;
	comments?: string;
	gate_code?: string;
	has_dog?: boolean;
	//images?: Image[];
	location?: Location;
	permission_type?: string;
	reporter?: ContactSimple;
	submitted?: string;
	//uri: string;
	wants_scheduled?: boolean;
}
export interface Concern {
	type: string;
	url: string;
}
export interface PublicReportDTO {
	address: Address;
	created: string;
	district: string;
	images: Image[];
	location: Location;
	log: LogEntryDTO[];
	public_id: string;
	reporter: ContactSimple;
	status: string;
	type: string;
	uri: string;
}
export interface PublicReportUpdate {
	address?: Address;
	created?: string;
	district?: string;
	images?: Image[];
	location?: Location;
	public_id?: string;
	reporter?: ContactSimple;
	status?: string;
	type?: string;
	uri?: string;
}
export interface PublicReportComplianceCreateRequest {
	client_id: string;
	district?: string;
	mailer_id?: string;
}
export interface PublicReportOptions {
	address: Address;
	created: Date;
	district: string;
	images: Image[];
	location: Location;
	log: LogEntry[];
	public_id: string;
	reporter: ContactSimple;
	status: string;
	type: string;
	uri: string;
}
export class PublicReport {
	address: Address;
	created: Date;
	district: string;
	images: Image[];
	log: LogEntry[];
	public_id: string;
	reporter: ContactSimple;
	status: string;
	type: string;
	uri: string;
	location?: Location;
	constructor(options?: PublicReportOptions) {
		this.address = options?.address ?? new Address();
		this.created = options?.created ?? new Date();
		this.district = options?.district ?? "";
		this.images = options?.images ?? [];
		this.log = options?.log ?? [];
		this.public_id = options?.public_id ?? "";
		this.reporter = options?.reporter ?? new ContactSimple();
		this.status = options?.status ?? "";
		this.type = options?.type ?? "";
		this.uri = options?.uri ?? "";
		this.location = options?.location ?? new Location();
	}
	static fromJSON(json: PublicReportDTO): PublicReport {
		switch (json.type) {
			case "compliance":
				return PublicReportCompliance.fromJSON(
					json as PublicReportComplianceDTO,
				);
			case "nuisance":
				return PublicReportNuisance.fromJSON(json as PublicReportNuisanceDTO);
			case "water":
				return PublicReportWater.fromJSON(json as PublicReportWaterDTO);
			default:
				throw new Error(`Unrecognized public report type '${json.type}'`);
		}
	}
}
export interface PublicReportComplianceDTO extends PublicReportDTO {
	access_instructions: string;
	availability_notes: string;
	comments: string;
	concerns: Concern[];
	gate_code: string;
	has_dog: boolean;
	permission_type: PermissionType;
	submitted: string;
	wants_scheduled: boolean;
}
export interface PublicReportComplianceOptions extends PublicReportOptions {
	access_instructions: string;
	availability_notes: string;
	comments: string;
	concerns: Concern[];
	gate_code: string;
	has_dog: boolean;
	permission_type: PermissionType;
	submitted?: Date;
	wants_scheduled: boolean;
}
export interface PublicReportComplianceUpdate extends PublicReportUpdate {
	access_instructions?: string;
	availability_notes?: string;
	comments?: string;
	gate_code?: string;
	has_dog?: boolean;
	permission_type?: PermissionType;
	submitted?: string;
	wants_scheduled?: boolean;
}
export class PublicReportCompliance extends PublicReport {
	access_instructions: string;
	availability_notes: string;
	comments: string;
	concerns: Concern[];
	gate_code: string;
	has_dog: boolean;
	permission_type: PermissionType;
	submitted?: Date;
	wants_scheduled: boolean;
	constructor(options?: PublicReportComplianceOptions) {
		super(options);
		this.access_instructions = options?.access_instructions ?? "";
		this.availability_notes = options?.availability_notes ?? "";
		this.comments = options?.comments ?? "";
		this.concerns = options?.concerns ?? [];
		this.gate_code = options?.gate_code ?? "";
		this.has_dog = options?.has_dog ?? false;
		this.permission_type = toPermissionType(
			options?.permission_type ?? PermissionType.UNSELECTED,
		);
		this.submitted = options?.submitted
			? new Date(options!.submitted)
			: undefined;
		this.wants_scheduled = options?.wants_scheduled ?? false;
	}
	static fromJSON(json: PublicReportComplianceDTO): PublicReportCompliance {
		return new PublicReportCompliance({
			...json,
			created: new Date(json.created),
			log: json.log.map((l: LogEntryDTO) => LogEntry.fromJSON(l)),
			submitted: json.submitted ? new Date(json.submitted) : undefined,
		});
	}
}
export interface PublicReportNuisanceDTO extends PublicReportDTO {
	additional_info: string;
	duration: string;
	is_location_backyard: boolean;
	is_location_frontyard: boolean;
	is_location_garden: boolean;
	is_location_other: boolean;
	is_location_pool: boolean;
	source_container: boolean;
	source_description: string;
	source_gutter: boolean;
	source_stagnant: boolean;
	time_of_day_day: boolean;
	time_of_day_early: boolean;
	time_of_day_evening: boolean;
	time_of_day_night: boolean;
}
export interface PublicReportNuisanceOptions extends PublicReportOptions {
	additional_info: string;
	duration: string;
	is_location_backyard: boolean;
	is_location_frontyard: boolean;
	is_location_garden: boolean;
	is_location_other: boolean;
	is_location_pool: boolean;
	source_container: boolean;
	source_description: string;
	source_gutter: boolean;
	source_stagnant: boolean;
	time_of_day_day: boolean;
	time_of_day_early: boolean;
	time_of_day_evening: boolean;
	time_of_day_night: boolean;
}
export class PublicReportNuisance extends PublicReport {
	additional_info: string;
	duration: string;
	is_location_backyard: boolean;
	is_location_frontyard: boolean;
	is_location_garden: boolean;
	is_location_other: boolean;
	is_location_pool: boolean;
	source_container: boolean;
	source_description: string;
	source_gutter: boolean;
	source_stagnant: boolean;
	time_of_day_day: boolean;
	time_of_day_early: boolean;
	time_of_day_evening: boolean;
	time_of_day_night: boolean;
	constructor(options: PublicReportNuisanceOptions) {
		super(options);
		this.additional_info = options.additional_info;
		this.duration = options.duration;
		this.is_location_backyard = options.is_location_backyard;
		this.is_location_frontyard = options.is_location_frontyard;
		this.is_location_garden = options.is_location_garden;
		this.is_location_other = options.is_location_other;
		this.is_location_pool = options.is_location_pool;
		this.source_container = options.source_container;
		this.source_description = options.source_description;
		this.source_gutter = options.source_gutter;
		this.source_stagnant = options.source_stagnant;
		this.time_of_day_day = options.time_of_day_day;
		this.time_of_day_early = options.time_of_day_early;
		this.time_of_day_evening = options.time_of_day_evening;
		this.time_of_day_night = options.time_of_day_night;
	}
	static fromJSON(json: PublicReportNuisanceDTO): PublicReportNuisance {
		return new PublicReportNuisance({
			...json,
			created: new Date(json.created),
			log: json.log.map((l: LogEntryDTO) => LogEntry.fromJSON(l)),
		});
	}
}

export interface PublicReportWaterDTO extends PublicReportDTO {
	access_comments: string;
	access_gate: boolean;
	access_fence: boolean;
	access_locked: boolean;
	access_dog: boolean;
	access_other: boolean;
	comments: string;
	has_adult: boolean;
	has_backyard_permission: boolean;
	has_larvae: boolean;
	has_pupae: boolean;
	is_reporter_confidential: boolean;
	is_reporter_owner: boolean;
	owner: ContactSimple;
}
export interface PublicReportWaterOptions extends PublicReportOptions {
	access_comments: string;
	access_gate: boolean;
	access_fence: boolean;
	access_locked: boolean;
	access_dog: boolean;
	access_other: boolean;
	comments: string;
	has_adult: boolean;
	has_backyard_permission: boolean;
	has_larvae: boolean;
	has_pupae: boolean;
	is_reporter_confidential: boolean;
	is_reporter_owner: boolean;
	owner: ContactSimple;
}
export class PublicReportWater extends PublicReport {
	access_comments: string;
	access_gate: boolean;
	access_fence: boolean;
	access_locked: boolean;
	access_dog: boolean;
	access_other: boolean;
	comments: string;
	has_adult: boolean;
	has_backyard_permission: boolean;
	has_larvae: boolean;
	has_pupae: boolean;
	is_reporter_confidential: boolean;
	is_reporter_owner: boolean;
	owner: ContactSimple;
	constructor(options: PublicReportWaterOptions) {
		super(options);
		this.access_comments = options.access_comments;
		this.access_gate = options.access_gate;
		this.access_fence = options.access_fence;
		this.access_locked = options.access_locked;
		this.access_dog = options.access_dog;
		this.access_other = options.access_other;
		this.comments = options.comments;
		this.has_adult = options.has_adult;
		this.has_backyard_permission = options.has_backyard_permission;
		this.has_larvae = options.has_larvae;
		this.has_pupae = options.has_pupae;
		this.is_reporter_confidential = options.is_reporter_confidential;
		this.is_reporter_owner = options.is_reporter_owner;
		this.owner = options.owner;
	}
	static fromJSON(json: PublicReportWaterDTO): PublicReportWater {
		return new PublicReportWater({
			...json,
			created: new Date(json.created),
			log: json.log.map((l: LogEntryDTO) => LogEntry.fromJSON(l)),
		});
	}
}
/*
	address: new Address(),
	comments: "",
	contact: {
		name: "",
		phone: "",
		can_sms: true,
		email: "",
	},
	id: "",
	images: [],
	location: {
		latitude: 0,
		longitude: 0,
	},
	permission: {
		access: PermissionType.UNSELECTED,
		access_instructions: "",
		availability_notes: "",
		gate_code: "",
		has_dog: false,
		wants_scheduled: false,
	},
	uri: "",
});
*/
export interface ResourceStubDTO {
	created: string;
	type: string;
	uri: string;
}
export class ResourceStub {
	constructor(
		public created: Date,
		public type: string,
		public uri: string,
	) {}
	static fromJSON(json: ResourceStubDTO): ResourceStub {
		return new ResourceStub(new Date(json.created), json.type, json.uri);
	}
}
// CommunicationType is §16's Communication Type: the channel an exchange came
// through, as the API states it (the `communication.type_` enum's spellings,
// mirrored in resource/communication.go). It is the communication's own
// property, not inferred from whichever record it relates to.
export type CommunicationType =
	| "unknown"
	| "email"
	| "text"
	| "phone_call"
	| "paper_letter"
	| "in_person"
	| "public_report"
	| "virtual_inspection";
// CommunicationStatus is the `communicationstatus` enum's spellings: where a
// communication sits in its handling, as the API states it (mirrored from
// db/gen/nidus-sync/public/model/communicationstatus.go and the buckets in
// platform/communication.go). It is the communication's own property.
export type CommunicationStatus =
	| "new"
	| "opened"
	| "pending"
	| "closed"
	| "resolved"
	| "possible-issue"
	| "possible-resolved"
	| "invalid";
// CommunicationStatusBucket names the two slices of that enum a list query can
// ask for.
export type CommunicationStatusBucket = "open" | "closed";
/**
 * The partition of the `communicationstatus` enum the workbench works in. The
 * server's `open` bucket — the statuses `GET /api/communication` returns when
 * no `status` parameter is given, and so the default view — is exactly the
 * list below; `closed` is its complement, fetched only when the operator asks.
 * Every status belongs to one bucket and no status belongs to both.
 *
 * This is the vocabulary's single statement: the communication list component
 * and `@/components/communicationFilters` both consume it, so a status the enum
 * grows is placed in a bucket here and becomes visible and filterable without
 * being spelled out again.
 */
export const COMMUNICATION_STATUS_BUCKETS: Record<
	CommunicationStatusBucket,
	readonly CommunicationStatus[]
> = {
	open: ["new", "opened", "pending"],
	closed: [
		"closed",
		"resolved",
		"possible-issue",
		"possible-resolved",
		"invalid",
	],
};
/** What put a communication in its current status: the status the record was
 * moved to, when that happened, and who did it. `user` is null when no actor is
 * recorded, which is not the same as an unnamed person. */
export interface CommunicationStatusUpdateDTO {
	created: string;
	status: string;
	user: string | null;
}
export class CommunicationStatusUpdate {
	constructor(
		public created: Date,
		public status: string,
		public user: string | null,
	) {}
	static fromJSON(
		json: CommunicationStatusUpdateDTO,
	): CommunicationStatusUpdate {
		return new CommunicationStatusUpdate(
			new Date(json.created),
			json.status,
			json.user ?? null,
		);
	}
}
export interface CommunicationDTO {
	body?: string | null;
	address_raw?: string | null;
	contact: ContactDTO;
	created: string;
	id: string;
	is_phone_intake: boolean;
	/** Absolute URI of GET /api/communication/{id}/parcel (always present). */
	parcel_uri: string;
	related: ResourceStubDTO[];
	response: string;
	source: string;
	status: string;
	status_update: CommunicationStatusUpdateDTO;
	transferred_from?: string;
	type: CommunicationType;
	uri: string;
	urls: {
		opened: string;
		response: string;
	};
}
export class Communication {
	constructor(
		public body: string | null,
		public address_raw: string | null,
		public contact: Contact,
		public created: Date,
		public id: string,
		public is_phone_intake: boolean,
		public related: ResourceStub[],
		public response: string,
		public source: string,
		public status: string,
		public status_update: CommunicationStatusUpdate,
		public transferred_from: string | null,
		public type: CommunicationType,
		public uri: string,
		public urls: CommunicationURLs,
		public parcel_uri: string = "",
	) {}
	static fromJSON(json: CommunicationDTO): Communication {
		return new Communication(
			json.body ?? null,
			json.address_raw ?? null,
			Contact.fromJSON(json.contact),
			new Date(json.created),
			json.id,
			json.is_phone_intake,
			json.related.map((r) => ResourceStub.fromJSON(r)),
			json.response,
			json.source,
			json.status,
			CommunicationStatusUpdate.fromJSON(json.status_update),
			json.transferred_from ?? null,
			json.type,
			json.uri,
			json.urls,
			json.parcel_uri,
		);
	}
}
interface CommunicationURLs {
	opened: string;
	response: string;
}

// ---------------------------------------------------------------------------
// Virtual inspection ("Mosquito Check") — shared by the sync (staff) and RMO
// (resident) SPAs. Shapes must match platform/types/virtualinspection.go and
// local://vi-contract.md.
// ---------------------------------------------------------------------------
export type VirtualInspectionStatus = "requested" | "started" | "submitted";
export type OffPropertyCategory = "neighbor" | "public" | "agricultural";
export interface VirtualInspectionHabitat {
	key: string;
	zone: string;
	media: string | null;
}
/** One photo attached to an observation or an off-property report. `id` is the
 *  per-photo handle used to remove it; `uuid` addresses the stored image. */
export interface VirtualInspectionPhoto {
	id: string;
	uuid: string;
}
export interface VirtualInspectionObservation {
	habitat_key: string;
	present: boolean;
	larvae_present: boolean | null;
	already_fixed: boolean | null;
	/** Capture order; a source can carry several photos. */
	photos: VirtualInspectionPhoto[];
}
export interface VirtualInspectionOffProperty {
	category: OffPropertyCategory;
	description: string | null;
	/** Capture order; a source can carry several photos. */
	photos: VirtualInspectionPhoto[];
}
export interface VirtualInspectionContact {
	name: string;
	email: string;
	phone: string;
}
export interface VirtualInspectionState {
	status: VirtualInspectionStatus;
	language: "en" | "es";
	logo_url: string;
	org_name: string;
	phone_office: string;
	property_address: string | null;
	geom: { latitude: number; longitude: number } | null;
	contact_id: number | null;
	contact: VirtualInspectionContact | null;
	habitats: VirtualInspectionHabitat[];
	observations: VirtualInspectionObservation[];
	off_property: VirtualInspectionOffProperty[];
}
export interface VirtualInspectionSummary {
	id: string;
	uri: string;
	status: VirtualInspectionStatus;
	language: string;
	created: string;
	confirmed: number;
	clear: number;
	off_property_count: number;
}
export interface VirtualInspectionDetail {
	id: string;
	uri: string;
	status: VirtualInspectionStatus;
	language: string;
	created: string;
	submitted_at: string | null;
	property_address: string | null;
	geom: { latitude: number; longitude: number } | null;
	contact: Contact | null;
	observations: VirtualInspectionObservation[];
	off_property: VirtualInspectionOffProperty[];
	communication: { id: string; uri: string; status: string } | null;
	has_link: boolean;
	link_clicks: number;
	link_last_clicked_at: string | null;
}
export interface VirtualInspectionSubmitRequest {
	resident_name?: string;
	resident_phone?: string;
	resident_email?: string;
}
export interface VirtualInspectionContactRequest {
	name?: string;
	phone?: string;
	email?: string;
	property_address?: string;
	geom?: { latitude: number; longitude: number } | null;
}
export interface VirtualInspectionObservationRequest {
	habitat_key: string;
	present: boolean;
	larvae_present?: boolean | null;
	already_fixed?: boolean | null;
}
export interface VirtualInspectionOffPropertyRequest {
	category: OffPropertyCategory;
	description?: string;
}
export interface VirtualInspectionLanguageRequest {
	language: "en" | "es";
}
// The sync resource store needs a fromJSON converter (PublicReport pattern);
// declaration-merged with the interface above so the type shape stays exact.
export class VirtualInspectionDetail {
	constructor(init: VirtualInspectionDetail) {
		Object.assign(this, init);
	}
	static fromJSON(json: VirtualInspectionDetail): VirtualInspectionDetail {
		return new VirtualInspectionDetail(json);
	}
}
export enum MessageType {
	Unknown = 0,
	Email = 1,
	Mailer = 2,
	Text = 3,
}

export interface MessageDTO {
	content: string;
	created: string;
	destination: string;
	source: string;
	type: number;
}
export interface MessageOptions {
	content?: string;
	created?: Date;
	destination?: string;
	source?: string;
	type?: MessageType;
}
export class Message {
	content: string;
	created: Date;
	destination: string;
	source: string;
	type: MessageType;
	constructor(options: MessageOptions) {
		this.content = options.content ?? "";
		this.created = options.created ?? new Date();
		this.destination = options.destination ?? "";
		this.source = options.source ?? "";
		this.type = options.type ?? MessageType.Unknown;
	}
	static fromJSON(json: MessageDTO): Message {
		return new Message({
			content: json.content,
			created: new Date(json.created),
			destination: json.destination,
			source: json.source,
			type: json.type as MessageType,
		});
	}
}

export interface Pool {
	condition: string;
	id: number;
	location: Location;
	site: Site;
}
export interface SignalDTO {
	address?: Address;
	addressed?: string;
	addressor?: number;
	created: string;
	creator: number;
	id: number;
	location: Location;
	pool?: Pool;
	report?: PublicReport;
	species?: string;
	type: string;
}
export class Signal {
	constructor(
		public created: Date,
		public creator: number,
		public id: number,
		public location: Location,
		public type: string,
		public address?: Address,
		public addressed?: string,
		public addressor?: number,
		public pool?: Pool,
		public report?: PublicReport,
		public species?: string,
	) {}
	static fromJSON(json: SignalDTO): Signal {
		return new Signal(
			new Date(json.created),
			json.creator,
			json.id,
			json.location,
			json.type,
			json.address,
			json.addressed,
			json.addressor,
			json.pool,
			json.report,
			json.species,
		);
	}
}
export interface Parcel {
	apn: string;
	description: string;
	id: number;
}
export interface MunicipalParcel {
	id: number;
	external_id: string;
	address_raw: string;
	description: string;
	property_type: string;
	geometry_geojson?: GeoJSON.Geometry;
	situs?: Situs | null;
}

export interface Situs {
	address_raw?: string | null;
	lot_size_sqft?: number | null;
	lot_size_acres?: number | null;
	year_built?: number | null;
	bedrooms?: number | null;
	full_baths?: number | null;
	half_baths?: number | null;
	building_sqft?: number | null;
	num_units?: number | null;
	property_type?: string | null;
	net_assessed_value?: number | null;
	land_value?: number | null;
	fireplace_count?: number | null;
	pool_count?: number | null;
	tax_rate_area?: string | null;
	raw_data?: any | null;
}

/**
 * The complete assessor (situs) record returned by GET /api/municipal/situs/{id}
 * — the full municipal.situs row, rendered in the detail panel's site view.
 */
export interface MunicipalSitusDetail {
	id: number;
	external_id: string;
	address_raw: string;
	assessment_type: string;
	assessment_description: string;
	assessment_status: string;
	document_date: string;
	property_type?: string | null;
	lot_type: string;
	lot_size_acres?: number | null;
	lot_size_sqft?: number | null;
	year_built?: number | null;
	bedrooms?: number | null;
	full_baths?: number | null;
	half_baths?: number | null;
	num_units?: number | null;
	building_sqft?: number | null;
	unfinished_sqft?: number | null;
	garage_size?: number | null;
	fireplace_count?: number | null;
	pool_count?: number | null;
	land_value?: number | null;
	net_assessed_value?: number | null;
	total_land_improvements?: number | null;
	structural_improvement?: number | null;
	homeowners_exemption?: number | null;
	personal_property_value?: number | null;
	fixtures_real_property?: number | null;
	fixtures_personal_property?: number | null;
	growing_improvement?: number | null;
	other_exemptions?: number | null;
	tax_rate_area?: string | null;
	manufactured_homes?: boolean | null;
	owner?: string | null;
	address_number?: string | null;
	address_street?: string | null;
	address_line_1?: string | null;
	address_line_2?: string | null;
	address_line_3?: string | null;
	address_line_4?: string | null;
	address_unit?: string | null;
	address_locality?: string | null;
	address_region?: string | null;
	address_postal_code?: string | null;
	/**
	 * The source's own field set the sync could not map to a typed column
	 * (county-specific labels and unnormalized values), verbatim.
	 */
	raw_data?: Record<string, string> | null;
	/**
	 * Situs attribute tokens this organization corrected via a situs override
	 * (token names match the JSON keys, e.g. "address_number", "owner").
	 * Empty/absent when the record shows the raw county values.
	 */
	overridden?: string[];
	/**
	 * The raw county situs row this record is based on — present only when an
	 * override exists, for comparing the district's correction against county.
	 */
	county?: MunicipalSitusDetail | null;
	/** Optional operator note attached to the organization's override. */
	note?: string | null;
}

/**
 * The organization's situs override for one county parcel — the payload of
 * GET/PUT /api/municipal/situs/{id}/situs-override. `fields` holds the
 * overridden attribute tokens ({token: value}; JSON value types match the
 * situs attribute). `has_override` is false when the org has no override for
 * the parcel backing the situs row.
 */
export interface SitusOverrideDetail {
	region: string;
	external_id: string;
	fields: Record<string, string | number | boolean>;
	note?: string | null;
	created_at?: string | null;
	updated_at?: string | null;
	has_override: boolean;
}

/**
 * GET /api/municipal/parcel-resolve/{external_id} — resolves a parcel clicked
 * on the map to the org's linked sites and its full situs (assessor) record.
 * site_ids holds every org site linked to this parcel (site_parcel), possibly
 * several — a parcel can be a shared footprint); empty means no site is
 * linked to it.
 */
export interface ParcelResolveResponse {
	parcel_id: number;
	external_id: string;
	/** The county the APN belongs to. APNs are not unique across counties, so
	 * this is what disambiguates a resolve (plan §3.3) and what the parcel's
	 * `site_parcel` link is keyed on. */
	region: string;
	site_ids: number[];
	situs?: MunicipalSitusDetail | null;
	/** The parcel's own address string when no situs record states one. */
	address_raw: string;
	description: string;
	property_type: string;
	/** The parcel footprint, null when the parcel has no geometry. */
	geometry_geojson?: GeoJSON.Geometry | null;
}

/**
 * The parcel backing a communication's location, as returned inside
 * CommunicationProperty. Deliberately distinct from the two MunicipalParcel
 * shapes above so neither existing consumer is disturbed.
 */
export interface MunicipalParcelSummary {
	parcel_id: number;
	external_id: string;
	site_ids: number[];
	region: string;
	address_raw: string;
	description: string;
	property_type: string;
	geometry_geojson?: GeoJSON.Geometry | null;
	situs?: MunicipalSitusDetail | null;
}

/** Which anchor resolved the parcel: geocoded address, report pin, or inspection. */
export type CommunicationPropertyMatch =
	| "address"
	| "report_location"
	| "virtual_inspection"
	| "none";

/** "contained" = ST_Contains hit; "nearest" = within the 10 m fallback. */
export type CommunicationPropertyMatchQuality = "contained" | "nearest" | "";

/**
 * GET /api/communication/{id}/parcel — the read-only property record for a
 * communication, resolved from its own source report (never from a contact's
 * unrelated reports). `parcel` is null when nothing matched.
 */
export interface CommunicationProperty {
	match: CommunicationPropertyMatch;
	match_quality: CommunicationPropertyMatchQuality;
	ambiguous: boolean;
	parcel: MunicipalParcelSummary | null;
}

export interface FeaturePoolBrief {
	condition: string;
	p_blue?: number | null;
	p_green?: number | null;
	p_dry?: number | null;
	risk?: number | null;
	medres_p_blue?: number | null;
	medres_p_green?: number | null;
	medres_p_dry?: number | null;
	medres_risk?: number | null;
	// Sticky (calibrated) axis: the posterior written by the weekly sticky
	// run onto the newest medium-res row. Absent when the pool has no
	// medium-res row or that row predates the sticky layer — the UI falls
	// back to the raw fields. posterior_condition is the current (newest-row)
	// condition as reported by the sticky layer, falling back to the raw
	// condition when the newest row has no posterior, so it is always the
	// condition to display.
	posterior_p_blue?: number | null;
	posterior_p_green?: number | null;
	posterior_p_dry?: number | null;
	posterior_risk?: number | null;
	posterior_condition?: string | null;
	template_status?: string | null;
	geometry?: GeoJSON.Geometry;
	medres_geometry?: GeoJSON.Geometry;
	// Pool provenance: how the pool row came to be. Present on rows written by
	// the manual add-pool feature (source='manual'); the site/feature API
	// returns these after the feature_pool columns land.
	source?: string | null;
	pool_type?: string | null;
	created_by?: number | null;
	created_at?: string | null;
	deleted_at?: string | null;
	exclude_from_training?: boolean | null;
}

// ControlState is a Feature's Control state, computed server-side from its
// cumulative treatment window: `controlled` while now is at or before the
// latest expiry across its treatments, `expired` once that has passed, and
// `unknown` when no treatment contributes a date (no product, no planned
// residual, or no treatment at all).
export type ControlState = "expired" | "controlled" | "unknown";

export interface Feature {
	id: number;
	location: Location;
	type: string;
	pool?: FeaturePoolBrief | null;
	// control_state is the server-computed Control state (see ControlState);
	// the map colours the Feature point by it and never recomputes it.
	control_state: ControlState;
	// window_end is the effective end of the Feature's control window — the
	// active manual plan where it governs, else the latest residual expiry —
	// or null when nothing contributes. It is the same instant control_state is
	// judged from, shipped so the client can band the point (due_soon) against
	// a tunable horizon without a refetch.
	window_end: string | null;
	// active is the district's standing, absolute decision to keep servicing
	// this Feature, inherited from the FieldSeeker source's `active` flag (a
	// deleted source counts as deactivated too). It is not time-bound and never
	// derived from the control window. The map hides inactive Features by
	// default; it is independent of control_state and never bands by it.
	active: boolean;
	// revisit_at is the Feature's ACTIVE manual revisit date ("pin"), or null
	// when none is set or the set one has been superseded by a treatment dated
	// at or after it. Read-time computed; the map marks a pinned Feature with a
	// neutral ring (never colour alone).
	revisit_at: string | null;
	// uri is the API handle to this feature's own detail endpoint
	// (/api/feature/{id}), which carries its treatments and inspections.
	uri: string;
}

export interface PoolStateEntry {
	date: string;
	condition: string;
	source: string;
	p_blue?: number | null;
	p_green?: number | null;
	p_dry?: number | null;
	risk?: number | null;
	signature?: number[] | null;
	week?: string | null;
	// Sticky (calibrated) axis, present on medium-res rows after the weekly
	// sticky run. posterior_condition falls back to the raw condition on rows
	// without a posterior, so it is always the condition to display for the
	// row; template_status is '' when the row has no sticky posterior.
	posterior_p_blue?: number | null;
	posterior_p_green?: number | null;
	posterior_p_dry?: number | null;
	posterior_risk?: number | null;
	posterior_condition?: string | null;
	template_status?: string | null;
}

export interface PoolWeekStats {
	blue: number[];
	green: number[];
	dry: number[];
}

export interface PoolExport {
	date: string;
	format: string;
}

export interface PoolDetail {
	depth_meters: number | null;
	current_condition: string;
	p_blue?: number | null;
	p_green?: number | null;
	p_dry?: number | null;
	risk?: number | null;
	// Sticky (calibrated) axis of the newest state row. posterior_condition
	// (and current_condition) fall back to the raw condition when the newest
	// row has no posterior — the sticky report IS the current condition
	// wherever one exists. template_status is '' when not sticky.
	posterior_p_blue?: number | null;
	posterior_p_green?: number | null;
	posterior_p_dry?: number | null;
	posterior_risk?: number | null;
	posterior_condition?: string | null;
	template_status?: string | null;
	state_history: PoolStateEntry[];
	exports: PoolExport[];
	week_stats?: Record<string, PoolWeekStats>;
	geometry?: any;
}

export interface FeatureDetail {
	id: number;
	type: string;
	location: Location;
	created: string;
	site_id: number;
	site_uri: string;
	pool: PoolDetail | null;
	treatments: Treatment[];
	inspections: Inspection[];
	// observations is the Feature's own observation records, newest first — the
	// Feature popup's link list and the row the Observation popup renders when
	// it is opened from one of those links. Each carries its inspection reading
	// and the treatments recorded under it.
	observations: MapReviewObservationRow[];
	// characteristics is the Feature's ontology classification — one current
	// value per dimension (superseded rows collapsed) — and production_state is
	// its current production state, null when never determined.
	characteristics: FeatureCharacteristic[];
	production_state: FeatureProductionStateDetail | null;
	/** Set when an Observation — not the source — is what made the Feature
	 * inactive: the condition token it asserted and the Observation that stated
	 * it. Rendered as a banner above the dimensions. */
	inactive_reason?: string;
	inactive_observation_id?: number;
}

// FeatureCharacteristic is one ontology classification dimension of a Feature
// with its current asserted value; dimension is the dimension name
// (habitat_class, water_origin, size, ...). Every dimension is present — a
// blank one carries an empty value rather than being dropped. observation_id
// and observed_at name the Observation that set the value, absent when it did
// not come from one.
export interface FeatureCharacteristic {
	dimension: string;
	value: string;
	observation_id?: number;
	observed_at?: string;
}

// FeatureProductionStateDetail is a Feature's current production state.
export interface FeatureProductionStateDetail {
	state: string;
	observed_at: string;
	reason?: string;
	observation_id?: number;
}
export interface Lead {
	compliance_report_requests: ComplianceReportRequest[];
	id: number;
	site_id: number;
	type: string;
}
// SiteActivity is a Site's activity rolled up through its Features: the Site
// has no observations or treatments of its own, so each fact is its Features'.
// Dates are absent (not zero) when nothing is recorded.
export interface SiteActivity {
	observations: number;
	treatments: number;
	last_observed_at?: string;
	/** The most recent observation's reading: found | none | no_inspection. */
	last_observed?: string;
	last_treated_at?: string;
	/** The product the most recent treatment applied; empty for a physical
	 * control. */
	last_product?: string;
}

/** Where a Site's contact link came from: the skip-trace lookup, the county
 * assessor, a person entered directly, an RMO import, or a virtual inspection. */
export type SiteContactSummarySource =
	| "skip_trace"
	| "county_assessor"
	| "direct"
	| "rmo"
	| "virtual_inspection";

/** One of a Site's people, as carried on the Site payload (`site.contacts`). */
export interface SiteContactSummaryDTO {
	contact_id: number;
	name: string;
	role: string;
	is_primary: boolean;
	source: string;
	/** The contact resource's own URI, when the server resolved one. */
	uri?: string;
}

export interface Site {
	address: Address;
	/** Activity is the Site's roll-up through its Features; filled on the
	 * single-Site detail (the popup), zero-valued in list payloads. */
	activity: SiteActivity;
	created: string;
	creator_id: number;
	/** The Site's people, each linked with its source. Filled on the
	 * single-Site detail; empty in list payloads. */
	contacts: SiteContactSummaryDTO[];
	features: Feature[];
	file_id: number;
	geometry_truncated: boolean;
	id: number;
	// kind/label/owner_name/owner_kind are the site fabric's own facts
	// (migration 00439): parcel | street | other, the display label, and the
	// owner of record (a free-text county string on parcel sites).
	kind: string;
	label: string;
	leads: Lead[];
	parcel: MunicipalParcel | null;
	notes: string;
	newest_exported_at?: string | null;
	organization_id: number;
	owner?: ContactSimple;
	owner_kind: string;
	owner_name: string;
	resident?: ContactSimple;
	resident_owned: boolean;
	tags: Map<string, string>;
	// The resource's own URI ("/api/site/{id}"), which the site edit form PUTs
	// its four fields to; empty when the server resolved none.
	uri: string;
	version: number;
}

/** The body of a Site edit (`PUT /api/site/{id}`): the four fields the edit
 * form owns, sent as a full replace. An empty owner or resident name clears
 * that role's link. */
export interface SiteUpdateRequest {
	address: { gid: string };
	owner: string;
	resident: string;
	notes: string;
}

/** The body of a Site create (`POST /api/site`): a point on the map, plus the
 * parcel the click resolved when there was one. `region`/`external_id` are the
 * parcel tile's own properties — they are what ties the new Site to the county
 * parcel (plan §6.2); without them the Site is created at the point from its
 * reverse-geocoded address. */
export interface SiteCreateRequest {
	latitude: number;
	longitude: number;
	region?: string;
	external_id?: string;
}

/** A Site create's answer: the new Site's id, its resolved address and its
 * kind (`parcel` when a parcel was linked, `other` when the point was
 * reverse-geocoded). The address is never empty — the server refuses a Site
 * whose address cannot be resolved. */
export interface SiteCreateResponse {
	site: {
		id: number;
		address: string;
		kind: string;
	};
}
export interface ReviewTaskPool {
	condition: string;
	location: Location;
	owner: ContactSimple;
	site: Site;
}
export interface ReviewTask {
	address: Address;
	addressed?: string;
	addressor?: User;
	created: string;
	creator: User;
	pool?: ReviewTaskPool;
	id: number;
}

/** One Site a contact is linked to, as carried on the contact detail. */
export interface ContactDetailSiteDTO {
	site_id: number;
	role: string;
	is_primary: boolean;
	source: string;
	last_traced_at?: string | null;
}

/** One person a skip-trace run returned, verbatim (the stored history). */
export interface SkipTracePersonDTO {
	first_name: string;
	last_name: string;
	full_name: string;
	dob?: string;
	age?: number;
	deceased?: boolean;
	property_owner?: boolean;
	litigator?: boolean;
	mailing_address?: {
		street?: string;
		city?: string;
		state?: string;
		zip?: string;
	};
	phones?: {
		number: string;
		type?: string;
		dnc?: boolean;
		tcpa?: boolean;
		carrier?: string;
		rank?: number;
	}[];
	emails?: { email: string; rank?: number }[];
}

/** One email exchanged with a contact's address, as the email history card
 * renders it. `source` is the sender, `destination` the recipient; which side
 * carries the contact's address is what makes the row inbound or outbound. */
export interface ContactEmailMessageDTO {
	created: string;
	sent_at?: string | null;
	source: string;
	destination: string;
	subject: string;
	type: string;
	delivery_status: string;
}

/** GET /api/contact/{id}: the contact's stored fields plus its Site links and
 * every person a skip-trace run returned for it, newest first. */
export interface ContactDetailDTO {
	id: number;
	name: string;
	uri: string;
	emails: string[];
	phones: Phone[];
	addresses: ContactAddressDTO[];
	reported_addresses: ContactReportedAddressDTO[];
	messages: MessageDTO[];
	sites: ContactDetailSiteDTO[];
	email_history: ContactEmailMessageDTO[];
	last_traced_at?: string | null;
	history: SkipTracePersonDTO[];
}

/** One person a skip-trace run resolved to a contact. */
export interface SkipTraceContactDTO {
	contact_id: number;
	name: string;
	role: string;
	is_primary: boolean;
	source: string;
	phones: string[];
	emails: string[];
	person: SkipTracePersonDTO;
}

/** GET /api/site/{id}/skip-trace: whether skip tracing is configured and its
 * real per-hit cost, so the confirm can name what a lookup actually costs. The
 * vendor is not exposed — it can be swapped underneath. */
export interface SkipTraceConfigDTO {
	configured: boolean;
	cost_cents: number;
	cost_display: string;
}

/** POST /api/site/{id}/skip-trace. `configured:false` comes back with HTTP 200
 * and a message; nothing was looked up and no credits were spent. */
export interface SkipTraceResultDTO {
	configured: boolean;
	hit: boolean;
	persons_count: number;
	credits_deducted: number;
	lookup_kind?: string;
	message?: string;
	contacts: SkipTraceContactDTO[];
}
export interface UploadDTO {
	created: string;
	error: string;
	filename: string;
	id: number;
	recordcount: number;
	status: string;
	type: string;
	csv_pool?: CSVPoolDetail;
}
export interface UploadOptions {
	created: Date;
	error: string;
	filename: string;
	id: number;
	recordcount: number;
	status: string;
	type: string;
	csv_pool?: CSVPoolDetail;
}
export class Upload {
	created: Date;
	error: string;
	filename: string;
	id: number;
	recordcount: number;
	status: string;
	type: string;
	csv_pool?: CSVPoolDetail;
	constructor(options: UploadOptions) {
		this.created = options.created;
		this.error = options.error;
		this.filename = options.filename;
		this.id = options.id;
		this.recordcount = options.recordcount;
		this.status = options.status;
		this.type = options.type;
		this.csv_pool = options.csv_pool;
	}
	static fromJSON(json: UploadDTO): Upload {
		return new Upload({
			...json,
			created: new Date(json.created),
		});
	}
}

export interface UploadPoolRow {
	address: Address;
	condition: string;
	errors: UploadPoolError[];
	location?: Location;
	status: string;
	tags: Map<string, string>;
}
export interface UploadPoolError {
	column: number;
	line: number;
	message: string;
}

export interface CSVPoolDetail {
	count: CSVPoolDetailCount;
	errors: CSVPoolError[];
	pools: UploadPoolRow[];
}
export interface User {
	avatar: string;
	certification: string;
	certification_number: string;
	display_name: string;
	id: number;
	initials: string;
	is_active: boolean;
	/** The district the employee belongs to; absent when it is not loaded. */
	organization?: string | null;
	role: string;
	tags: string[];
	uri: string;
	username: string;
}
export type MailerStatus = "created" | "printed" | "mailed" | "completed";
export interface MailerDTO {
	address: Address;
	compliance_report_request_id?: string;
	created: string;
	id: string;
	recipient: string;
	status: MailerStatus;
	site_id: string;
	uri: string;
}
export interface MailerOptions {
	address: Address;
	compliance_report_request_id?: string;
	created: Date;
	id: string;
	recipient: string;
	site_id: string;
	status: MailerStatus;
	uri: string;
}
export class Mailer {
	address: Address;
	compliance_report_request_id?: string;
	created: Date;
	id: string;
	recipient: string;
	site_id: string;
	status: MailerStatus;
	uri: string;
	constructor(options: MailerOptions) {
		this.address = options.address;
		this.compliance_report_request_id = options.compliance_report_request_id;
		this.created = options.created;
		this.id = options.id;
		this.recipient = options.recipient;
		this.site_id = options.site_id;
		this.status = options.status;
		this.uri = options.uri;
	}
	pdfUrl(): string {
		return `/mailer/mode-3/${this.compliance_report_request_id}/preview`;
	}
	static fromJSON(json: MailerDTO): Mailer {
		return new Mailer({
			...json,
			created: new Date(json.created),
		});
	}
}

export interface Organization {
	id: number;
	name: string;
	lob_address_id: string;
	licenses: string[];
	integrations: string[];
	service_area?: Bounds;
	unit_preference: UnitPreference;
	/** The district's public URL slug (`organization.slug`); empty when the
	 * district has none. Generated trap serials are prefixed with it. */
	slug: string;
}

// UnitPreference is an organization's chosen units for label-rate display. The
// preset (imperial/metric) fills every dimension; an override, when set, wins.
// Values are the stored enum labels ("acre", "sq m", "per 1000 sq ft", ...).
export interface UnitPreference {
	preset: string;
	area: string | null;
	mass: string | null;
	volume: string | null;
	areaBasis: string | null;
	volumeBasis: string | null;
}

// UnitPreferenceSave is the update shape sent to PUT /api/organization/{id}: an
// empty string clears an override so the dimension inherits the preset.
export interface UnitPreferenceSave {
	preset: string;
	area: string;
	mass: string;
	volume: string;
	areaBasis: string;
	volumeBasis: string;
}

// OrganizationResponse matches the Go api.OrganizationResponse struct
// returned by GET /api/organization/{id}
export interface OrganizationResponse {
	id: number;
	name: string;
	active: boolean;
	website: string | null;
	generalManagerName: string | null;
	officeAddressStreet: string | null;
	officeAddressCity: string | null;
	officeAddressPostalCode: string | null;
	officePhone: string | null;
	officeFax: string | null;
	officeEmail: string | null;
	serviceArea?: Area;
	satelliteSurveillanceArea?: Area;
	unitPreference: UnitPreference;
	/** The district's drive origin and destination, or null when it has none
	 * (the plan then runs stop to stop and says so). */
	routingBase: RoutingBase | null;
	/** Valhalla costing profile: auto, truck or pedestrian. */
	routingCosting: string;
	/** Minutes spent at a stop, by assignment kind. A per-Assignment
	 * `service_minutes` overrides the kind default. */
	routingServiceMinutes: Record<string, number>;
}

/** The district's base point: a yard, not a Site. */
export interface RoutingBase {
	lng: number;
	lat: number;
	label: string | null;
}

// SiteFabricRunMetric is one line of the site fabric's verification report.
export interface SiteFabricRunMetric {
	metric: string;
	value: string;
}

// SiteFabricRunCounty is a county the district's service area touches, with
// whether its TIGER centrelines are already ingested (the ingest stage is
// county-scoped and shared, so it need only run once per county).
export interface SiteFabricRunCounty {
	name: string;
	fips: string;
	ingested: boolean;
	segments: number;
}

// SiteFabricStage is which part of the fabric a run performs: counties ingests
// the shared TIGER centrelines, build rebuilds the district's fabric from what
// is already ingested, and run does both.
export type SiteFabricStage = "counties" | "build" | "run";

// SiteFabricRunStatus is the organization's fabric state, returned by
// GET /api/organization/{id}/site-fabric. status is "never" until a run has
// been recorded; stage is the last run's stage.
export interface SiteFabricRunStatus {
	status: "never" | "queued" | "running" | "succeeded" | "failed";
	stage: SiteFabricStage | "";
	jobId: number | null;
	startedAt: string | null;
	completedAt: string | null;
	error: string | null;
	report: SiteFabricRunMetric[];
	counties: SiteFabricRunCounty[];
}

// SiteFabricRunResponse is the result of POST
// /api/organization/{id}/site-fabric/run. enqueued is false with a reason when
// a run for the organization was already queued.
export interface SiteFabricRunResponse {
	enqueued: boolean;
	reason: string;
	jobId?: number;
	stage: SiteFabricStage | "";
	status: string;
}

export interface ServiceAreaLocation {
	latitude: number;
	longitude: number;
	accuracy?: number | null;
}
export interface Phone {
	can_sms: boolean;
	e164: string;
}
export interface PhoneSimple {
	can_sms: boolean;
	number: string;
}
export interface PhoneReporter {
	can_sms: boolean;
	number: string;
}
export interface UserNotificationCounts {
	communication: number;
	home: number;
	review: number;
}
export interface SessionNotificationCounts {
	communication: number;
	home: number;
	review: number;
}
export interface Session {
	impersonating?: string;
	notifications: Notification[];
	notification_counts: SessionNotificationCounts;
	organization: Organization;
	self: User;
	urls: URLs;
}
export interface SyncDTO {
	created: Date;
	id: string;
	organization: string;
	records_created: number;
	records_unchanged: number;
	records_updated: number;
}
export class Sync {
	constructor(
		public created: Date,
		public id: string,
		public organization: string,
		public records_created: number,
		public records_unchanged: number,
		public records_updated: number,
	) {}
	static fromJSON(json: SyncDTO): Sync {
		return new Sync(
			new Date(json.created),
			json.id,
			json.organization,
			json.records_created,
			json.records_unchanged,
			json.records_updated,
		);
	}
}
export interface URLs {
	api: URLsAPI;
	tegola: string;
	tile: string;
}
// Define interfaces matching your Go structs
interface URLsAPI {
	avatar: string;
	certification: string;
	communication: string;
	communication_message: string;
	impersonation: string;
	mailer: string;
	review_task: string;
	signal: string;
	site: string;
	sync: string;
	upload: string;
	user: string;
}

// CommunicationSearchResult represents a search result from the communication search endpoint.
export interface CommunicationSearchResult {
	display_text: string;
	subtitle: string;
	type: "address" | "name" | "report_id";
	gid?: string;
	location?: Location;
	has_communication: boolean;
	public_id?: string;
	report_id?: number;
}

// CommunicationSearchLocation is a lat/lng location returned in search results.
export interface CommunicationSearchLocation {
	latitude: number;
	longitude: number;
}

// Vision analysis list response — matches api.visionAnalysisListItem
// (GET /api/vision/analysis).
export interface AnalysisTaskCounts {
	all: number;
	queued: number;
	completed: number;
}

export interface AnalysisListItem {
	id: number;
	type: string;
	zoom: number;
	status: string;
	createdAt: string;
	startedAt: string | null;
	completedAt: string | null;
	error: string | null;
	tasks: AnalysisTaskCounts;
	completedIds: number[];
	poolsPendingReview: number;
	coverage?: unknown;
}

// XYZ slippy tile framing a pool in the review UI — matches api.visionReviewTile
// in the review-pools response.
export interface ReviewTile {
	z: number;
	x: number;
	y: number;
}

// A reviewer's pool-review tally for the leaderboard — matches
// api.visionReviewerCount (GET /api/vision/review/stats).
export interface VisionReviewerCount {
	reviewerId: number;
	displayName: string;
	count: number;
}

// Vision pool-corpus review progress — matches api.visionReviewStats
// (GET /api/vision/review/stats).
export interface VisionReviewStats {
	totalPools: number;
	reviewedPools: number;
	byReviewer: VisionReviewerCount[];
}

// One planet item in the corpus review landing table — matches
// api.visionReviewItemSummary (GET /api/vision/review/items). The bounds are
// the item's EPSG:3857 raster extent for map centering.
export interface VisionReviewItemSummary {
	id: string;
	acquired: string;
	cloud_cover: number | null;
	min_x: number;
	min_y: number;
	max_x: number;
	max_y: number;
	pool_total: number;
	pool_unreviewed: number;
	task_total: number;
	task_needs_review: number;
}

// One review section (slippy tile) of an item's pool corpus — matches
// api.visionReviewTileSummary (GET /api/vision/review/item/{item_id}/sections).
export interface VisionReviewTileSummary {
	z: number;
	x: number;
	y: number;
	total_pools: number;
	unreviewed_pools: number;
	reviewed_pools: number;
}

// One planet item intersecting a review tile — matches api.visionReviewTileItem
// in the tile-detail response (GET /api/vision/review/item/{item_id}/{z}/{x}/{y}).
export interface VisionReviewTileItem {
	id: string;
	asset_type: string;
	acquired: string;
	cloud_cover: number | null;
}

// A pool intersecting a review tile — matches api.visionReviewTilePool
// in the tile-detail response (GET /api/vision/review/item/{item_id}/{z}/{x}/{y}).
export interface VisionReviewTilePool {
	id: number;
	uri: string;
	condition: string;
	geometry: GeoJSON.GeoJSON;
	reviewed: boolean;
	reviewerId: number | null;
	reviewedAt: string | null;
	createdAt: string;
	itemId: string | null;
}

// Tile detail for found-pools overlays — matches the response of
// GET /api/vision/review/item/{item_id}/{z}/{x}/{y}: the requested slippy tile
// plus the planet items and pools intersecting it.
export interface VisionReviewTileDetail {
	z: number;
	x: number;
	y: number;
	items: VisionReviewTileItem[];
	pools: VisionReviewTilePool[];
}

// A single unreviewed pool for position-inference anchoring review — matches
// api.visionPositionInferencePool (GET /api/vision/review/position-inference).
export interface VisionPositionInferencePool {
	id: number;
	uri: string;
	condition: string;
	geometry: GeoJSON.Geometry;
	tile: { z: number; x: number; y: number } | null;
	createdAt: string;
	itemId: string | null;
}

// A single pool queued for the pool-condition review — matches
// api.visionConditionReviewPool (GET /api/vision/review/condition-pool).
// Geometry is read-only in this task; the reviewer records a condition
// verdict (PUT /api/vision/train/pool/{id}) or rejects the pool back to the
// correct-finding review (POST /api/vision/review/condition-pool/{id}/reject).
export interface VisionConditionReviewPool {
	id: number;
	uri: string;
	condition: string;
	geometry: GeoJSON.Geometry;
	tile: { z: number; x: number; y: number; offset?: boolean } | null;
	createdAt: string;
	itemId: string | null;
}

// A reviewer-flagged finding on a review tile — matches the response of
// POST/GET /api/vision/review/finding. pool is the referenced vision pool
// (false positives); tile is the imagery centering tile computed from the
// finding geometry.
export interface ReviewFinding {
	id: number;
	createdAt: string;
	kind: "false_negative" | "false_positive";
	geometry: GeoJSON.Geometry;
	itemId: string | null;
	reviewTileTaskId: number | null;
	poolId: number | null;
	status: "pending" | "in_progress" | "resolved" | "discarded";
	pool?: VisionReviewTilePool | null;
	// tile.offset means the imagery is rendered through the offset-tile path,
	// which centers the tile on a grid corner (see api.visionReviewTile).
	tile?: { z: number; x: number; y: number; offset?: boolean } | null;
}

// A single pool awaiting review in the analysis review workflow — matches
// api.reviewPoolItem (GET /api/vision/analysis/{id}/review-pools). The source
// task context (tile, mask, planet crop) anchors the review imagery.
export interface ReviewPool {
	poolId: number;
	geometry: unknown;
	condition: string;
	confidence: number | null;
	taskId: number;
	maskUrl: string;
	tileX: number;
	tileY: number;
	offset: boolean;
	createdAt: string;
	reviewTile?: ReviewTile;
	// Planet tasks: native pixel origin of the source task inside the item
	// raster; the review panes crop the item directly instead of requesting a
	// slippy tile (native pixel tiles can fall outside the item footprint).
	planetItemId?: string | null;
	planetAssetType?: string | null;
	tileSize: number;
}

// A review tile task — a native review tile of a planet item awaiting human
// review. Matches api.reviewTileTaskResponse
// (GET /api/vision/review-task, /api/vision/review-task/{id},
//  GET /api/vision/review-task/next). poolVerdict is the human pool-completeness
// verdict: null = no verdict yet, "pool_free"/"all" = confirmed complete,
// "partial" = not complete.
export interface ReviewTileTask {
	id: number;
	createdAt: string;
	itemId: string;
	assetType: string;
	tileSize: number;
	x: number;
	y: number;
	hasPool: boolean;
	status: "pending" | "in_progress" | "reviewed" | "skipped";
	reviewerId: number | null;
	reviewedAt: string | null;
	geometry: GeoJSON.GeoJSON;
	tileUrl: string;
	poolVerdict: "pool_free" | "partial" | "all" | null;
}

// Per-item review-task coverage — matches api.reviewTileCoverageResponse
// (GET /api/vision/review-task/coverage). poolFree/partial/all tally the
// review tasks by their human pool-completeness verdict.
export interface ReviewTileCoverage {
	itemId: string;
	total: number;
	pending: number;
	inProgress: number;
	reviewed: number;
	skipped: number;
	withPool: number;
	poolFree: number;
	partial: number;
	all: number;
}

// A MapVision site record — matches platform.MapVisionSite
// (GET /api/mapvision/site/{id}).
export interface MapvisionSite {
	id: number;
	site_id: number;
	name: string | null;
	address1: string | null;
	street: string | null;
	city: string | null;
	zipcode: string | null;
	state: string | null;
	county: string | null;
	type_name: string | null;
	sub_type_name: string | null;
	zone_name: string | null;
	calsurv_sitecode: string | null;
	xref: string | null;
	no_spray: boolean | null;
	notify: boolean | null;
	reinspection_date: string | null;
	date_last_action: string | null;
	date_last_treated: string | null;
	// URI of the municipal parcel nearest to the site (within 10m), or null.
	parcel_uri: string | null;
	// Bitemporal history from the placer.bi_h_site mirror (oldest valid date
	// first). Empty when the org has no placer data loaded.
	history: MapvisionSiteHistory[];
}

export interface MunicipalParcel {
	id: number;
	address_raw: string;
	description: string;
	external_id: string;
	property_type: string;
	geometry: GeoJSON.Geometry;
	// URI of the situs (assessment) row sharing this parcel's external_id, or
	// null when there is none.
	situs_uri: string | null;
}

export interface MapvisionSiteHistory {
	h_id: number;
	site_id: number;
	site_type: string | null;
	site_subtype: string | null;
	latitude: string | null;
	longitude: string | null;
	zone: string | null;
	date_valid: string | null;
	date_expired: string | null;
	is_deleted: string | null;
}

export interface MapvisionSiteSearchResult {
	id: number;
	site_id: number;
	name: string | null;
	type_name: string | null;
	sub_type_name: string | null;
	zone_name: string | null;
	city: string | null;
}

// MapVision integration — the mirror as the settings screen reports it.
// GET /api/integration/mapvision/sync retried on demand; the page shows the
// panel only once the read answers.
export type MapvisionMirrorCategory =
	| "layers"
	| "lists"
	| "master"
	| "snapshot";

export interface MapvisionResourceCount {
	resource: string;
	label: string;
	category: MapvisionMirrorCategory;
	rows: number;
}

export interface MapvisionSyncLastRun {
	// False when the run stopped early, so the counts describe a part-run.
	succeeded: boolean;
	startedAt: string | null;
	completedAt: string;
	error: string | null;
}

export interface MapvisionSyncStatus {
	// The completion of the last sync that succeeded; null until one has.
	syncedAt: string | null;
	resourceCounts: MapvisionResourceCount[];
	running: boolean;
	lastRun: MapvisionSyncLastRun | null;
}

// MapVision Review — generic data browser over synchronized MapVision tables.
// GET /api/mapvision/type lists the tables; each entry can be paged through
// (rows as index-aligned arrays) and inspected by primary key.
export type MapvisionReviewCategory = "layers" | "lists" | "master";

export interface MapvisionReviewType {
	name: string;
	table: string;
	category: MapvisionReviewCategory;
	label: string;
	description: string;
	row_count: number;
	pk: string;
}

/** One row of a review type as `QUERY /api/mapvision/type/{type}` answers it
 * (doc/in-progress/query.md §11 Phase 4): the value of the type's primary key —
 * what the row's detail link is built from — and the row's columns in the
 * table's own order. A review type's column set is data rather than a declared
 * capability, so the header row is the first item's own keys. */
export interface MapvisionReviewListRow {
	pk: unknown;
	row: Record<string, unknown>;
}

export interface MapvisionReviewTypeRow {
	name: string;
	table: string;
	category: MapvisionReviewCategory;
	label: string;
	description: string;
	pk: string;
	id: string;
	row: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Contract — Notes review module (`/_/review/note`).
//
// Screen contract for the Review Workbench Transcripts sub-module (see
// doc/design/workbenches/review-workbench-ui.md and doc/design/workbenches/review-workbench.md).
// Backed by the Phase 4 endpoints below. Read responses report every row id
// as a decimal STRING (e.g. note "4821", extraction "3"); mutation bodies
// that reference rows (supersedesId, review objects) carry the numeric ids.
//
//   GET  /api/transcript?before=&limit=&search=&technician=&route=&status=&date=
//        — the legacy queue, newest first, cursor paged (doc/in-progress/
//        query.md §11 Phase 4; the resource's QUERY below is what the review
//        queue reads now). Returns TranscriptNote[]; heavy
//        fields (transcript/extractions/locationStream/photos) are EMPTY in
//        list rows — the detail endpoint is the full assembly — and
//        `derivedResources` is always `[]` here. The light `layersStatus`
//        verdict projection IS present on list rows; the full `layers` record
//        is detail-only.
//   QUERY /api/transcript               — the queue's own page, newest first
//        (the descriptor's default order), with the same row shape and the
//        envelope's counts. Its clauses are `search` (a computed free-text
//        match over the note id's digits, the transcription and the creator's
//        display name), `technician`, `status`, `created` and `id`; a note's
//        route is not a property, because nothing records one.
//   GET  /api/transcript/{id}          — full TranscriptNote (including
//        `layers`, the device's derived-layer verdict — detail-only, like
//        `knowledge`: queue rows carry null). `derivedResources` is the
//        detail-only assembly of the ontology resources this note version's
//        derivation created or matched (kind/id/role; `uri` is the canonical
//        `/api/<resource>/<id>` path, empty when the kind has no route).
//   GET  /api/transcript/{id}/audio    — normalized audio (audio/ogg) when
//        present, else raw recording (audio/mp4); Range-supported
//   PUT  /api/transcript/{id}/transcript — TranscriptEditRequest → 204
//   POST /api/transcript/{id}/extraction — TranscriptExtractionRequest →
//        created TranscriptExtraction (supersedes → old row drops from GET)
//   POST /api/review                   — TranscriptReviewRequest → {id}
//   PUT  /api/transcript/{id}/status   — TranscriptStatusRequest → 204
//   POST /api/transcript/{id}/clarify  — TranscriptClarifyRequest → {id}
//
// route/surveyId/assignment/location are "" when not derivable (recordings
// made outside a survey context); the UI renders blank. legacy note rows
// (plain-text transcription, no line/word rows) come back as synthesized
// lines with id null and words [] — rendered read-only.
// ─────────────────────────────────────────────────────────────────────────────

/** Kind of Type 2 structured record a transcript's extraction proposes. */
export type TranscriptExtractionKind =
	| "observation"
	| "inspection"
	| "treatment"
	| "constraint";

// Canonical attribute enums from the Nidus Notes data model
// (doc/design/nidus-notes/data-model.md). Field records capture
// these by voice; the review UI renders them verbatim.
export type TranscriptHabitat =
	| "Swimming Pool"
	| "Above Ground Pool"
	| "Hot Tub (Spa)"
	| "Plant Saucer"
	| "Bird Bath"
	| "Kid Pool"
	| "Watering Bowl"
	| "Tarp Cover"
	| "Tires Habitat"
	| "Appliance"
	| "Misc Water Accumulation"
	| "Utility Habitat"
	| "Catch Basin"
	| "Pond"
	| "Marsh Wetland"
	| "Retention Basin"
	| "Slough"
	| "Stream Creek"
	| "River"
	| "Ditch"
	| "Dairy Lagoon"
	| "Depression"
	| "Low Point"
	| "Drain"
	| "Culvert"
	| "Sump"
	| "Wastewater Treatment"
	| "Agricultural Grasses Grain"
	| "Orchard"
	| "Pasture"
	| "Row Crops"
	| "Vine Crops"
	| "Trough Habitat"
	| "Irrigation Standpipe"
	| "Gutter"
	| "Rain Gutter"
	| "Yard Drain"
	| "Flooded Structure"
	| "Treehole"
	| "Plant Axil"
	| "Fountain Water Feature"
	| "Tail Ditch";

export type TranscriptWaterPresent = "Wet" | "Dry";

export type TranscriptWaterOrigin =
	| "Flood Irrigation"
	| "Furrow Irrigation"
	| "Drip Irrigation"
	| "Sprinkler Irrigation"
	| "Wastewater Irrigation"
	| "Irrigation Runoff"
	| "Permanent Natural Water"
	| "Temporary Natural Water"
	| "Rainwater Accumulation"
	| "Leak"
	| "Seepage"
	| "Stormwater Municipal Runoff"
	| "Industrial Runoff"
	| "Wastewater System"
	| "Water Conveyance"
	| "Stored Water"
	| "Recreational Ornamental Water";

export type TranscriptWaterUse =
	| "Agricultural"
	| "Commercial"
	| "Conveyance"
	| "Industrial"
	| "Mixed use"
	| "Natural"
	| "Public Domain"
	| "Residential";

export type TranscriptOrganicContent = "Clear" | "Light" | "Heavy";
export type TranscriptFlowState = "Stagnant" | "Slow-flowing" | "Fast-flowing";
export type TranscriptWaterConnectivity =
	| "Isolated"
	| "Locally Connected"
	| "System-connected"
	| "Unknown";
export type TranscriptVegetationInfluence =
	| "None"
	| "Incidental"
	| "Influential"
	| "Constraining";
export type TranscriptSunExposure = "Full Sun" | "Partial Shade" | "Full Shade";
export type TranscriptContainerCondition =
	| "Intact"
	| "Cracked"
	| "Deteriorated"
	| "Collapsed";
export type TranscriptBankAccessibility =
	| "Fully Accessible"
	| "Partially Accessible"
	| "Inaccessible";

/** Derived from Inspection (or the dry rule) — never spoken by the technician. */
export type TranscriptSourceStatus = "Not a Source" | "Source";

export type TranscriptGenusGuess = "Culex" | "Anopheles" | "Aedes" | "Unknown";
export type TranscriptDensity =
	| "Zero"
	| "Trace"
	| "Low"
	| "Medium"
	| "High"
	| "Loaded";
export type TranscriptStageComposition =
	| "Mostly early"
	| "Mixed"
	| "Mostly late";

export type TranscriptTreatmentMethod =
	| "Liquid"
	| "Granular"
	| "Briquette"
	| "Dunk"
	| "Physical Removal"
	| "Biological"
	| "Other";

/** Safety + access constraint types (SafetyConstraint / AccessConstraint). */
export type TranscriptConstraintType =
	| "Aggressive Animal"
	| "Unstable Bank"
	| "Confined Space"
	| "Electrical Hazard"
	| "Hostile Individual"
	| "Chemical Exposure Risk"
	| "Locked Gate"
	| "No Response"
	| "Refused Entry"
	| "Physical Barrier"
	| "No Access Route"
	| "Posted No Trespassing"
	| "Other";

/** Review lifecycle state of a transcript record (Review artifact: Recorded → Closed). */
export type TranscriptReviewStatus =
	| "Recorded"
	| "In Review"
	| "Awaiting Clarification"
	| "Superseded"
	| "Closed";

/**
 * Queue flags carry two meanings (workbench UI decisions):
 * - "system" — system-tagged items (anomaly, out-of-recommendation treatment);
 * - "reviewer" — records with reviewer state (opened, has findings, awaiting
 *   clarification response).
 * The two are rendered with visually distinct styles in the queue.
 */
export type TranscriptFlagKind = "system" | "reviewer";

export interface TranscriptFlag {
	kind: TranscriptFlagKind;
	/** Short reason shown next to the ⚠ marker, e.g. "Anomaly". */
	label: string;
}

/**
 * One property row as the review UI renders it (queue sorting and the detail
 * properties section both operate on these entries).
 */
export interface TranscriptPropertyEntry {
	key: string;
	label: string;
	value: string;
	unit?: string;
	/**
	 * Highlighted as anomalous in the properties section (⚠ + highlight) when
	 * set, with `peerContext` inline where available. Sources: system anomaly
	 * flags and peer-comparison deviation — analytical (Type 3), never
	 * mutating. The rough draft flags only system-tagged values.
	 */
	anomalous?: boolean;
	/** Inline peer context, e.g. "peers: 2–8". */
	peerContext?: string;
	/** Proximity attribution of the extracted value, e.g. "12 m @ 00:42". */
	attribution?: string;
	/** Derived value (e.g. sourceStatus, productionConfirmed) — marked "(derived)". */
	derived?: boolean;
}

/** A point on the recording's location/time track. */
export interface TranscriptLocationPoint {
	/** Seconds into the recording, when the point was captured. */
	timeOffsetSeconds?: number;
	/** Distance from the recording's anchor, e.g. 12 for "12 m". */
	distanceMeters?: number;
	latitude: number;
	longitude: number;
}

/** One word of a transcript line, with its own timestamp range. */
export interface TranscriptWord {
	text: string;
	/** Seconds into the recording when the word starts. */
	start: number;
	/** Seconds into the recording when the word ends. */
	end: number;
}

/** One timestamped line/segment of the server transcript. */
export interface TranscriptLine {
	/**
	 * Server identity of the transcript-line row, used to address edits
	 * (PUT /transcript). Null on legacy/synthesized lines (plain-text
	 * transcription without line/word rows) — those render read-only.
	 */
	id: number | null;
	/** Seconds into the recording. */
	startSeconds: number;
	text: string;
	/**
	 * Pre-edit text, present only when the line has been edited server-side
	 * (non-destructive edits: the reviewer's current text is in `text`, the
	 * original is struck through beside it). Absent on unedited lines.
	 */
	original?: string;
	/**
	 * Word-level timestamps covering the line's duration (contiguous: the
	 * first word starts at `startSeconds`, each word ends where the next
	 * begins, the last ends at the line's end). Drives the current-word
	 * playback highlight and word-click seeking. Empty for edited and
	 * legacy/synthesized lines (words no longer match the text).
	 */
	words: TranscriptWord[];
}

/** Transcript range a proposed extraction was read from, e.g. [00:12–00:31]. */
export interface TranscriptRef {
	startSeconds: number;
	endSeconds: number;
}

/**
 * A proposed Type 2 structured record extracted server-side from a recording —
 * one of the ordered records in the extraction section. This is what review
 * validates: ⚠ anomaly flags sit on the individual record's anomalous
 * field, derived values are marked "(derived)", and each record references
 * the transcript segment it was read from.
 */
export interface TranscriptExtraction {
	kind: TranscriptExtractionKind;
	/** Record id (decimal string of the extraction row) — addresses POST /extraction supersede + review_object links. */
	id: string;
	/** Short label, e.g. "Observation · Feature". */
	label: string;
	/** One-line summary, e.g. "Swimming Pool · Wet". */
	summary: string;
	/** Transcript segment the extraction was read from, e.g. [00:12–00:31]. */
	transcriptRef?: TranscriptRef;
	/** Structured fields in the ontology vocabulary. */
	fields: TranscriptPropertyEntry[];
	/** Extraction-level flags, e.g. "Out of recommendation". */
	flags?: TranscriptFlag[];
}

/**
 * A structured record the server re-derived from a recording's own stored
 * words at read time, rather than read back from a persisted row (nothing is
 * persisted — layered-extraction design D7). Exposed for new-format notes
 * only, i.e. notes whose version carried an NLU layers snapshot; the same
 * records are rebuilt on every read from that note's words.
 *
 * Read-only evidence: a derived record carries no row id, so it cannot be
 * corrected, superseded, commented on, or used as a review object. The
 * reviewer-actionable records — the ones review validates — live in
 * `extractions` instead, and the two lists never overlap.
 */
export interface TranscriptDerivedExtraction {
	kind: TranscriptExtractionKind;
	/** Short label, e.g. "Observation · Feature". */
	label: string;
	/** One-line summary, e.g. "Swimming Pool · Wet". */
	summary: string;
	/** Structured fields in the ontology vocabulary. */
	fields: TranscriptPropertyEntry[];
}

/**
 * A photo taken DURING an audio recording (photo-only notes are out of
 * scope). Photos surface on the review timeline between location points;
 * clicking one opens the photo view.
 */
export interface TranscriptPhoto {
	/** Photo id, e.g. "P-4821-1". */
	id: string;
	/** Seconds into the recording when the photo was taken. */
	timestamp: number;
	/** Where the photo was taken (location point on the timeline). */
	location: TranscriptLocationPoint;
	/** Thumbnail — draft fixtures use an inline SVG data URI. */
	thumbnailUrl: string;
}

/**
 * A minted short link for a mosquito-service URL. Clicks counts resolves;
 * campaign groups links for attribution (campaign links target the walk-up
 * Mosquito Check landing).
 */
export interface ShortLink {
	id: number;
	code: string;
	link: string;
	target: string;
	campaign: string | null;
	disabled: boolean;
	clicks: number;
	last_clicked_at: string | null;
	created_by: number | null;
	created: string;
}

/** One day's click count for a short link (last 30 days, newest first). */
export interface ShortLinkDaily {
	day: string;
	clicks: number;
}

/** A single recent click on a short link. */
export interface ShortLinkClick {
	clicked_at: string;
	referrer: string | null;
}

/** Per-link analytics: daily click series plus the most recent clicks. */
export interface ShortLinkAnalytics {
	daily: ShortLinkDaily[];
	recent: ShortLinkClick[];
}

/**
 * Server verdict on the extraction layers the device derived and shipped with
 * one note version (migration 00378). The device derives the layers itself and
 * uploads them; ingest re-derives them server-side with its own vocabulary and
 * records how the two compare:
 * - "matched" — the server reproduced the device's tree;
 * - "drifted" — the server derived something else (see `driftSummary`);
 * - "unverified" — the payload could not be checked against a vocabulary the
 *   server knows (the device sent an unrecognized vocabulary digest).
 * The distinction is the point: a payload the server cannot check surfaces
 * instead of being silently accepted.
 */
export type TranscriptLayersStatus = "matched" | "drifted" | "unverified";

/**
 * One attribute of a session-tree node, as the device emitted it: what the
 * attribute is, the state the device assigned it, and the captured value in
 * whichever shape the device used — a bare `value`/`valueLabel` pair, free
 * `text`, or a measured `count` with its `unit`. All value shapes are nullable
 * because only one is set per attribute (a state-only attribute carries none).
 * `startWord`/`endWord` bracket the note's word stream, so an attribute maps
 * back to the transcript segment it was heard in; `position` is the stored
 * index of the attribute under its node.
 */
export interface TranscriptLayersAttribute {
	position: number;
	attribute: string;
	label: string;
	state: string;
	value: string | null;
	valueLabel: string | null;
	text: string | null;
	count: number | null;
	unit: string | null;
	startWord: number;
	endWord: number;
}

/**
 * One node of the device's session tree. `id`/`parentId` are the device's own
 * ids (roots have `parentId: null`), `position` the node's index among its
 * parent's children and `depth` its distance from the root — that adjacency
 * plus sibling order is the tree the server's re-derivation must reproduce,
 * which is what makes a drift verdict meaningful. `startWord`/`endWord`
 * bracket the words the node was derived from.
 */
export interface TranscriptLayersNode {
	id: string;
	parentId: string | null;
	position: number;
	depth: number;
	kind: string;
	label: string;
	state: string;
	startWord: number;
	endWord: number;
	attributes: TranscriptLayersAttribute[];
	children: TranscriptLayersNode[];
}

/** One layer the server found drifted, with the evidence for the alarm. */
export interface TranscriptLayersDrift {
	layer: string;
	detail: string;
}

/**
 * A corrected size specification: the dimensions the technician set together
 * and the flattened value they produced. `depth` is null for a surface area
 * (a volume carries it); `unit` is "" when the dimensions carry their own units.
 */
export interface TranscriptLayersMeasurement {
	length: number;
	width: number;
	depth: number | null;
	value: string;
	unit: string;
}

/**
 * One side of a correction — the engine's reading or the technician's.
 * `count`/`measurement` are nullable because only one value shape is set per
 * side (a state-only fact carries neither), and a state-only side reads by its
 * `state` alone.
 */
export interface TranscriptLayersCorrectionSide {
	state: string;
	value: string;
	valueLabel: string;
	text: string;
	count: number | null;
	unit: string;
	measurement: TranscriptLayersMeasurement | null;
}

/**
 * One technician correction to one fact of the device's session tree. The
 * identity is (nodeID, attribute) — the same key the tree's own attributes
 * carry — so the corrections arrive as a flat list that the UI joins to the
 * tree rather than nested inside it. Both sides are present to show what the
 * engine read beside what the technician said. `evidenceStart`/`evidenceEnd`
 * are the engine's own word indices: they locate the fact for a reader of the
 * engine's word stream and MUST NOT be used to index the note's words.
 */
export interface TranscriptLayersCorrection {
	nodeID: string;
	attribute: string;
	original: TranscriptLayersCorrectionSide;
	corrected: TranscriptLayersCorrectionSide;
	correctedAt: string;
	evidenceStart: number;
	evidenceEnd: number;
	evidenceText: string;
}

/**
 * The device's derived extraction layers for one note version plus the
 * server's verdict on them. `vocabularyVersion` is the device's vocabulary
 * digest, the thing the server compares its own vocabulary against; `derivedAt`
 * is when the verdict row was written.
 *
 * `nodes` mirrors the persisted row set, nested into the device's tree via
 * `children` (roots have `parentId: null`) in stored sibling order, with each
 * node's `attributes` in stored order. Orphans cannot occur: ingest writes the
 * header, nodes, attributes and drift rows in one transaction.
 *
 * `corrections` is the technician's capture-time amendments, joined to `nodes`
 * by (nodeID, attribute). It is always an array — `[]` when nothing was
 * corrected, never null — and, like the rest of the record, rides the detail
 * read only (queue rows carry `layersStatus` alone).
 */
export interface TranscriptLayers {
	version: number;
	vocabularyVersion: string;
	status: TranscriptLayersStatus;
	driftSummary: string | null;
	derivedAt: string;
	drift: TranscriptLayersDrift[];
	nodes: TranscriptLayersNode[];
	corrections: TranscriptLayersCorrection[];
}

/**
 * One ontology resource a note's derivation created or matched, for the detail
 * panel's Resources list. `kind` is the ontology resource name; `uri` is the
 * canonical API path (`/api/<resource>/<id>`), empty when the kind has no
 * resource route yet. The review path is derived from kind+id, never parsed
 * from `uri`.
 */
export interface TranscriptDerivedResource {
	kind: string;
	id: number;
	role: "created" | "matched";
	uri: string;
}

/**
 * A raw audio recording under review. Audio carries a duration, the server
 * transcript (timestamped lines), a point/timestamp track, and any photos
 * taken during the recording (timeline events).
 */
export interface TranscriptNote {
	id: string;
	timestamp: string;
	technician: string;
	route: string;
	surveyId: string;
	assignment: string;
	/** Human-readable location context, e.g. "near Site #455". */
	location: string;
	status: TranscriptReviewStatus;
	flags: TranscriptFlag[];
	durationSeconds: number;
	/** Server-side transcription — timestamped lines (raw Type 1 evidence). */
	transcript: TranscriptLine[];
	/** Proposed Type 2 structured records (server-side extraction). */
	extractions: TranscriptExtraction[];
	/**
	 * The server's re-derived records, read-only: rebuilt on read from the
	 * note's own words (nothing persisted; see `TranscriptDerivedExtraction`),
	 * present only for notes whose version carried an NLU layers snapshot.
	 * Always `[]` on queue rows and for legacy notes — a legacy note keeps its
	 * persisted rows in `extractions`, and the two lists never overlap.
	 */
	derivedExtractions: TranscriptDerivedExtraction[];
	/**
	 * Ontology resources (site, feature, inspection, treatment, ...) this note
	 * version's derivation created or matched, each labeled with its role.
	 * Detail-only (heavy-field rule): always `[]` on queue rows.
	 */
	derivedResources: TranscriptDerivedResource[];
	/**
	 * Light NLU verdict projection of `layers` — the status alone, cheap enough
	 * to ship on every queue row (present on queue AND detail reads instead of
	 * being nulled out under the heavy-field rule). Null when the note's version
	 * sent no snapshot or the note predates the layers table.
	 */
	layersStatus: TranscriptLayersStatus | null;
	/**
	 * Device-derived layers + the server's verdict on them, exposed only on the
	 * detail read. Null on queue rows (heavy-field rule, like `knowledge`), and
	 * null when the note's version sent no snapshot or the note predates the
	 * layers table.
	 */
	layers: TranscriptLayers | null;
	/** Point/timestamp track captured during recording. */
	locationStream: TranscriptLocationPoint[];
	/** Photos taken during the recording (timeline events). */
	photos: TranscriptPhoto[];
}

// ---------------------------------------------------------------------------
// Request payloads for the Phase 4 review-mutation endpoints (camelCase on
// the wire; shapes locked with resource handlers).
// ---------------------------------------------------------------------------

/** PUT /api/transcript/{id}/transcript — non-destructive line edit. */
export interface TranscriptEditRequest {
	/** TranscriptLine.id of the line being corrected. */
	lineId: number;
	/** Corrected text; the server preserves the original (original_text). */
	text: string;
}

/** PUT /api/transcript/{id}/status — review lifecycle transition. */
export interface TranscriptStatusRequest {
	status: TranscriptReviewStatus;
}

/**
 * POST /api/transcript/{id}/extraction — create a proposal, or correct an
 * existing one by supersession (set `supersedesId` to the record being
 * corrected; it drops out of subsequent GET extractions[]).
 */
export interface TranscriptExtractionRequest {
	kind: TranscriptExtractionKind;
	/** Short label, e.g. "Observation · Feature". */
	label: string;
	/** One-line summary, e.g. "Swimming Pool · Wet". */
	summary: string;
	/** Transcript segment the record was read from. */
	startSeconds?: number;
	endSeconds?: number;
	/**
	 * Row id of the record this correction supersedes (the id shown in the
	 * detail's `TranscriptExtraction.id` as a decimal string; the wire body
	 * carries it as a JSON number).
	 */
	supersedesId?: number;
	/** Structured fields in the ontology vocabulary (full corrected set). */
	fields: TranscriptPropertyEntry[];
}

/** POST /api/review — one referenced object of a review. */
export interface TranscriptReviewObject {
	/**
	 * Ontology object type: "transcript" for the audio note as a whole, or
	 * the extraction kind ("observation"|"inspection"|"treatment"|
	 * "constraint") for an extraction card.
	 */
	objectType: "transcript" | TranscriptExtractionKind;
	/**
	 * Object row id — the numeric DB id (the detail's string ids convert to
	 * these; the wire body carries them as JSON numbers).
	 */
	objectId: number;
}

/** POST /api/review — create a Review artifact (reviewer, objects, findings). */
export interface TranscriptReviewRequest {
	/** Scope of the evaluation — the review UI always evaluates one object. */
	scopeType: "object";
	/** Kind of evaluation — reviewer comments are data-quality findings. */
	reviewType: "data_quality";
	/** Free-text finding/comment. */
	findings?: string;
	severity?: string;
	recommendedAction?: string;
	/** Objects the review references (note and/or extraction rows). */
	objects: TranscriptReviewObject[];
}

/** POST /api/transcript/{id}/clarify — ask the technician a question. */
export interface TranscriptClarifyRequest {
	question: string;
}

// ---------------------------------------------------------------------------
// Vision auto-scaler — per-provider configs + active-for-workload slots.
// Shapes match resource/vision_auto_scale.go (see local://autoscaler-design.md
// Part 2a/2d). The `type` column was removed; workload targeting is now the
// active-for-training / active-for-inference slot pair.
// ---------------------------------------------------------------------------

export type WorkloadType = "training" | "inference";

export interface AutoScaleConfig {
	provider: string;
	enabled: boolean;
	location: string;
	instance: string;
	image: string;
	gpu: string;
	disk_gb: number;
	network_storage_gb: number;
	idle_shutdown_mins: number;
	startup_timeout_minutes: number;
	minimum_duration_seconds: number;
	active_for_training: boolean;
	active_for_inference: boolean;
	updated_at: string;
	updater_id: number | null;
}

// GET /api/vision/auto-scale/configs and PUT /api/vision/auto-scale/active
// both return this shape.
export interface AutoScaleConfigsResponse {
	configs: AutoScaleConfig[];
	active_training_provider: string;
	active_inference_provider: string;
}

export interface AutoScaleActiveRequest {
	training_provider: string;
	inference_provider: string;
}

export interface AutoScaleStatusPerWorkload {
	enabled: boolean;
	provider: string;
	running_workers: number;
	provisioning_count: number;
	pending_tasks: number;
}

export interface AutoScaleStatusResponse {
	training: AutoScaleStatusPerWorkload;
	inference: AutoScaleStatusPerWorkload;
	monthly_spend: number;
	idle_shutdown_mins: number;
}

// GET /api/vision/auto-scale/provider — provider registry metadata.
export interface AutoScaleProvider {
	name: string;
	configured: boolean;
	selection: "instance" | "gpu";
	locations: string[];
}

export interface AutoScaleProvidersResponse {
	providers: AutoScaleProvider[];
}

export interface AutoScaleHistoryEntry {
	id: string;
	action_type: string;
	triggered_by: string;
	worker_id: string | null;
	droplet_id: string | null;
	reason: string | null;
	created_at: string;
}

// POST /api/vision/auto-scale/start and /api/vision/auto-scale/emergency-stop.
export interface ManualStartResponse {
	status: string;
	message: string;
}
// GET /api/sudo/background — one job waiting in the background job queue.
// pool is the execution tier: "fast" (parallel pool) or "slow" (serial
// listener), derived from platform.FastPoolJob. priority is the dispatch
// priority: lower numbers run first (0 = most prioritized).
export interface SudoBackgroundQueueEntry {
	id: number;
	type: string;
	created: string;
	row_id: number;
	row_id_string: string | null;
	cron_id: number | null;
	pool: "fast" | "slow";
	priority: number;
}

// PUT /api/sudo/background/queue/{id} — body: the new dispatch
// priority for a queued job (lower number = runs first).
export interface SudoBackgroundPriorityRequest {
	priority: number;
}

// PUT /api/sudo/background/queue/{id} — response: the updated job
// and its new priority.
export interface SudoBackgroundPriorityResponse {
	id: number;
	priority: number;
}

// GET /api/sudo/background — one recorded run of a scheduled cron job type.
// queue_wait_ms is started - triggered when the run started, else null.
export interface SudoBackgroundCronRun {
	id: number;
	triggered: string | null;
	started: string | null;
	completed: string | null;
	error: string | null;
	queue_wait_ms: number | null;
}

// GET /api/sudo/background — one scheduled cron job type with its recent runs.
export interface SudoBackgroundCron {
	type: string;
	interval_seconds: number;
	runs: SudoBackgroundCronRun[];
}

// GET /api/sudo/background — current job queue, in-flight task, and cron run
// history. current_tasks holds jobs currently being processed by a worker; it
// is kept in process memory only (never persisted), so it disappears on
// restart. More than one may run at once (the fast pool runs several workers
// in parallel, and a slow job runs concurrently on the single listener).
export interface SudoBackgroundCurrentTask {
	id: number;
	type: string;
	row_id: number;
	row_id_string: string | null;
	pool: "fast" | "slow";
	started: string;
}

export interface SudoBackgroundResponse {
	current_tasks: SudoBackgroundCurrentTask[];
	queue: SudoBackgroundQueueEntry[];
	crons: SudoBackgroundCron[];
}

// GET /api/sudo/background/statistics — per-job-type aggregates over a rolling
// window. Durations and queue waits are in milliseconds over attempts with a
// recorded started time; failed/succeeded/attempts are attempt counts.
export interface SudoBackgroundStatsType {
	type: string;
	attempts: number;
	succeeded: number;
	failed: number;
	jobs: number;
	retried_jobs: number;
	avg_duration_ms: number | null;
	p50_duration_ms: number | null;
	p95_duration_ms: number | null;
	max_duration_ms: number | null;
	avg_queue_wait_ms: number | null;
}

// GET /api/sudo/background/statistics — since is the start of the window.
export interface SudoBackgroundStatsResponse {
	since: string;
	types: SudoBackgroundStatsType[];
}

// GET /api/sudo/background/history — one job's final (terminal) attempt.
// duration_ms is run time (completed - started); queue_wait_ms is started -
// enqueued for the final attempt; both are null when the attempt never started.
export interface SudoBackgroundHistoryEntry {
	job_id: number;
	type: string;
	row_id: number | null;
	row_id_string: string | null;
	cron_id: number | null;
	attempt_no: number;
	status: "succeeded" | "failed";
	enqueued_at: string | null;
	started_at: string | null;
	completed_at: string;
	duration_ms: number | null;
	queue_wait_ms: number | null;
	error: string | null;
}

// GET /api/sudo/background/history — reverse-chronological job history.
export interface SudoBackgroundHistoryResponse {
	entries: SudoBackgroundHistoryEntry[];
}

// /api/sudo/ask-log — one logged Map Review natural-language ask, with the
// spec it resolved to. `answered` is false when the question could not be
// expressed; `explanation` then carries the reason, otherwise the resolved
// filter in plain words. The optional fields are null when the ask never
// reached the stage that fills them.
//
// It is the row shape both surfaces answer with: the legacy GET's entries and a
// `QUERY /api/sudo/ask-log` page's items are one declaration server-side
// (platform.AskLogEntries), so the view renders either without a second shape.
export interface SudoAskLogEntry {
	id: number;
	created: string;
	organization_id: number;
	organization_name: string;
	user_id: number;
	user_name: string;
	question: string;
	tz_offset_minutes: number | null;
	answered: boolean;
	explanation: string;
	table_name: string | null;
	spec: string | null;
	total: number | null;
	latency_ms: number;
	used_model: boolean;
	model: string | null;
	error: string | null;
}

/** A planet item that has corpus (human-labeled) pools, with pool count. */
export interface CorpusItem {
	item_id: string;
	pool_count: number;
}

/** A vision.pool row belonging to the human-labeled training corpus. */
export interface CorpusPool {
	id: number;
	condition: string;
	created_at: string;
	reviewed: boolean;
	reviewer_id: number | null;
	reviewed_at: string | null;
	item_id: string | null;
	area_sqm: number;
	geometry: GeoJSON.Geometry | null;
}

// POST /api/manual-pool — body for adding a pool by hand. `use_parcel` uses
// the situs parcel geometry/centroid; `use_parcel=false` requires `geometry`
// (a GeoJSON Polygon in EPSG:4326). `create_new` forces a new pool row even
// when the location overlaps a previously removed pool.
export interface ManualPoolCreateRequest {
	situs_id?: number;
	lng?: number;
	lat?: number;
	condition: "blue" | "green" | "dry" | "unknown";
	pool_type: "standard" | "above-ground";
	use_parcel: boolean;
	geometry?: GeoJSON.Polygon;
	create_new: boolean;
}

// POST /api/manual-pool — info about the removed pool a drawn/located polygon
// overlaps, offered for reactivation instead of a duplicate add.
export interface DeletedPoolInfo {
	feature_id: number;
	address: string;
	deleted_at: string;
	deletor_id: number;
	removal_note: string;
}

// POST /api/manual-pool — success (created) or overlap (with a removed pool).
export interface ManualPoolCreateResponse {
	created: boolean;
	feature_id?: number;
	site_id?: number;
	pool?: {
		feature_id: number;
		source: string;
		pool_type: string;
		condition: string;
		exclude_from_training: boolean;
		created_at: string;
		created_by: number;
		deleted_at: null;
	};
	overlap?: { deleted_pool: DeletedPoolInfo };
}

// POST /api/manual-pool/{feature_id}/remove — body requires a non-empty removal note.
export interface ManualPoolRemoveRequest {
	note: string;
}

// POST /api/manual-pool/{feature_id}/remove — the soft-removed pool row.
export interface ManualPoolRemoveResponse {
	feature_id: number;
	deleted_at: string;
	deletor_id: number;
	removal_note: string;
}

// GET /api/manual-pool/review — one unreviewed manual pool addition.
export interface ManualReviewTask {
	task_id: number;
	feature_id: number;
	site_id: number;
	address: string;
	condition: string;
	pool_type: string;
	created_at: string;
	created_by: { id: number; display_name: string };
	exclude_from_training: boolean;
	geometry?: GeoJSON.Polygon;
}

// POST /api/manual-pool/review/{task_id} — confirm (optionally setting
// exclude_from_training) or reject (soft-removes the pool).
export interface ManualReviewDecisionRequest {
	action: "confirm" | "reject";
	exclude_from_training?: boolean;
}

// POST /api/pool-condition/run — response for the medium-res analysis trigger.
export interface PoolConditionRunResponse {
	enqueued: boolean;
	reason: string;
	job_id: number;
}

// --- Maintenance lockout (design doc/design/maintenance-mode.md) -------------------
// Shared status/request types for the per-host maintenance gates, restore
// recovery controller, and sudo maintenance controls.

// One host gate as reported by the maintenance status endpoints. origin is
// "restore" when the recovery controller armed the gate automatically,
// "manual" when a root operator armed it; null while the gate is inactive.
export interface MaintenanceGate {
	active: boolean;
	origin: "restore" | "manual" | null;
	message: string | null;
	startedAt: string | null;
}

// Live restore-rebuild pipeline progress (present only while a restore-backed
// rebuild session is armed on the sync gate).
export interface MaintenanceRestoreProgress {
	sentinelId: number | null;
	stage: string | null;
	stagesDone: number;
	stagesTotal: number;
	pendingJobs: number;
	scope: string[];
}

// GET /api/maintenance and GET /api/sudo/maintenance (sudo returns the same
// shape; both gates plus the optional restore sub-object).
export interface MaintenanceStatus {
	gates: { sync: MaintenanceGate; rmo: MaintenanceGate };
	restore: MaintenanceRestoreProgress | null;
}

// GET /api/rmo/maintenance — unauthenticated single-gate status for the RMO
// SPA's friendly maintenance takeover page.
export interface RMOMaintenanceStatus {
	active: boolean;
	message: string | null;
	startedAt: string | null;
}

export type MaintenanceHost = "sync" | "rmo";
export type MaintenanceHostOrAll = MaintenanceHost | "all";

// PUT /api/sudo/maintenance — arm manual maintenance on one host or both.
// message is optional and shown to locked-out users; until is an optional
// max-duration auto-clear safety.
export interface MaintenanceSetRequest {
	host: MaintenanceHostOrAll;
	active: boolean;
	message?: string;
	until?: string;
}

// DELETE /api/sudo/maintenance — clear a manual arm on one host or both.
// force: true confirms clearing a restore-origin gate before its queue has
// drained (leaves the restore sentinel unprocessed so a manual rebuild can
// re-run later).
export interface MaintenanceClearRequest {
	host: MaintenanceHostOrAll;
	force?: boolean;
}

// GET /api/treatment — one intervention recorded against a feature.
// area_treated is square metres; quantity_unit is the source's verbatim unit.
// Datetimes are UTC ISO strings — render them through the Time* components.
// creator_name/product_name are resolved on the detail endpoint only.
export interface Treatment {
	id: number;
	feature_id: number;
	organization_id: number;
	creator_id: number | null;
	creator_name?: string | null;
	product_id: number | null;
	product_name?: string | null;
	method: string | null;
	geometry?: GeoJSON.Geometry | null;
	area_treated: number | null;
	quantity: number | null;
	quantity_unit: string | null;
	start_datetime: string;
	end_datetime: string | null;
	created: string;
	uri: string;
	// observation_id is the Observation whose conditions led to this treatment
	// (treatment.observation_id), null when the source did not let the leading
	// observation be recovered. It is the link the treatment popup walks to the
	// observation.
	observation_id: number | null;
	// Detail-endpoint enrichment: product_name/creator_name are joined;
	// catalog_product_id is the linked shared-catalog product's id (the page the
	// popup links to; absent for an unlinked district product);
	// certification_number/certification_uri are the applicator's certificate;
	// quantity_preferred/quantity_unit_preferred carry the amount applied in the
	// district's preferred units when a unit preference converts it.
	catalog_product_id?: number | null;
	certification_number?: string | null;
	certification_uri?: string | null;
	quantity_preferred?: number | null;
	quantity_unit_preferred?: string | null;
}

// GET /api/product — one live product the session district has configured (what
// a Treatment's product_id references), ordered by name. This is the district's
// own list, not the shared pesticide catalog; a retired product is left out,
// though its row and past Treatments stay readable. control_class is null until
// the district curates it.
export interface Product {
	id: number;
	name: string;
	// product_catalog_id is the shared catalog entry this district-named row is
	// an instance of, as the catalog's stable slug; null for a row the district
	// has not linked to the catalog. The add-from-catalog picker uses it to hide
	// the entries the district already has.
	product_catalog_id: string | null;
	control_class: string | null;
	// kind is the row's kind: "simple" for an ordinary district product, "mix"
	// for a custom mix (a product whose components are mix_recipe_component
	// rows). A mix has no product_catalog_id of its own.
	kind: ProductKind;
	// residual_days is the district's planned residual in days (the interval on
	// product.residual_duration, read as whole days); null until the district
	// sets one. residual_days_suggested is the shared catalog's conservative
	// suggestion (the shortest window the linked entry's rates state, from its
	// low bound or its high where the label states a single figure), shown as a
	// hint only — the stored value is always the district's.
	residual_days: number | null;
	residual_days_suggested: number | null;
	// target_life_stages is the district's own set of life stages it uses the
	// product on, as ";"-separated codes from the icon set (I1..I4, P); null
	// until the district sets it. It is district curation, not the catalog's.
	target_life_stages: string | null;
	// catalog_revision is the catalog revision this row last accepted from;
	// catalog_revision_current is the linked entry's revision now. The row is
	// behind exactly when the first is below the second, and
	// catalog_update_available is that server-computed comparison.
	catalog_revision: number | null;
	catalog_revision_current: number | null;
	catalog_update_available: boolean;
	catalog_accepted_at: string | null;
	// registry_brand_id and registry_brand_name are the brand this district row
	// names — the packaged product a technician picks off the shelf. The id is
	// inherited from the catalog entry when the product was added; the name is
	// read from the brand row. Both are null for a custom mix and for a product
	// whose catalog entry the registry could not match to a brand.
	registry_brand_id: number | null;
	registry_brand_name: string | null;
	// catalog is the linked shared catalog entry (read-only reference facts:
	// name, EPA registration, active ingredients, formulation, manufacturer,
	// category, label, rates). Null when the row is unlinked or the catalog
	// does not carry its slug. The district's own name above is authoritative
	// and may differ from catalog.name.
	catalog: PesticideCatalogProduct | null;
}

// GET /api/intervention — one non-product (physical-control) intervention the
// session district has named, ordered by name. A treatment records an
// intervention instead of a product; a retired row stays on past treatments
// but is no longer offered for new ones, so `retired` is null while active.
// residual_days is the district's planned residual in days for physical
// control (the interval on intervention.residual_duration), null until the
// district sets one.
export interface Intervention {
	id: number;
	name: string;
	control_class: string | null;
	retired: string | null;
	residual_days: number | null;
}

// GET /api/inspection — one inspection recorded against a feature. The
// location shown for an inspection is the linked feature's location, not a
// geometry of its own. stage is one of early|late|mixed and density one of
// zero|trace|low|medium|high|loaded, both null when unrecorded. Datetimes are
// UTC ISO strings — render them through the Time* components.
export interface Inspection {
	id: number;
	feature_id: number;
	organization_id: number;
	creator_id: number | null;
	method: string;
	start_datetime: string;
	end_datetime: string | null;
	production: boolean;
	stage: string | null;
	density: string | null;
	created: string;
	uri: string;
}

// GET /api/integration/fieldseeker/service-request/{id} — one mirrored
// FieldSeeker service request (latest version for a globalid). Datetimes are
// UTC ISO strings on the wire; imported communications carry this resource's
// uri as their source.
export interface FieldseekerServiceRequestDTO {
	uri: string;
	id: string;
	version: number;
	deleted: boolean;
	object_id: number;
	sr_number: number | null;
	status: string | null;
	source: string | null;
	priority: string | null;
	target: string | null;
	description: string | null;
	comments: string | null;
	notes_for_technician: string | null;
	notes_for_customer: string | null;
	field_notes: string | null;
	program_actions: string | null;
	issues_reported: string | null;
	next_action: string | null;
	jurisdiction: string | null;
	zone: string | null;
	zone_2: string | null;
	assigned_technician: string | null;
	supervisor: string | null;
	entry_technician: string | null;
	reviewed: number | null;
	reviewed_by: string | null;
	reviewed_date: string | null;
	tech_closed: string | null;
	closed_date: string | null;
	due_date: string | null;
	estimated_completion_date: string | null;
	accepted: number | null;
	accepted_by: string | null;
	accepted_date: string | null;
	rejected_by: string | null;
	rejected_date: string | null;
	rejected_reason: string | null;
	schedule_period: string | null;
	schedule_notes: string | null;
	scheduled: number | null;
	scheduled_date: string | null;
	notified: number | null;
	notified_date: string | null;
	allowed: string | null;
	permission: number | null;
	caller_anonymous: number | null;
	caller_name: string | null;
	caller_email: string | null;
	caller_phone_1: string | null;
	caller_phone_2: string | null;
	caller_company: string | null;
	caller_address_1: string | null;
	caller_address_2: string | null;
	caller_city: string | null;
	caller_state: string | null;
	caller_zip: string | null;
	caller_other: string | null;
	caller_contact_preference: string | null;
	request_company: string | null;
	request_address_1: string | null;
	request_address_2: string | null;
	request_city: string | null;
	request_state: string | null;
	request_zip: string | null;
	cross_street: string | null;
	subdivision: string | null;
	map_grid: string | null;
	recorded_date: string | null;
	created_at: string | null;
	first_response_date: string | null;
	response_day_count: number | null;
	has_dog: number | null;
	has_spanish_speaker: number | null;
	record_status: number | null;
	created_user: string | null;
	created_date: string | null;
	last_edited_user: string | null;
	last_edited_date: string | null;
	creator: string | null;
	edit_date: string | null;
	editor: string | null;
	external_id: string | null;
	external_error: string | null;
	point_location_id: string | null;
	notification_timestamp: string | null;
	h3cell: string | null;
	location: { latitude: number; longitude: number; accuracy?: number } | null;
}

export class FieldseekerServiceRequest {
	uri: string;
	id: string;
	version: number;
	deleted: boolean;
	object_id: number;
	sr_number: number | null;
	status: string | null;
	source: string | null;
	priority: string | null;
	target: string | null;
	description: string | null;
	comments: string | null;
	notes_for_technician: string | null;
	notes_for_customer: string | null;
	field_notes: string | null;
	program_actions: string | null;
	issues_reported: string | null;
	next_action: string | null;
	jurisdiction: string | null;
	zone: string | null;
	zone_2: string | null;
	assigned_technician: string | null;
	supervisor: string | null;
	entry_technician: string | null;
	reviewed: number | null;
	reviewed_by: string | null;
	reviewed_date: Date | null;
	tech_closed: string | null;
	closed_date: Date | null;
	due_date: Date | null;
	estimated_completion_date: Date | null;
	accepted: number | null;
	accepted_by: string | null;
	accepted_date: Date | null;
	rejected_by: string | null;
	rejected_date: Date | null;
	rejected_reason: string | null;
	schedule_period: string | null;
	schedule_notes: string | null;
	scheduled: number | null;
	scheduled_date: Date | null;
	notified: number | null;
	notified_date: Date | null;
	allowed: string | null;
	permission: number | null;
	caller_anonymous: number | null;
	caller_name: string | null;
	caller_email: string | null;
	caller_phone_1: string | null;
	caller_phone_2: string | null;
	caller_company: string | null;
	caller_address_1: string | null;
	caller_address_2: string | null;
	caller_city: string | null;
	caller_state: string | null;
	caller_zip: string | null;
	caller_other: string | null;
	caller_contact_preference: string | null;
	request_company: string | null;
	request_address_1: string | null;
	request_address_2: string | null;
	request_city: string | null;
	request_state: string | null;
	request_zip: string | null;
	cross_street: string | null;
	subdivision: string | null;
	map_grid: string | null;
	recorded_date: Date | null;
	created_at: Date | null;
	first_response_date: Date | null;
	response_day_count: number | null;
	has_dog: number | null;
	has_spanish_speaker: number | null;
	record_status: number | null;
	created_user: string | null;
	created_date: Date | null;
	last_edited_user: string | null;
	last_edited_date: Date | null;
	creator: string | null;
	edit_date: Date | null;
	editor: string | null;
	external_id: string | null;
	external_error: string | null;
	point_location_id: string | null;
	notification_timestamp: string | null;
	h3cell: string | null;
	location: Location | null;

	constructor(json: FieldseekerServiceRequestDTO) {
		this.uri = json.uri;
		this.id = json.id;
		this.version = json.version;
		this.deleted = json.deleted;
		this.object_id = json.object_id;
		this.sr_number = json.sr_number;
		this.status = json.status;
		this.source = json.source;
		this.priority = json.priority;
		this.target = json.target;
		this.description = json.description;
		this.comments = json.comments;
		this.notes_for_technician = json.notes_for_technician;
		this.notes_for_customer = json.notes_for_customer;
		this.field_notes = json.field_notes;
		this.program_actions = json.program_actions;
		this.issues_reported = json.issues_reported;
		this.next_action = json.next_action;
		this.jurisdiction = json.jurisdiction;
		this.zone = json.zone;
		this.zone_2 = json.zone_2;
		this.assigned_technician = json.assigned_technician;
		this.supervisor = json.supervisor;
		this.entry_technician = json.entry_technician;
		this.reviewed = json.reviewed;
		this.reviewed_by = json.reviewed_by;
		this.reviewed_date = json.reviewed_date
			? new Date(json.reviewed_date)
			: null;
		this.tech_closed = json.tech_closed;
		this.closed_date = json.closed_date ? new Date(json.closed_date) : null;
		this.due_date = json.due_date ? new Date(json.due_date) : null;
		this.estimated_completion_date = json.estimated_completion_date
			? new Date(json.estimated_completion_date)
			: null;
		this.accepted = json.accepted;
		this.accepted_by = json.accepted_by;
		this.accepted_date = json.accepted_date
			? new Date(json.accepted_date)
			: null;
		this.rejected_by = json.rejected_by;
		this.rejected_date = json.rejected_date
			? new Date(json.rejected_date)
			: null;
		this.rejected_reason = json.rejected_reason;
		this.schedule_period = json.schedule_period;
		this.schedule_notes = json.schedule_notes;
		this.scheduled = json.scheduled;
		this.scheduled_date = json.scheduled_date
			? new Date(json.scheduled_date)
			: null;
		this.notified = json.notified;
		this.notified_date = json.notified_date
			? new Date(json.notified_date)
			: null;
		this.allowed = json.allowed;
		this.permission = json.permission;
		this.caller_anonymous = json.caller_anonymous;
		this.caller_name = json.caller_name;
		this.caller_email = json.caller_email;
		this.caller_phone_1 = json.caller_phone_1;
		this.caller_phone_2 = json.caller_phone_2;
		this.caller_company = json.caller_company;
		this.caller_address_1 = json.caller_address_1;
		this.caller_address_2 = json.caller_address_2;
		this.caller_city = json.caller_city;
		this.caller_state = json.caller_state;
		this.caller_zip = json.caller_zip;
		this.caller_other = json.caller_other;
		this.caller_contact_preference = json.caller_contact_preference;
		this.request_company = json.request_company;
		this.request_address_1 = json.request_address_1;
		this.request_address_2 = json.request_address_2;
		this.request_city = json.request_city;
		this.request_state = json.request_state;
		this.request_zip = json.request_zip;
		this.cross_street = json.cross_street;
		this.subdivision = json.subdivision;
		this.map_grid = json.map_grid;
		this.recorded_date = json.recorded_date
			? new Date(json.recorded_date)
			: null;
		this.created_at = json.created_at ? new Date(json.created_at) : null;
		this.first_response_date = json.first_response_date
			? new Date(json.first_response_date)
			: null;
		this.response_day_count = json.response_day_count;
		this.has_dog = json.has_dog;
		this.has_spanish_speaker = json.has_spanish_speaker;
		this.record_status = json.record_status;
		this.created_user = json.created_user;
		this.created_date = json.created_date ? new Date(json.created_date) : null;
		this.last_edited_user = json.last_edited_user;
		this.last_edited_date = json.last_edited_date
			? new Date(json.last_edited_date)
			: null;
		this.creator = json.creator;
		this.edit_date = json.edit_date ? new Date(json.edit_date) : null;
		this.editor = json.editor;
		this.external_id = json.external_id;
		this.external_error = json.external_error;
		this.point_location_id = json.point_location_id;
		this.notification_timestamp = json.notification_timestamp;
		this.h3cell = json.h3cell;
		this.location = json.location
			? new Location(
					json.location.latitude,
					json.location.longitude,
					json.location.accuracy ?? undefined,
				)
			: null;
	}

	static fromJSON(
		json: FieldseekerServiceRequestDTO,
	): FieldseekerServiceRequest {
		return new FieldseekerServiceRequest(json);
	}
}

export interface PesticideCatalogRate {
	id: number;
	product_catalog_id: number;
	scenario_description: string | null;
	target_life_stages: string | null;
	rate_low: number | null;
	rate_high: number | null;
	rate_unit: string | null;
	rate_basis: string | null;
	residual_days_low: number | null;
	residual_days_high: number | null;
	amount_unit: string | null;
	calculation_notes: string | null;
	preferred?: PesticideCatalogPreferredRate | null;
}

// PesticideCatalogPreferredRate is a label rate projected into the district's
// preferred units: the same rate, converted. The label's own fields above are
// untouched. converted is false when the label already reads that way or the
// rate could not be converted (a percentage, a concentration, per stem, ...).
export interface PesticideCatalogPreferredRate {
	rate_low: number | null;
	rate_high: number | null;
	rate_unit: string | null;
	rate_basis: string | null;
	converted: boolean;
}

export interface PesticideCatalogProduct {
	id: number;
	product_id: string;
	name: string;
	category: string;
	formulation: string | null;
	manufacturer: string | null;
	active_ingredients: string | null;
	ai_composition_source: string | null;
	epa_registration: string | null;
	epa_source: string | null;
	// dpr_registration_number is the California DPR registration number
	// (VectorSurv's product key), resolved from the EPA registration against
	// VectorSurv's catalog. Null until resolved, or when no match was certain —
	// never "has no registration".
	dpr_registration_number: string | null;
	target_life_stages: string | null;
	label_file: string | null;
	sds_file: string | null;
	label_url: string | null;
	label_source: string | null;
	formulation_source: string | null;
	formulation_source_note: string | null;
	manufacturer_source: string | null;
	manufacturer_source_note: string | null;
	created: string;
	retired: string | null;
	label_document_url: string | null;
	sds_document_url: string | null;
	// The address each stored PDF was downloaded from and when. Provenance, not
	// a live link: the catalog serves its own copy.
	label_source_url: string | null;
	label_retrieved_at: string | null;
	sds_source_url: string | null;
	sds_retrieved_at: string | null;
	// in_district reports whether the session district currently holds this
	// catalog entry — a live (non-retired) district product links to its slug.
	// It is computed server-side from the district's own rows, never inferred
	// from a name match, and it drives the catalog's add/remove action.
	in_district: boolean;
	// district_product_id is the live district product that owns the entry when
	// in_district is true, and null otherwise. Removing the entry retires that
	// row (DELETE /api/product/{id}); the shared catalog row is never written.
	district_product_id: number | null;
	rates: PesticideCatalogRate[];
	// revision is the entry's monotonically increasing revision. A district row
	// whose catalog_revision is below it is behind; accepting a catalog
	// revision increments it and writes the change log below.
	revision: number;
	registry_product_status: string | null;
	label_accepted_date: string | null;
	// The root curator's revision blocks. pending_change_count badges the
	// entry; pending_changes carries the field-by-field difference; change_log
	// is the accepted history; behind_organizations lists the districts that
	// hold the entry and are behind. Present only for a root session.
	pending_change_count: number;
	pending_changes?: PesticideCatalogPendingChange[];
	change_log?: PesticideCatalogChangeLogEntry[];
	behind_organizations?: CatalogStalenessHolder[];
	// registry_product_id links a promoted entry back to the import record it
	// came from.
	registry_product_id?: number;
	// The brand hop of the provenance chain. registry_brand_id is the stored
	// link; the name, activity and SDS presence are read from the brand row.
	// All are null when the registry could not match the entry to a brand —
	// which the catalog list states, because a brand-less entry has no SDS slot
	// to fill.
	registry_brand_id: number | null;
	registry_brand_name: string | null;
	registry_brand_active: boolean | null;
	registry_brand_sds_present: boolean | null;
	// registry_brand_sds_document_url is the brand's SDS download path, present
	// exactly when a sheet is filed.
	registry_brand_sds_document_url: string | null;
	// where_to_buy is the ranked distributor list for the entry's brand: the
	// reader's own district first and marked, then the rest. Carried on detail
	// responses only; a root reader also gets each coverage row's provenance.
	where_to_buy?: PesticideWhereToBuy[];
}

// PesticideCatalogFieldsRequest mirrors resource.PesticideCatalogFieldsRequest:
// the catalog columns a root user may create or restate on the sudo catalog
// manager (POST/PUT /api/sudo/pesticide-catalog). Optional columns are null when
// unset — an empty box means "the catalog does not state it", not "".
export interface PesticideCatalogFieldsRequest {
	product_id: string;
	name: string;
	category: string;
	formulation: string | null;
	manufacturer: string | null;
	active_ingredients: string | null;
	epa_registration: string | null;
	dpr_registration_number: string | null;
	target_life_stages: string | null;
	label_url: string | null;
}

// PesticideCatalogRateRequest mirrors resource.PesticideCatalogRateRequest: one
// label application scenario the sudo manager adds or restates
// (POST/PUT /api/sudo/pesticide-catalog/{id}/rate). Null means the label is
// silent about the value, not zero.
export interface PesticideCatalogRateRequest {
	scenario_description: string | null;
	target_life_stages: string | null;
	rate_low: number | null;
	rate_high: number | null;
	rate_unit: string | null;
	rate_basis: string | null;
	residual_days_low: number | null;
	residual_days_high: number | null;
	amount_unit: string | null;
	calculation_notes: string | null;
}

// ---- The brand hierarchy ---------------------------------------------------
// The unit a district buys and a technician picks up is the brand, not the
// registration: one EPA registration carries a dozen brands (private labels,
// distributor names). The chain the pages show is
// district product -> catalog entry -> registry brand -> registration ->
// label + SDS, and every hop is a stored fact with its own source and date.
//
// A brand's SDS is curated — neither CDPR nor the PPLS carries one — so the
// slot records the file, the revision the sheet states, when a person verified
// it, and who filed it. `present: false` is the missing-SDS state, shown
// plainly rather than as an empty document block.

// PesticideDistributorRelationship is how a distributor is attached to a brand:
// `distributor` carries it, `private_label` means the brand is the
// distributor's own.
export type PesticideDistributorRelationship = "distributor" | "private_label";

// PesticideCoverageProvenance says who stated one coverage row:
// `distributor_stated` (a curator loaded the distributor's own list) or
// `customer_stated` (a district said it buys from the distributor). Both may
// exist for the same distributor and district.
export type PesticideCoverageProvenance =
	| "distributor_stated"
	| "customer_stated";

// PesticideCoverage is one distributor-serves-district statement. provenance,
// recorded_by and recorded_at are present on the curator's surfaces and omitted
// from a district reader's where-to-buy rows.
export interface PesticideCoverage {
	id: number;
	organization_id: number;
	organization_name: string;
	provenance?: PesticideCoverageProvenance;
	note: string | null;
	recorded_by?: string;
	recorded_at?: string;
}

// PesticideWhereToBuy is one distributor offered for a brand. Rows arrive
// ranked: those covering the reader's own district first, marked by
// covers_reader. coverage_count is the number of districts covered, visible to
// every reader even when the names are not.
export interface PesticideWhereToBuy {
	distributor_id: number;
	name: string;
	website: string | null;
	relationship: PesticideDistributorRelationship;
	relationship_note: string | null;
	covers_reader: boolean;
	coverage_count: number;
	coverage: PesticideCoverage[];
}

// PesticideDistributorBrand is one brand attachment with its relationship.
export interface PesticideDistributorBrand {
	id: number;
	registry_brand_id: number;
	brand_name: string;
	epa_reg_no: string;
	brand_active: boolean;
	relationship: PesticideDistributorRelationship;
	note: string | null;
	added_by: string;
	added_at: string;
	updated_at: string;
}

// PesticideDistributor is the full distributor detail
// (GET /api/sudo/distributors/{id}). Root only.
export interface PesticideDistributor {
	id: number;
	name: string;
	epa_company_no: string | null;
	ca_company_no: string | null;
	website: string | null;
	note: string | null;
	active: boolean;
	added_by: string;
	added_at: string;
	updated_at: string;
	brands: PesticideDistributorBrand[];
	coverage: PesticideCoverage[];
	coverage_count: number;
	customer_stated_count: number;
}

// PesticideDistributorSummary is one distributor in the sudo list
// (GET /api/sudo/distributors).
export interface PesticideDistributorSummary {
	id: number;
	name: string;
	epa_company_no: string | null;
	ca_company_no: string | null;
	website: string | null;
	active: boolean;
	brand_count: number;
	coverage_count: number;
	customer_stated_count: number;
	added_by: string;
	added_at: string;
}

// PesticideDistributorRequest is the create/update body
// (POST /api/sudo/distributors, PUT /api/sudo/distributors/{id}).
export interface PesticideDistributorRequest {
	name: string;
	epa_company_no: string | null;
	ca_company_no: string | null;
	website: string | null;
	note: string | null;
}

// PesticideDistributorBrandRequest is the attach/restate body
// (POST/PUT /api/sudo/distributors/{id}/brands...).
export interface PesticideDistributorBrandRequest {
	registry_brand_id: number;
	relationship: PesticideDistributorRelationship;
	note: string | null;
}

// PesticideDistributorCoverageRequest records several districts in one action
// (POST /api/sudo/distributors/{id}/coverage).
export interface PesticideDistributorCoverageRequest {
	organization_ids: number[];
	provenance: PesticideCoverageProvenance;
	note: string | null;
}

// PesticideDistributorCoverageImportRequest carries a CSV keyed by organization
// name (POST /api/sudo/distributors/{id}/coverage/import).
export interface PesticideDistributorCoverageImportRequest {
	csv: string;
}

// PesticideDistributorCoverageImportResult reports what was written and which
// names did not resolve; unknown names are never dropped silently.
export interface PesticideDistributorCoverageImportResult {
	imported: PesticideCoverage[];
	unknown_names: string[];
}

// PesticideOrganizationOption is one district in the coverage picker
// (GET /api/sudo/distributor-organizations?q=).
export interface PesticideOrganizationOption {
	id: number;
	name: string;
}

// PesticideDistributorPurchaseRequest is the district's statement of which
// distributor it buys a brand from
// (POST /api/pesticide-catalog/brand/{id}/purchase).
export interface PesticideDistributorPurchaseRequest {
	distributor_id: number | null;
	distributor_name: string | null;
	note: string | null;
}

// PesticideDistributorPurchase is the customer-stated write's result.
export interface PesticideDistributorPurchase {
	distributor_id: number;
	distributor_name: string;
	created_distributor: boolean;
	coverage: PesticideCoverage;
}

// ---- Supply chain ----------------------------------------------------------
// The chain our data reaches: manufacturer (EPA registrant) -> CA
// registrant/agent (when different) -> distributor -> purchaser (district).
// Every lens is read-only and states its gaps explicitly.

export interface PesticideSupplyChainBrand {
	id: number;
	name: string;
	epa_reg_no: string;
	active: boolean;
	relationship: PesticideDistributorRelationship | "";
}

export interface PesticideSupplyChainDistributorRef {
	id: number;
	name: string;
	active: boolean;
	relationship: PesticideDistributorRelationship;
	coverage_count: number;
}

export interface PesticideSupplyChainManufacturerBrand {
	id: number;
	name: string;
	epa_reg_no: string;
	active: boolean;
	ca_reg_nos: string[];
	registration_count: number;
	distributors: PesticideSupplyChainDistributorRef[];
}

export interface PesticideSupplyChainManufacturer {
	firm_no: string | null;
	name: string | null;
	ca_registrant_firm_no: string | null;
	ca_registrant_name: string | null;
	brands: PesticideSupplyChainManufacturerBrand[];
}

export interface PesticideSupplyChainCoverage {
	id: number;
	organization_id: number;
	organization_name: string;
	distributor_id: number;
	distributor_name: string;
	distributor_active: boolean;
	provenance: PesticideCoverageProvenance;
	note: string | null;
	recorded_by: string;
	recorded_at: string;
}

export interface PesticideSupplyChainDistributor {
	id: number;
	name: string;
	epa_company_no: string | null;
	ca_company_no: string | null;
	website: string | null;
	note: string | null;
	active: boolean;
	added_by: string;
	added_at: string;
	brands: PesticideSupplyChainBrand[];
	coverage: PesticideSupplyChainCoverage[];
	coverage_count: number;
	customer_stated: PesticideSupplyChainCoverage[];
}

export interface PesticideSupplyChainReachableBrand {
	brand_id: number;
	name: string;
	epa_reg_no: string;
	relationship: PesticideDistributorRelationship;
	distributor_id: number;
	distributor: string;
}

export interface PesticideSupplyChainDistrict {
	organization_id: number;
	organization_name: string;
	active: boolean;
	distributor_count: number;
	distributors: PesticideSupplyChainCoverage[];
	brands: PesticideSupplyChainReachableBrand[];
	purchases: PesticideSupplyChainCoverage[];
}

export interface PesticideSupplyChainRef {
	id: number;
	name: string;
	active: boolean;
}

export interface PesticideSupplyChainPurchaseGap {
	organization_id: number;
	organization_name: string;
	distributor_id: number;
	distributor_name: string;
	distributor_brands: number;
	district_brands: number;
}

export interface PesticideSupplyChainGaps {
	brands_without_distributor: PesticideSupplyChainBrand[];
	distributors_without_coverage: PesticideSupplyChainRef[];
	districts_without_distributor: PesticideSupplyChainRef[];
	purchases_without_listed_brand: PesticideSupplyChainPurchaseGap[];
}

// PesticideRegistryBrandSDS is one brand's safety data sheet slot. `file` is a
// bare filename under the brand SDS directory; `document_url` is the
// authenticated download path, derived so a slot with no file has no URL.
export interface PesticideRegistryBrandSDS {
	present: boolean;
	file: string | null;
	sha256: string | null;
	revision: string | null;
	verified_at: string | null;
	source: string | null;
	updated_by: string | null;
	updated_at: string | null;
	document_url: string | null;
}

// PesticideRegistryBrandLabel is one label attributed to the brand, with the
// date the EPA accepted it and whether it is the registration's current label.
export interface PesticideRegistryBrandLabel {
	id: number;
	ca_reg_no: string;
	epa_reg_no: string;
	source_url: string;
	sha256: string;
	byte_size: number;
	accepted_date: string | null;
	is_current: boolean;
	document_url: string | null;
}

// PesticideRegistryBrandRegistration is one registry record carrying the
// brand's EPA registration — the legal parent, with the numbers, status and
// dates the registry states.
export interface PesticideRegistryBrandRegistration {
	ca_reg_no: string;
	epa_reg_no: string | null;
	registration_no: string | null;
	product_name: string;
	registrant_name: string | null;
	registrant_firm_no: string | null;
	registration_type: string | null;
	product_status: string | null;
	product_status_date: string | null;
	registered_on: string | null;
	restricted_use: boolean;
	cancel_flag: boolean | null;
	source: string;
	source_retrieved_at: string;
}

// PesticideRegistryBrandCatalogEntry is one catalog entry that is the brand.
export interface PesticideRegistryBrandCatalogEntry {
	id: number;
	product_id: string;
	name: string;
	category: string;
	retired: string | null;
}

// PesticideRegistryBrandProduct is one district product that uses the brand.
export interface PesticideRegistryBrandProduct {
	id: number;
	name: string;
	organization_id: number;
	organization_name: string;
	product_catalog_id: string | null;
	retired: string | null;
}

// PesticideRegistryBrandDetail is the whole chain behind one brand
// (GET /api/sudo/catalog-brands/{id}). Root only.
export interface PesticideRegistryBrandDetail {
	id: number;
	epa_reg_no: string;
	name: string;
	active: boolean;
	source: string;
	source_retrieved_at: string;
	first_seen_at: string;
	last_seen_at: string;
	registrations: PesticideRegistryBrandRegistration[];
	labels: PesticideRegistryBrandLabel[];
	catalog_entries: PesticideRegistryBrandCatalogEntry[];
	district_products: PesticideRegistryBrandProduct[];
	where_to_buy: PesticideWhereToBuy[];
	sds: PesticideRegistryBrandSDS;
}

// ---- Map Review tables (QUERY /api/<table>) -------------------------------
// The filter spec exists once, per browser session, and is resolved once,
// server side, into SQL predicates. No TypeScript here resolves a clause into
// a predicate: the tables and the map both render only what the server
// returns in `map`.

// The tables the Map Review resolver knows. Nine of them are the review
// workbench's own tabs (MAP_REVIEW_TABLES in columns.ts, the set the workbench pins);
// `pool` is the lab records page's second ledger — the lab's pools and the
// results recorded against them — which no workbench surface offers as a tab,
// so it is a table of the resolver without being a tab of that page.
//
// Each has a ledger row table and a map selection; `observation`'s layer draws
// per Feature rather than per record (see MapReviewObservationRow), `species_count`
// has no layer of its own, so its selection is empty, `trap_sample` draws the
// site it is traceable to through its instance, and `zone` and `pool` draw
// layers with no tile-side predicate, so their selection ships the kept ids
// alone. Every ledger is an ordinary page.
export type MapReviewTable =
	| "site"
	| "feature"
	| "treatment"
	| "observation"
	| "trap_station"
	| "trap_instance"
	| "species_count"
	| "trap_sample"
	| "zone"
	| "pool";

export type MapReviewFilterOp =
	| "contains"
	| "enum"
	| "numberRange"
	| "dateRange"
	| "relation";

export interface MapReviewColumnFilter {
	table: MapReviewTable;
	column: string; // key from MAP_REVIEW_COLUMNS
	op: MapReviewFilterOp;
	text?: string | null; // op "contains"
	values?: (string | number)[] | null; // op "enum"
	min?: number | null; // op "numberRange"
	max?: number | null;
	from?: string | null; // op "dateRange", "YYYY-MM-DD"
	to?: string | null;
	// op "dateRange": the viewer's zone offset in minutes east of UTC (-420 for
	// California in PDT), so `from`/`to` name the viewer's days rather than UTC
	// days. Absent means UTC, the behaviour before this existed. Not cosmetic: a
	// quarter of the treatments and observations fall on a different local day
	// than UTC day, so a UTC boundary drops the afternoon and evening the
	// operator asked for.
	tz_offset_minutes?: number | null;
	// op "relation": narrows `table` to exactly the records related to one
	// record of `source`. Emitted by the grid's relation-link cells, resolved
	// server-side like every other clause.
	source?: MapReviewTable | null;
	source_id?: number | null;
}

/** The sex vocabulary's three facets: the Mosquito counts table's Sex column's
 * members, so "female" means every female state the source distinguishes. */
export type MapReviewSexFacet = "female" | "male" | "unknown";

/** The length units a buffer distance may be stated in. The server parses
 * exactly these four short labels, so this union is the wire's vocabulary
 * rather than a display convenience: the buffer form offers them and the
 * request carries the one the operator picked. */
export type MapReviewLengthUnit = "m" | "km" | "ft" | "mi";

/** One temporary zone: a buffer materialised server-side from the
 * currently-filtered rows of a surface at a stated distance — see
 * doc/design/mvp/zones.md, Part Two. It is session-scoped — no id, no row, no
 * version — and lives only as long as the operator keeps it in the filter
 * summary; saving one as a standing zone is the bridge to Part One. `name` is
 * the server's label for what was buffered ("Within 1 km of 12 trap
 * instances"), which the buffer form shows and the operator may override before
 * saving. The geometry is the materialised MultiPolygon in EPSG:4326, held
 * inline because the client already has it: the map draws it from a GeoJSON
 * source with no tiles and no selection round trip. */
export interface MapReviewTempZone {
	name: string;
	geometry: GeoJSON.MultiPolygon;
	/** Which side of the buffer narrows: absent/true keeps the rows the buffer
	 * CONTAINS, false keeps the rows it does NOT contain. */
	inside?: boolean;
}

/** How several temporary buffers compose. Standing zones always compose by
 * union; this choice applies to the buffers alone. `union` (absent) keeps a row
 * any inside buffer contains; `intersection` keeps only the rows every inside
 * buffer contains. An outside buffer always excludes the rows it contains. */
export type MapReviewBufferCombine = "union" | "intersection";

export interface MapReviewSpec {
	/** The selected standing zones' ids: a geographic narrowing of every
	 * spatial table, never propagated through the relation graph. Absent or
	 * empty means no zone narrowing, and a record is kept when it falls inside
	 * ANY selected zone, so overlapping zones compose by union — always, whatever
	 * the buffers' mode. A retired zone — and an id naming no zone of the org —
	 * keeps no row rather than failing the request. */
	zones: number[];
	/** The session's temporary zones, drawn on the map and narrowing every
	 * spatial table by containment. Each carries its own side (`inside`) and the
	 * several inside buffers compose by `buffer_combine`. Unlike a standing zone
	 * a temporary one names no ledger row and no stored version: its geometry
	 * travels inline, and it drops out of the narrowing the moment it is cleared. */
	temp_zones: MapReviewTempZone[];
	/** How the inside temporary buffers compose: absent/`union`, or
	 * `intersection`. */
	buffer_combine?: MapReviewBufferCombine;
	search: Record<MapReviewTable, string>; // cross-column search box per table
	filters: MapReviewColumnFilter[];
	// An explicit Feature selection: the re-treatment bucket's membership, or
	// any other click that names Features rather than a column. It narrows the
	// Feature ledger by identity and propagates to the related records exactly
	// as a Feature clause does. Absent or empty states no narrowing.
	feature_ids?: number[];
}

/** The checkbox selection a bulk write is narrowed to, beneath the spec.
 *
 * The spec still says which rows the ledger is showing; this says which of them
 * the operator ticked. Exactly one field is stated: `ids` is an explicit set
 * (the rows ticked when only some are), `exclude_ids` is every row the spec
 * keeps except these (the rows unticked after "select all matching"). Absent
 * means the whole spec's keep set. Ids outside it narrow nothing — the
 * selection never widens what the filter selected. */
export interface MapReviewBulkSelection {
	ids?: number[];
	exclude_ids?: number[];
}

export interface MapReviewSort {
	column: string;
	desc: boolean;
}

export interface MapReviewRowBase {
	id: number;
	latitude: number | null;
	longitude: number | null;
}

export interface MapReviewSiteRow extends MapReviewRowBase {
	label: string;
	kind: string;
	address: string;
	owner_name: string;
	owner_kind: string;
	has_features: boolean;
	/** True when the Site holds at least one live (non-retired) Trap Station.
	 * Filterable and sortable as a Yes/No enum; the Site-scale answer to "which
	 * sites have traps", so a trap clause never narrows the Sites ledger. */
	has_traps: boolean;
	notes: string;
	tags: string;
	/** The ids of the tags this Site carries, for the ledger's chips: the chip
	 * is drawn from `tag_ids` plus the symbology catalog's tag list, while
	 * `tags` keeps the comma-joined text it has always been, so a ledger whose
	 * catalog has not loaded still says what a Site is tagged with. */
	tag_ids: number[];
	features: number;
	created: string;
	/** The site's planning assignment: the assigned technician's display name
	 * ("" while the site has none) and the bare calendar day the assignment is
	 * for (null while it has none). Display and filter only — both arrive
	 * through a join, so neither can drive the page's ORDER BY — and a site
	 * shows at most one, because the ledger reads only the latest assignment;
	 * an assign supersedes and a clear cancels, neither one deleting the plan
	 * from the assignment history. */
	assigned_to: string;
	assigned_on: string | null;
}

export interface MapReviewFeatureRow extends MapReviewRowBase {
	site: string;
	type: string;
	description: string;
	created: string;
	treatments: number;
	// calculated_revisit_at is the computed cumulative window end — the latest
	// treatment.residual_expires over the Feature — null when nothing
	// contributes. planned_revisit_at is the manual pin, present ONLY while
	// active (feature.revisit_at set and not superseded by a treatment dated at
	// or after it). Both are server-computed at read time; their difference is
	// the variance the Feature table shows.
	calculated_revisit_at: string | null;
	planned_revisit_at: string | null;
	// control_state is the server's derived control verdict for the Feature,
	// from the same effective-window rule the map bands by (see ControlState); a
	// Feature the ledger filters by control state shows the value it filtered on.
	control_state: ControlState;
	// active is the district's standing decision to keep servicing the Feature
	// (see Feature); an independent dimension from control_state.
	active: boolean;
}

export interface MapReviewTreatmentRow extends MapReviewRowBase {
	start_datetime: string;
	/** The instant the application ended, null while it is still open. The review
	 * list's Duration column is this subtracted from the start, so the generic
	 * page carries it exactly as the grid's row does — the ledger and the generic
	 * page are one builder (map_review.TreatmentPayload). */
	end_datetime: string | null;
	product: string;
	quantity: number | null;
	quantity_unit: string;
	feature: string;
	site: string;
	method: string;
	area_treated: number | null;
	/** The treated extent in the district's resolved area unit, and the unit's
	 * display name (e.g. "acre" — the district's own setting); null/empty when
	 * no area was recorded. area_treated stays canonical square metres. */
	area_treated_preferred: number | null;
	area_treated_unit: string;
	creator: string;
	/** The employee behind the treatment, carried beside the joined name, so
	 * the ledger's Applicator cell links to that employee's page (id quoted
	 * there); null when the treatment records no creator. */
	creator_id: number | null;
	/** The district product the row applies, or null for a physical-control row
	 * that applied none; the district product detail page addresses it. */
	product_id: number | null;
	/** The district intervention the row records instead of a product (a
	 * physical-control treatment), or null when a product was applied. */
	intervention_id: number | null;
	/** The intervention's name, empty when the row carries none. */
	intervention: string;
	/** The Feature and Site the treatment was applied to; the grid's relation
	 * links and the map popup address them. */
	feature_id: number;
	site_id: number;
	site_address: string;
	/** The product's label application scenarios, in the district's preferred
	 * units. Each keeps the label's own basis; no applied rate is derived. */
	prescribed_rates: MapReviewPrescribedRate[];
	/** The treatment's own applied rate, or null when none of the label's
	 * bases is computable from what the treatment states. */
	applied_rate: MapReviewAppliedRate | null;
	/** The applied rate judged against the label's envelope. */
	rate_verdict: MapReviewRateVerdict;
	/** The VectorSurv application id the treatment was filed under, null when
	 * it has not been sent. Display-only: the server projects it, and there is
	 * no confirmed per-application Gateway URL to link to. */
	vectorsurv_application_id: number | null;
}

/** One label application scenario's prescribed rate, rendered in the district's
 * preferred units. Unit/basis are the label's own statement when the package
 * could not convert them or they already read that way. */
export interface MapReviewPrescribedRate {
	scenario: string;
	low: number | null;
	high: number | null;
	unit: string;
	basis: string;
	converted: boolean;
}

export type MapReviewRateVerdict =
	| "over"
	| "under"
	| "in_range"
	| "unknown"
	| "not_applicable";

/** A treatment's own applied rate, in the product label's own rate unit and
 * basis, as the district reads it. For a proportional basis — a mass or volume
 * per area, or a dose per basin or per water-volume band — `value` is the
 * quantity over the label's denominator. For a coverage basis (a discrete count
 * per area, where the label states the area one unit treats) `value` is the count
 * applied in `unit` as recorded, `coverage` is true and `basis` is the label's
 * own "per N" statement. `value` is null when no label basis is computable for
 * this treatment (a gap, never a zero). */
export interface MapReviewAppliedRate {
	value: number | null;
	unit: string;
	basis: string;
	converted: boolean;
	/** The label states coverage (one unit treats up to N), so the number is a
	 * count and the label sets no maximum. */
	coverage: boolean;
}

/** One inspection reading. `found` and `none` are the two answers the
 * production check gives; `no_inspection` is the visit that produced no check
 * at all (a dry Feature, a routine visit). The last two are different facts and
 * are never collapsed — the whole point of showing the inspection. */
export type MapReviewInspectionReading = "found" | "none" | "no_inspection";

export interface MapReviewObservationRow extends MapReviewRowBase {
	/** When the technician recorded what they saw. The row's identifier and its
	 * default sort. */
	observed_at: string;
	/** The record's production reading, derived server-side from its live
	 * Inspections. */
	inspection: MapReviewInspectionReading;
	/** The reading Inspection's own density and stage; empty when there is no
	 * Inspection to read them from. */
	density: string;
	stage: string;
	/** The Feature the observation was recorded at (its habitat type) and that
	 * Feature's Site label — the two relation links. */
	feature: string;
	feature_id: number;
	site: string;
	site_id: number;
	/** The applicator: the org member id the filter narrows by, and the joined
	 * display name the cell shows. */
	creator_id: number | null;
	creator: string;
	/** The source's own record of the conditions seen (FieldSeeker `sitecond`,
	 * pipe-delimited and multi-valued; MapVision `tComment`), and the source's
	 * zone where it states one. The Condition block reads the first; the second
	 * is the person line's fallback when no technician was resolved — never
	 * shown beside a named person. */
	condition: string | null;
	zone: string | null;
	/** The Feature's properties AS OF this observation, resolved server-side from
	 * the versioned tables (feature_classification, feature_production_state) at
	 * this record's observed_at — never from a _current view. Each is the row of
	 * its dimension with the greatest observed_at (ties by greatest id) not later
	 * than the observation. An empty string means no assertion of that dimension
	 * existed at or before the observation; Volume/Area are null where the size
	 * dimension did not record that measure. VegetationInfluence has no rows in
	 * the clone yet — the column and filter ship ahead of capture. */
	production_state: string;
	water_origin: string;
	flow_state: string;
	organic_content: string;
	vegetation_influence: string;
	volume: number | null;
	area: number | null;
	/** The Observation's own note (the source's comment), empty when none. */
	notes: string;
	/** The treatments recorded under this observation (treatment.observation_id)
	 * — the link the observation popup walks onward to. Empty when none is
	 * linked. */
	treatments: MapReviewObservationTreatment[];
}

/** One treatment recorded under an observation, reduced to the facts the
 * observation popup's link list shows. */
export interface MapReviewObservationTreatment {
	id: number;
	start_datetime: string;
	product: string;
}

/** One Traps row: a Trap Station (§23) — the district's standing trapping
 * network. Deliberately thin: the station carries no trap type, lure or
 * datetimes (those are the instance's). */
export interface MapReviewTrapStationRow extends MapReviewRowBase {
	site: string;
	site_id: number;
	active: boolean;
	/** The station's trapping series, a display-only correlated count whose
	 * cell links to the counts surface narrowed to this station. */
	instances: number;
}

/** The counts cell's reading of a catch. `state` is which of the three facts it
 * reports: `not_collected` (the instance is Set, no sample exists yet),
 * `not_analyzed` (the sample exists but its composition is not known) or
 * `analyzed` (total/female are the catch's own sums; an Analyzed sample with no
 * counts reads 0/0). `matrix` is the full species × sex composition the rail
 * detail renders. */
export interface MapReviewTrapCounts {
	state: "not_collected" | "not_analyzed" | "analyzed";
	total: number;
	female: number;
	matrix: MapReviewTrapSpeciesCount[];
}

/** One species × sex count in the counts matrix. */
export interface MapReviewTrapSpeciesCount {
	species_id: number;
	species: string;
	sex: string;
	facet: MapReviewSexFacet;
	count: number;
}

/** One Trap Instances row: a Trap Instance (§24) and what it produced. */
export interface MapReviewTrapInstanceRow extends MapReviewRowBase {
	/** The instance's set datetime — the Date column, which shows it with the
	 * collect datetime and the interval inline. */
	date: string;
	/** Empty while the trap is still out (§24's Set state). */
	collected_at: string | null;
	/** The derived sampling effort in whole days; null while the instance is
	 * Set. Read-only, shown inline in the Date cell. */
	interval: number | null;
	/** The associated station — empty for an off-station instance, which is
	 * normal; station_id is the relation link's source, null when off-station. */
	station: string;
	station_id: number | null;
	site: string;
	site_id: number;
	trap_type: string;
	lure: string;
	counts: MapReviewTrapCounts;
	/** The instance's sample (§25), null while the instance is Set and no sample
	 * exists yet. It is what the detail page reads the sample's label and
	 * analysis state through — the row itself carries only what the sample
	 * produced, so the specimen's own facts need this id. */
	sample_id: number | null;
	/** The row's three-valued reading of the sample's tests. */
	positivity: "positive" | "negative" | "not_tested";
	/** The sample's pool count, display-only. */
	pools: number;
	/** The instance's state, derived from the collect datetime. */
	state: "set" | "collected";
}

/** The body of a Trap Instance create (`POST /api/trap-instance`): the facts
 * the operator states when a trap is set. Only `site_id` and `set_at` are
 * required. `station_id`, `trap_type_id` and `lures` are optional — an
 * off-station instance with no type or lure stated is an ordinary instance.
 * `lures` is the set of lure ids carried (zero or more, §24 Rule 4); an empty
 * array means none stated. `latitude`/`longitude` are stated together or not at
 * all, and a set form that gave no coordinates leaves both null (the instance
 * has no location). `collected_at` is null while the trap is still out. The
 * response is the created `MapReviewTrapInstanceRow`, the same row a
 * `GET /api/trap-instance` item carries. */
export interface TrapInstanceCreate {
	site_id: number | null;
	station_id: number | null;
	trap_type_id: number | null;
	lures: number[];
	latitude: number | null;
	longitude: number | null;
	set_at: string;
	collected_at: string | null;
}

/** The parcel a Trap Station's Site is linked to (`site_parcel`), as the
 * station detail reports it. Keyed on the county identity — `region` +
 * `external_id` (APN) — never on the county's own parcel row id, which is
 * re-issued on every sync (plan §3.3). `owner` is the owner of record, null
 * when the situs does not state one. */
export interface TrapStationParcel {
	region: string;
	external_id: string;
	property_type: string;
	owner: string | null;
}

/** The active location version of a Trap Station (`trap_station_location`):
 * the point the station stands at and when that version was recorded. */
export interface TrapStationPoint {
	latitude: number;
	longitude: number;
	recorded_at: string;
}

/** GET /api/trap-station/{id} — one Trap Station (§23) with everything its
 * review page states: when and by whom it was created, the Site (and the parcel
 * behind it) it belongs to, where it stands, its derived permission status, and
 * the trap instances set at it. Org-scoped through the Site: another district's
 * station answers 404.
 *
 * `created_by`/`created_by_name` are null/"" for a station imported from an
 * external system, which is ordinary rather than a gap. `parcel` is null when
 * the Site is not parcel-linked and `location` is null when no location version
 * has been recorded. */
export interface TrapStationDetail {
	id: number;
	active: boolean;
	created: string;
	created_by: number | null;
	created_by_name: string;
	site_id: number;
	site_address: string;
	site_kind: string;
	parcel: TrapStationParcel | null;
	location: TrapStationPoint | null;
	permission: "permitted" | "awaiting_permission";
	instances: MapReviewTrapInstanceRow[];
}

/** One Mosquito Counts row: a single Species Count (§26), normalised — a
 * species, a sex and a count. Its Trap Instance, Station and Site are reached
 * through the sample. */
export interface MapReviewSpeciesCountRow extends MapReviewRowBase {
	/** The instance's set datetime, reached through the sample: the row's own
	 * ordering and its date filter. */
	set_at: string;
	station: string;
	station_id: number | null;
	site: string;
	site_id: number;
	/** The species vocabulary row's display name, and the id the filter narrows
	 * by (the column's options are the district's species, by id). */
	species: string;
	species_id: number;
	/** The sex vocabulary row's display name, and the facet the filter matches. */
	sex: string;
	sex_facet: MapReviewSexFacet;
	count: number;
}

/** One Trap Samples row: a Trap Sample (§25) as the worklist reads it — the
 * district's handwriting on the container, the instance parameters it is
 * traceable to through its instance, and the state of its composition. There is
 * no "received at lab" state and no reconciled flag: the list IS the worklist,
 * read as `state = not_analyzed` and nothing else.
 *
 * `label` is the row's identity column — the district's own handwriting, and
 * the only string this bench shows, reads or searches on a sample. `counts` is
 * how many rows the sample's live composition holds, and `holding` says whether
 * any of it was set aside into a holding container: the `?` marker, never a
 * count. */
export interface MapReviewTrapSampleRow extends MapReviewRowBase {
	label: string | null;
	/** The instance's set datetime, reached through the sample. */
	set_at: string;
	collected_at: string | null;
	station: string;
	station_id: number | null;
	site: string;
	site_id: number;
	trap_type: string | null;
	/** The sample's analysis state (§25 Rule 3). */
	state: "not_analyzed" | "analyzed";
	/** The number of rows in the sample's live composition. */
	counts: number;
	/** Whether the sample has holding containers. */
	holding: boolean;
}

/** One Pools row: a Pool (§26) of a Trap Sample's catch — the lab's pooled
 * composition read beside the Counts ledger it was recorded in. One row per pool
 * × recorded test, so a pool carrying several results reads once per result and
 * a pool with no test reads once with every test column empty; the row's
 * identity is the pool's own id, because the district labels a pool after
 * recording it and the label is therefore nullable.
 *
 * Its species, sex and size are the pool's own. Its target, method, status and
 * test date come through the test, its results-complete mark is the pool's, and
 * the set date, station and site are the Trap Instance's, reached through the
 * sample. `latitude`/`longitude` are that instance's captured point, so the
 * ledger can centre the map on a row. */
export interface MapReviewPoolRow extends MapReviewRowBase {
	label: string | null;
	season_year: number;
	/** The species vocabulary row's display name, and the id the filter narrows
	 * by. */
	species: string;
	species_id: number;
	/** The sex vocabulary row's display name, and the facet the filter matches —
	 * the facet rather than the name, so a filter reaches every state of that
	 * sex the source distinguishes. */
	sex: string;
	sex_facet: MapReviewSexFacet;
	size: number;
	/** The pool's three-valued reading of its own tests (§28 System Rule 1): at
	 * least one test positive, tested and none positive, or no test at all —
	 * which is not the same as testing negative. */
	positivity: "positive" | "negative" | "not_tested";
	/** The test's target vocabulary row's display name and the id the filter
	 * narrows by; null on a pool that carries no test. */
	target: string | null;
	target_id: number | null;
	/** The test's method vocabulary row's display name and the id the filter
	 * narrows by; null on a pool that carries no test. */
	method: string | null;
	method_id: number | null;
	/** The test's status vocabulary row's display name, the facet the filter
	 * matches, and the day the laboratory ran the test; each null on a pool that
	 * carries no test. */
	status: string | null;
	status_facet: string | null;
	test_date: string | null;
	/** When the pool's results were complete, null while they are outstanding. */
	results_complete_at: string | null;
	/** The instance's set datetime, reached through the sample: the row's
	 * ordering and its date filter. */
	set_at: string;
	/** The station the instance was set at ("Station 12", "" when it was set off
	 * station), with its id for the relation link. */
	station: string;
	station_id: number | null;
	site: string;
	site_id: number;
	/** The sample the pool was counted in, and the district's handwriting on it
	 * (null while the container is unlabelled). */
	sample_id: number;
	sample_label: string | null;
	/** The Trap Instance the pool's sample came from. */
	instance_id: number;
}

/** One Zones row: a district's own named unit (doc/design/mvp/zones.md) read
 * from its active version — that version's name, comment and geometry. `records`
 * is a display-only correlated count of the Features the active version
 * contains, and its cell links to the Feature ledger narrowed to this zone.
 * `active` is the unit's life: a zone is retired, never deleted. */
export interface MapReviewZoneRow extends MapReviewRowBase {
	name: string;
	comment: string;
	records: number;
	created: string;
	active: boolean;
}

/** One zone in the map control pane's list (GET /api/zone): the unit's
 *  id, its active version's name, and its life. The list names the live units
 *  so the filter control can offer one; the map's polygon layer is the `zone`
 *  tile map, and the layer's MVT feature id is this id. */
export interface MapReviewZoneListRow {
	id: number;
	name: string;
	active: boolean;
}

/** What a zone import created and what it skipped: one zone per
 *  polygon-bearing feature; point- and line-only features are skipped. */
export interface MapReviewZoneImportResult {
	created: number;
	skipped: number;
}

/** POST /api/zone/buffer — materialise a temporary zone from the
 * currently-filtered rows of a surface at a stated distance. The spec is the
 * whole selection, so the rows buffered are exactly the ones the grid, the
 * counts and the map are showing; `source_table` must be a surface with a
 * geometry of its own, so the `zone` surface — which has none — is refused. */
export interface MapReviewBufferRequest {
	source_table: MapReviewTable;
	spec: MapReviewSpec;
	distance: number;
	unit: MapReviewLengthUnit;
}

/** The materialised buffer: the union of the kept rows' geometries buffered at
 * the ground distance, as a MultiPolygon in EPSG:4326; that distance in metres
 * as the server resolved it, so the form can echo the conversion; and the
 * server's label for what was buffered, which the operator may override before
 * saving the buffer as a standing zone. */
export interface MapReviewBufferResponse {
	geometry: GeoJSON.MultiPolygon;
	meters: number;
	name: string;
}

/** POST /api/site-assignment — bulk-assign the currently-filtered
 * Sites to one technician for one day. The spec IS the selection, exactly as
 * the bulk revisit write's is, so the write lands on the rows the grid, the
 * counts and the map are showing and nowhere else. Each kept Site ends up with
 * a new assignment: any prior plan for it is superseded — completed or
 * cancelled, but kept in the history — so the ledger thereafter shows only the
 * one just written.
 * `assigned_on` is a bare calendar day ("YYYY-MM-DD") with no time of day —
 * the day the technician is meant to visit — and `user_id` must name a member
 * of the caller's organisation.
 * `kind` (set_trap, collect_trap, inspect, abate, treat, custom) is mandatory:
 * it says what work every created Assignment directs, and it decides which
 * completion evidence the server will demand, so the client always sends one. */
export interface MapReviewSiteAssignmentRequest {
	spec: MapReviewSpec;
	selection?: MapReviewBulkSelection;
	user_id: number;
	assigned_on: string;
	kind: string;
}

/** POST /api/site-assignment/clear — cancel the current assignment of the
 * currently-filtered Sites, so the ledger then shows none for them; the
 * cancelled plan stays in the assignment history. Symmetric with the assign:
 * the same spec, the same selection, the opposite write. */
export interface MapReviewSiteAssignmentClearRequest {
	spec: MapReviewSpec;
	selection?: MapReviewBulkSelection;
}

/** PUT /api/feature/revisit — set (an RFC3339 instant) or clear (null) the
 * planned revisit date on the selected Features, where the selection is the
 * spec narrowed by the ticked rows. */
export interface MapReviewPlannedRevisitRequest {
	spec: MapReviewSpec;
	selection?: MapReviewBulkSelection;
	revisit_at: string | null;
}

/** How many Sites a bulk site-assignment write changed, so the caller can say
 * what it did rather than assume it worked. */
export interface MapReviewSiteAssignmentResult {
	sites: number;
}

export type MapReviewRow =
	| MapReviewSiteRow
	| MapReviewFeatureRow
	| MapReviewTreatmentRow
	| MapReviewObservationRow
	| MapReviewTrapStationRow
	| MapReviewTrapInstanceRow
	| MapReviewSpeciesCountRow
	| MapReviewTrapSampleRow
	| MapReviewZoneRow
	| MapReviewPoolRow;

// One table's map highlight selection, already resolved server side.
export interface MapReviewSelection {
	ids: number[] | null; // non-null: render membership of these ids (exact)
	/** Opaque token addressing a server-side bitmap of the ids a selection too
	 * large to ship as membership resolves to. When non-null the tile layers
	 * (treatment, observation and feature) are already the filtered set — the
	 * client puts the token in the tile URL and the layer carries no filter.
	 * Null when the set ships as `ids` instead. */
	selection_token: string | null;
	exact: boolean; // true: the rendering below reproduces the resolved set
	truncated: boolean; // ids omitted because the set exceeded the cap
	product_ids: number[] | null; // treatment tiles only
	start_min: number | null; // epoch ms, treatment tiles only
	start_max: number | null;
}

export interface MapReviewCounts {
	site: number;
	feature: number;
	treatment: number;
	observation: number;
	trap_station: number;
	trap_instance: number;
	species_count: number;
	trap_sample: number;
	zone: number;
	pool: number;
}

/** One live trap vocabulary row: its id, its name, and the facet where the
 * vocabulary carries one (the `sex` vocabulary's three facets). */
export interface MapReviewVocabularyEntry {
	id: number;
	name: string;
	facet: MapReviewSexFacet | null;
	/** Present on the trap_type vocabulary only: the catalog entry this district
	 * row is an instance of, when it is linked. */
	catalog_trap_type_id?: string | null;
}

/** The district's live trap vocabularies, for the grid's vocabulary columns and
 * the counts clause's species and sex controls — and, beside the trap's own,
 * the lab's three test vocabularies, which the Pools ledger's columns read: the
 * targets a pool is tested for, the methods it is tested by, and the result
 * states a test can be in. Each answers the same entry shape, so a column reads
 * its options without knowing which vocabulary it names. */
export interface MapReviewTrapVocabulary {
	species: MapReviewVocabularyEntry[];
	sex: MapReviewVocabularyEntry[];
	trap_type: MapReviewVocabularyEntry[];
	lure: MapReviewVocabularyEntry[];
	test_method: MapReviewVocabularyEntry[];
	test_target: MapReviewVocabularyEntry[];
	test_status: MapReviewVocabularyEntry[];
	/** The district's live products, which the Nidus Notes product correction
	 * picker is limited to. A custom mix has no catalog product id. */
	products: MapReviewTrapVocabularyProduct[];
}

/** One live district product on the trap-vocabulary pushdown. */
export interface MapReviewTrapVocabularyProduct {
	id: number;
	name: string;
	/** The shared catalog slug, absent for a district custom mix. */
	catalog_product_id?: string;
	/** The shared catalog entry's display name; the recognition vocabulary's
	 * product word. */
	catalog_name?: string;
}

// ---------------------------------------------------------------------------
// Trap administration (trap-ontology-plan): the shared trap-type catalog, the
// district's reusable configurations, its physical devices and its sample labels.

/** One shared catalog entry with the acting district's enablement folded in
 * (`GET /api/trap-type-catalog`). */
export interface TrapTypeCatalogEntry {
	id: number;
	trap_type_id: string;
	name: string;
	acronym: string | null;
	manufacturer: string | null;
	model: string | null;
	product_code: string | null;
	unit_of_measure: string | null;
	expected_service_interval_days: number | null;
	target_species: string | null;
	provenance: string | null;
	retired: string | null;
	/** Whether units of this product carry a manufacturer serial the district
	 * reads off the device. The register form asks for serials when true and
	 * generates them when false (migration 00521). */
	requires_manufacturer_serial: boolean;
	enabled: boolean;
}

export interface TrapTypeCatalogResponse {
	entries: TrapTypeCatalogEntry[];
}

/** The body of the enablement write: whether the district fields one catalog
 * type (`POST /api/trap-type-enablement`). */
export interface TrapTypeEnablementWrite {
	catalog_trap_type_id: string;
	enabled: boolean;
}

/** The enablement write's answer. */
export interface TrapTypeEnablementView {
	catalog_trap_type_id: string;
	enabled: boolean;
}

/** The body of a trap configuration create or supersede. */
export interface TrapConfigurationWrite {
	name: string;
	trap_type_id: number;
	lures: number[];
	setup_instructions?: string | null;
}

/** A configuration family with its current version, as a write answers or the
 * list reads it. */
export interface TrapConfigurationResult {
	id: number;
	name: string;
	version: number;
	version_id: number;
	trap_type_id: number;
	lures: number[];
	setup_instructions?: string | null;
	retired: boolean;
}

/** One device in a register batch. */
export interface TrapDeviceRegister {
	serial: string;
	catalog_trap_type_id: string;
}

export interface TrapDeviceRegisterRequest {
	devices: TrapDeviceRegister[];
}

/** One registered device as the office reads it, with its open (still-set)
 * instance folded in. */
export interface TrapDeviceView {
	id: number;
	serial: string;
	catalog_trap_type_id: string;
	state: string;
	public_token: string;
	open_instance_id: number | null;
	open_instance_set_at: string | null;
}

export interface TrapDeviceStateChangeRequest {
	state: string;
	reason?: string | null;
}

/** A label's open (unresolved) deployment, if any: the binding, the instance it
 * is inside, when it was opened, and whether that instance has been collected. */
export interface TrapLabelOpenBinding {
	binding_id: number;
	instance_id: number;
	opened_at: string;
	instance_collected: boolean;
}

/** One sample label as the office reads it, with its open binding and its last
 * resolution folded in. */
export interface TrapLabelView {
	id: number;
	label: string;
	public_token: string;
	state: string;
	open_binding: TrapLabelOpenBinding | null;
	last_resolution: string | null;
}

export interface TrapLabelMintRequest {
	count: number;
	batch?: string | null;
}

/** A label bound to a trap instance. */
export interface TrapLabelBindingView {
	id: number;
	trap_label_id: number;
	trap_instance_id: number;
}

export interface TrapLabelBindingOpenRequest {
	label: string;
	instance_id: number;
}

export interface TrapLabelBindingReleaseRequest {
	resolution: string;
	reason: string;
}

// ---------------------------------------------------------------------------
// Nidus Lab (doc/in-progress/nidus-notes/lab-sample-processing.md): identifying a
// sample, submitting a counting run, recording the pools' test results, and the
// shared species catalog the district's local set is adopted from. Shapes must
// match the platform-layer payloads behind `GET /api/trap-sample/summary`,
// `GET /api/trap-sample/resolve`, `GET /api/trap-sample/{id}`,
// `POST /api/count-session`, `POST /api/pool-label`, `GET /api/species-catalog`,
// `POST /api/species`, and the pool-results surface's `GET /api/pool/open`,
// `POST /api/pool/{id}/test` and `POST /api/pool/{id}/complete`.
// ---------------------------------------------------------------------------

/** `GET /api/trap-sample/summary` — the hub's two worklist counts, both scoped
 * to the acting district: the samples still waiting to be counted (the
 * worklist, §8.1) and the live session's pools still on the bench — the pools
 * whose results are not yet marked complete (§7.1), so a pool with some of its
 * targets recorded is still counted.
 * Read as one object because the hub shows them side by side and a second round
 * trip would only let the two halves disagree. */
export interface LabWorklistSummary {
	unanalyzed_traps: number;
	open_pools: number;
}

/** One resolution candidate: a collected sample the typed or scanned label
 * matched. A label may repeat, so it returns every candidate and the operator
 * chooses; the label is the only way in. */
export interface TrapSampleCandidate {
	id: number;
	label: string | null;
	station: string;
	site: string;
	collected_at: string | null;
	state: "not_analyzed" | "analyzed";
}

/** `GET /api/trap-sample/resolve` — what one label resolved to. `exact` is true
 * when the label is one that exactly one sample carries: the workbench may then
 * load that sample without asking. An empty candidate list means nothing
 * matched, and nothing was created. */
export interface TrapSampleResolveResponse {
	code: string;
	exact: boolean;
	candidates: TrapSampleCandidate[];
}

/** One stored species × sex count: a row of the expansion the submit wrote —
 * a state row, or the plain remainder row — never one the operator typed. */
export interface LabSpeciesCountView {
	id: number;
	species_id: number;
	species: string;
	sex: MapReviewSexFacet;
	sex_facet: MapReviewSexFacet;
	count: number;
}

/** One pool drawn from a sample, with its seasonal label — the number the
 * server allocated when the pool was entered, unique within the district and
 * season and used again in a later season (the pool's identity is its id). */
export interface LabPoolView {
	id: number;
	species_id: number;
	species: string;
	sex: MapReviewSexFacet;
	sex_facet: MapReviewSexFacet;
	size: number;
	label: string;
}

/** One holding container: the sample's unidentified specimens, which carry no
 * label, no species and no count and are invisible to every count read. The
 * container is identified by being a row attached to its sample, and by the
 * note the later evaluation records — never a species count. */
export interface LabHoldingContainerView {
	id: number;
	status: "pending" | "reviewed";
	note: string | null;
}

/** One submitted counting run. `live` marks the session the sample's rows
 * currently read from; the rest are retained history. */
export interface LabCountSessionView {
	id: number;
	submitted_at: string;
	submitted_by: number;
	submitted_by_name: string;
	supersedes_id: number | null;
	live: boolean;
}

/** The sample a counting run belongs to, as the workbench's subject panel reads
 * it. Every instance parameter is read through the instance, never copied. */
export interface LabSampleSubject {
	id: number;
	label: string | null;
	analysis_state: "not_analyzed" | "analyzed";
	count_session_id: number | null;
	station: string;
	station_id: number | null;
	site: string;
	site_id: number;
	set_at: string;
	collected_at: string | null;
	trap_type: string | null;
	lure: string | null;
}

/** `GET /api/trap-sample/{id}` — one sample with everything the workbench reads
 * about it: its live composition, its pools, its holding containers, and its
 * session history newest first. */
export interface LabTrapSampleDetail {
	sample: LabSampleSubject;
	composition: LabSpeciesCountView[];
	pools: LabPoolView[];
	holding_containers: LabHoldingContainerView[];
	sessions: LabCountSessionView[];
}

/** One entered line of the operator's composition model: a species, the
 * district's `sex` row and the count typed against that pair. The grain is flat
 * — one line is one `species_count` row — so there is no total to expand and no
 * breakdown inside it. Two lines for the same species × sex are the same row. */
export interface LabCompositionEntry {
	speciesId: number;
	sex: MapReviewSexFacet;
	count: number;
}

/** `POST /api/pool-label` — the entry-time reservation: the season the labels
 * were issued in and the labels themselves, in order. The label is a seasonal
 * container reference, not an identity, so the district's counter resets each
 * season and the number is used again in a later one. The operator never types
 * or derives it: the server hands it out here, and the submission sends it back
 * unchanged. */
export interface LabPoolLabelAllocation {
	seasonYear: number;
	labels: string[];
}

/** `POST /api/count-session` — one counting run's whole submission. There is no
 * label: the sample's label is the FIELD side's to write, and the bench only
 * reads it and searches by it. Each pool carries the label and seasonYear the
 * entry-time reservation handed out, which the submit writes verbatim;
 * `holdingContainers` is how many containers the unidentified subset needs, and
 * each is written with no label of its own. On the supersede route
 * `trapSampleId` is omitted: the sample is the superseded session's. */
export interface LabCountSessionWrite {
	trapSampleId?: number;
	composition: LabCompositionEntry[];
	pools: {
		speciesId: number;
		sex: MapReviewSexFacet;
		size: number;
		label: string;
		seasonYear: number;
	}[];
	holdingContainers?: number;
}

/** The stored record a submit answers with: the session, the sample's new
 * state, and the exact rows the transaction wrote. */
export interface LabCountSessionResult {
	session: LabCountSessionView;
	sample: LabSampleSubject;
	composition: LabSpeciesCountView[];
	pools: LabPoolView[];
	holding_containers: LabHoldingContainerView[];
}

/** One shared catalog species, and the district's own row when it has adopted
 * it. The catalog itself is Gleipnir's to seed and curate. */
export interface SpeciesCatalogEntry {
	species_id: string;
	name: string;
	acronym: string | null;
	full_name: string | null;
	taxonomic_rank: string | null;
	retired: boolean;
	local_species_id: number | null;
}

/** `GET /api/species-catalog` — the shared catalog plus the district's species
 * rows no catalog entry matches, which is what a suggestion is for. */
export interface SpeciesCatalogResponse {
	catalog: SpeciesCatalogEntry[];
	unlinked: string[];
}

/** `POST /api/species` — adopt a catalog entry into the district's local set.
 * The Configuration toggle and the workbench's escape hatch are the same act. */
export interface SpeciesAdoptWrite {
	catalogSpeciesId: string;
}

/** `POST /api/species-catalog/suggestion` — suggest a catalog addition to
 * Gleipnir; it lands as a Communication. */
export interface SpeciesCatalogSuggestionWrite {
	name: string;
	note?: string | null;
}

/** One pool on the bench: the label written on its container, the species it was
 * drawn from, how many specimens it holds, and the results recorded against it
 * so far. `tests` is always present — `[]` when nothing is recorded — so the
 * ledger's recorded column is drawn from the row it is read in. */
export interface LabOpenPool {
	id: number;
	label: string; // "" when the pool carries no label
	species: string;
	size: number;
	tests: LabPoolTest[];
}

/** One recorded result: the target and status it names, the optional value as
 * the laboratory states it (verbatim text), the optional test date, and the
 * actor/instant that recorded it. A second result for the same target is
 * refused, so this row is the whole record for that target. */
export interface LabPoolTest {
	id: number;
	target_id: number;
	target: string;
	status_id: number;
	status: string;
	value: string | null;
	test_date: string | null;
	created_at: string;
}

/** One district target the pool may be screened for. */
export interface LabTestTarget {
	id: number;
	name: string;
}

/** One district status a result may be recorded as, with the positivity fold the
 * ledger reads ("positive" | "negative" | "inconclusive" | "pending"). */
export interface LabTestStatus {
	id: number;
	name: string;
	facet: string;
}

/** `GET /api/pool/open` — the bench's whole page in one read: every open pool
 * with the results recorded against it, and the vocabulary those results name.
 * The page is one surface — a result is recorded in the row it is read in — so
 * the targets and statuses arrive with the rows. All three arrays are always
 * present, and each pool's own `tests` is what its recorded column reads. */
export interface LabPoolLedger {
	pools: LabOpenPool[];
	targets: LabTestTarget[];
	statuses: LabTestStatus[];
}

// ---------------------------------------------------------------------------
// The ledger's always-visible footer aggregate. One query response's worth of
// per-column aggregates over the WHOLE filtered set — never a page, because the
// ledger is server-paged and a page-only sum would change under the operator as
// they page. Keyed by registry column key; a column whose summary kind is
// `none` is absent from the map rather than sent empty. Shapes must match
// `platform/map_review/spec.go`.
// ---------------------------------------------------------------------------

/** Which shape a column's footer aggregate takes. `none` never reaches the
 *  client: a column carrying it is simply missing from the summary map. */
export type MapReviewSummaryKind =
	| "sum"
	| "sum_avg"
	| "range"
	| "distinct"
	| "positivity";

/** One test target's positive pool-test count, for the positivity footer. */
export interface MapReviewSummaryTarget {
	target: string;
	count: number;
}

/** One column's aggregate over the whole filtered set, read as the header
 *  tooltip on that column. `kind` names the shape and only the fields that shape
 *  reads are non-null — a sum carries no min/max, a range no sum — and targets
 *  is always present, empty when nothing tested positive. */
export interface MapReviewColumnSummary {
	kind: MapReviewSummaryKind;
	sum: number | null;
	avg: number | null;
	min: string | null;
	max: string | null;
	distinct: number | null;
	targets: MapReviewSummaryTarget[];
}

/** The resolver's whole answer for one filter spec over one table: the ask
 * reply's resolved page (`POST /api/ask`), which returns the spec it built
 * beside the page that spec resolves. It is the retired Map Review query
 * endpoint's response shape, kept because ask still answers with it. */
export interface MapReviewQueryResponse {
	table: MapReviewTable;
	rows: MapReviewRow[];
	total: number; // requested table's row count under the spec
	counts: MapReviewCounts; // all eight tables, exact, full dataset
	map: Record<MapReviewTable, MapReviewSelection>;
	/** Treatment ids that are over label, capped at 20,000; [] when none. */
	over_rate_ids: number[];
	/** False when over_rate_ids was truncated at the cap. */
	over_rate_exact: boolean;
	/** The requested table's per-column footer aggregate over the whole
	 * filtered set, keyed by registry column key. Exactly the columns whose
	 * summary kind is not `none`; never absent (an empty object when the table
	 * has no summarised column). */
	summary: Record<string, MapReviewColumnSummary>;
}

// ---------------------------------------------------------------------------
// The generic resource QUERY layer (doc/in-progress/query.md §3, §4, §9): the
// per-resource replacement for `POST /api/query`. A `QUERY
// /api/<resource>` carries one resource's own narrowing — its clauses, the
// spatial narrowing, an identity-set instruction and a page — and answers the
// metadata + items envelope; `OPTIONS /api/<resource>` answers the capability
// document; `POST /api/summary` answers the footer under one resource's
// columns; and a minted identity set is the map's highlight, shipped as ids at
// or below the server's 20,000-id cap and as a token above it.
//
// Shapes must match `queryspec/queryspec.go`, `resource/query.go` and
// `resource/summary.go`. The Map Review types above are what the bespoke
// endpoints still answer with: `MapReviewQueryResponse` is the ask reply's resolved
// page (`POST /api/ask`), and the row types are the ledger's own wire rows.
// ---------------------------------------------------------------------------

/** The neutral property vocabulary (D3/D5), which replaced the grid's
 * `columnKind`: `number` splits into int/float, and `counts` is display-only. */
export type MapReviewQueryPropertyType =
	| "text"
	| "enum"
	| "int"
	| "float"
	| "bool"
	| "date"
	| "time"
	| "datetime"
	| "set"
	| "list"
	| "geometry";

/** The frozen operator vocabulary (D3). `contains` is substring match on text
 * and membership on a set/list; `equal`/`not-equal` are (non-)membership, one
 * member or an array meaning any-of; `before`/`after` order a temporal value;
 * `empty`/`not-empty` mean "no value"; `relation` is a declared-edge
 * traversal. */
export type MapReviewQueryOperator =
	| "contains"
	| "not-contains"
	| "equal"
	| "not-equal"
	| "lt"
	| "lte"
	| "gt"
	| "gte"
	| "before"
	| "after"
	| "empty"
	| "not-empty"
	| "relation";

/** An ordering's direction, stated rather than implied (D14). */
export type MapReviewQueryDirection = "asc" | "desc";

/** One column of a requested ordering (D13/D14): multi-column, each column
 * naming an exact property. A property the resource does not declare sortable
 * is refused 422. */
export interface MapReviewQueryBodyOrder {
	property: string;
	direction: MapReviewQueryDirection;
}

/** A relation clause's value (§8.1): keep the queried resource's rows that are
 * related to record `source_id` of `source`, along the edge the resource
 * declares. */
export interface MapReviewQueryRelationValue {
	source: string;
	/** One record of `source` the traversal starts from. Exactly one of
	 * `source_id` and `identity_set` is set. */
	source_id?: number;
	/** A set of `source`'s own ids — a minted identity-set token — the traversal
	 * starts from instead of one record: the queried table is narrowed to the
	 * rows related to ANY of them. It is how one table's filter reaches the
	 * records related to it (`relatedSetClauses`). */
	identity_set?: string;
}

/** A clause's value: a JSON scalar for a scalar operator, an array of members
 * for a membership operator, and a relation's `{source, source_id}` for the
 * `relation` operator. Absent (or null) for `empty`/`not-empty`. */
export type MapReviewQueryValue =
	| string
	| number
	| boolean
	| (string | number | boolean)[]
	| MapReviewQueryRelationValue;

/** One clause of a query's narrowing (D3), AND-combined with every other and
 * naming a property of the queried resource itself (D4). */
export interface MapReviewQueryBodyFilter {
	property: string;
	operator: MapReviewQueryOperator;
	value?: MapReviewQueryValue;
}

/** One H3 cell of the map grid's drill-down narrowing: the cell and the
 * resolution it was drawn at, because a drill can span several grid levels. */
export interface MapReviewQueryBodyH3Cell {
	cell: string;
	resolution: number;
}

/** The spatial narrowing (D16): a drawn GeoJSON shape tested against the
 * resource's own geometry, H3 cells the geometry falls in, the district's zones
 * the row must lie in, and the session's temporary buffers. The drawn shape and
 * the cells are separate narrowings, AND-combined with each other and with
 * every other clause; `zones` and the buffer members are ONE narrowing. Standing
 * zones always compose by union; an inside buffer must contain the row (any of
 * them under `polygon_combine` union, every one under intersection) and an
 * outside buffer must not. Refused 422 on a resource that declares no geometry,
 * and — for the zone members — on one that declares no zone narrowing. */
export interface MapReviewQueryBodyLocation {
	geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
	h3?: MapReviewQueryBodyH3Cell[];
	/** The selected standing zones' ids: the row is kept when the ACTIVE version
	 * of any of them contains its geometry. A retired zone, and an id naming no
	 * zone of the district, keeps no row. */
	zones?: number[];
	/** The session's inline buffers, each a MultiPolygon in EPSG:4326 — the
	 * shapes the server materialised and the client already holds. They narrow
	 * exactly as `zones` does, and the two are one set. */
	polygons?: GeoJSON.MultiPolygon[];
	/** The OUTSIDE buffers: a row must lie in NONE of these shapes, whatever
	 * `polygon_combine` says. */
	outside_polygons?: GeoJSON.MultiPolygon[];
	/** How the inside `polygons` compose: absent/`union` keeps a row any of
	 * them contains, `intersection` keeps only rows every one contains. The
	 * standing `zones` always compose by union. */
	polygon_combine?: MapReviewBufferCombine;
}

/** A query's identity-set instruction (D18/D26): `ref` narrows by an existing
 * set, `mint` asks the response to mint one over the rows this query keeps and
 * report it in `metadata.identity_set`, and `name`/`ttl_seconds` decide the
 * minted set's life. */
export interface MapReviewQueryBodyIdentitySet {
	ref?: string;
	mint?: boolean;
	name?: string;
	ttl_seconds?: number;
}

/** The root-only request to query beyond the caller's own organization (D19).
 * Refused 403 on a non-root session, and refused 422 on a resource whose
 * descriptor is `org`. */
export interface MapReviewQueryBodyScope {
	organizations?: number[];
	all?: boolean;
}

/** The body of `QUERY /api/<resource>` (plan §3.1). Every field is optional: an
 * absent field is not narrowing. It is the per-resource successor to the
 * retired Map Review query body, which carried every table's clauses in one spec. */
export interface MapReviewQueryBody {
	offset?: number;
	limit?: number;
	cursor?: string | null;
	/** The client zone's offset in minutes east of UTC, so `before`/`after` on a
	 * bare day mean the operator's day rather than a UTC one. */
	tz_offset_minutes?: number;
	order?: MapReviewQueryBodyOrder[];
	filter?: MapReviewQueryBodyFilter[];
	location?: MapReviewQueryBodyLocation;
	identity_set?: MapReviewQueryBodyIdentitySet;
	scope?: MapReviewQueryBodyScope;
}

/** A minted identity set, as the response's metadata reports it (D26): its
 * token, and — the second channel of the same set — the set's own ids while it
 * holds at most 20,000 of them, `null` above that, where only the token travels
 * and the tiles apply it. Present with `null` rather than omitted, so "no ids"
 * reads as "let the tiles narrow it" and never as "the set is empty"; an empty
 * set ships `[]` and draws nothing. */
export interface MapReviewQueryIdentitySetInfo {
	id: string;
	ids: number[] | null;
	name?: string | null;
	/** When the set stops being honoured; null for a durable (promoted) set. */
	expires: string | null;
}

/** A query response's envelope metadata (plan §3.2, D12). */
export interface MapReviewQueryMetadata {
	/** The collection's size with no narrowing applied: the resource's standing
	 * predicates and org scope alone. */
	collection_total: number;
	/** The size of the set this query narrows to — the ledger's count for the
	 * table, and the value an unfiltered reading equals the collection's. */
	filtered_total: number;
	returned: number;
	/** The page bound the query was answered with (the resource's max when the
	 * request asked for more). */
	page_size: number;
	offset: number;
	/** The continuation token for the next page, or null at the end. */
	cursor: string | null;
	identity_set?: MapReviewQueryIdentitySetInfo;
}

/** The only response shape a `QUERY` returns: metadata plus the resource's own
 * items. `items` is always an array, so an empty page is `[]` and never null. */
export interface MapReviewQueryEnvelope<Item> {
	metadata: MapReviewQueryMetadata;
	items: Item[];
}

/** One property's shape, as the capability document states it (§4.2). */
export interface MapReviewCapabilityProperty {
	name: string;
	type: MapReviewQueryPropertyType;
	/** The operators this property accepts; a clause naming another is 422. */
	operators: MapReviewQueryOperator[];
	sortable: boolean;
	/** A static enum vocabulary's members. Absent for an id-backed or runtime
	 * vocabulary, whose members are district data (D33). */
	values?: string[];
	/** Names the per-district list supplying an id-backed enum's members
	 * ("product", "intervention" or "user"). */
	id_source?: string;
	/** An enum whose members are the district's live vocabulary, resolved by the
	 * server at request time rather than declared here. */
	runtime?: boolean;
}

/** A resource's page bounds, both required at registration (D11). */
export interface MapReviewCapabilityPage {
	default: number;
	max: number;
}

/** A resource's spatial surface; absent when it declares none, which is also
 * when a `location` clause is refused (D16). */
export interface MapReviewCapabilityLocation {
	kind: string;
}

/** One declared relation edge (D9). */
export interface MapReviewCapabilityRelation {
	name: string;
	target: string;
	direction: string;
	resolve: string;
}

/** The edges a relation clause may traverse, under the reserved property name
 * the clause carries (§4.2). */
export interface MapReviewCapabilityRelations {
	property: string;
	edges: MapReviewCapabilityRelation[];
}

/** Whether the resource serves minted identity sets (D18/D26). */
export interface MapReviewCapabilityIdentitySet {
	supported: boolean;
	ttl_seconds?: number;
}

/** The capability document `OPTIONS /api/<resource>` answers with (D7, plan
 * §4.2): API shape only — no customer data, no session needed. */
export interface MapReviewCapability {
	resource: string;
	properties: MapReviewCapabilityProperty[];
	page: MapReviewCapabilityPage;
	location?: MapReviewCapabilityLocation;
	relations?: MapReviewCapabilityRelations;
	scope: string;
	identity_set: MapReviewCapabilityIdentitySet;
}

/** The capability documents a session has read, by ledger table. A resource
 * whose document has not arrived (or does not answer) is absent, and its
 * columns keep the presentation registry's own declaration. */
export type MapReviewCapabilities = Partial<
	Record<MapReviewTable, MapReviewCapability>
>;

/** The `POST /api/summary` body (D17): the resource whose columns are
 * aggregated, plus the shared query body's narrowing — the same grammar, one
 * strict decoder. A page bound, an ordering and `identity_set.mint` are refused
 * 422 rather than ignored: a footer is over the whole filtered set, and there
 * is no page for them to mean. */
export interface MapReviewSummaryRequest extends MapReviewQueryBody {
	resource: MapReviewTable;
}

/** The `POST /api/summary` answer: the resource the columns belong to, and one
 * aggregate per column whose registry entry declares a footer shape — the same
 * keys and shapes as the ledger's own `summary`. */
export interface MapReviewSummaryResponse {
	resource: string;
	columns: Record<string, MapReviewColumnSummary>;
}

/** The `POST /api/histogram` body (D17): the resource and numeric column whose
 * distribution is wanted, the domain and scale the client's track draws (in the
 * column's STORED numbers, which is what the range clause compares), plus the
 * shared query body's narrowing — the same grammar, one strict decoder. A page
 * bound, an ordering and `identity_set.mint` are refused 422 rather than
 * ignored: a histogram is over the whole filtered set, and there is no page for
 * them to mean. The bucketed column's own clause is dropped by the endpoint, so
 * the bars never collapse to the window the operator already selected. */
export interface MapReviewHistogramRequest extends MapReviewQueryBody {
	resource: MapReviewTable;
	column: string;
	bins: number;
	domain_min: number;
	domain_max: number;
	log_scale: boolean;
}

/** One bucket of a numeric column's distribution: the half-open interval
 * `[from, to)` and the rows that fall in it. */
export interface MapReviewHistogramBin {
	from: number;
	to: number;
	count: number;
}

/** The `POST /api/histogram` answer: the resource and column the bars belong
 * to, the echoed domain and scale (so the track and the bars provably share one
 * scale), one bar per bucket, and the value-total beside them — the rows the
 * narrowing keeps that have a value at all, counted separately from the bars. A
 * null-valued row is in neither the bars nor the total. */
export interface MapReviewHistogramResponse {
	resource: string;
	column: string;
	/** True when the buckets are equal width in ln(value) rather than linear. */
	log_scale: boolean;
	domain_min: number;
	domain_max: number;
	bins: MapReviewHistogramBin[];
	total: number;
}

// ---------------------------------------------------------------------------
// The Map Review reporting dashboard (doc/design/mvp/dashboard.md). ONE aggregate
// request over the same spec the ledger resolves — the client never resolves a
// clause itself, it renders what the server counted and turns a clicked bucket
// back into a spec clause. Shapes must match `platform/map_review/dashboard.go`.
// ---------------------------------------------------------------------------

/** The dashboard request: the shared filter spec plus the viewer's zone offset
 * in minutes east of UTC (-420 for California in PDT). The offset is what the
 * server buckets the month series and the usage-vs-catches alignment in, so a
 * month bucket names the viewer's month rather than a UTC one. Absent means
 * UTC. */
export interface MapReviewDashboardRequest {
	spec: MapReviewSpec;
	tz_offset_minutes?: number;
}

/** One category's tally. `key` is the raw value a drill-down clause carries —
 * exactly what the ledger's own filter accepts, so clicking the bucket narrows
 * the ledger to the rows it counted. `label` is the display text, shared with
 * the ledger's vocabulary. `area` is the preferred-unit area sum, present only
 * where a per-bucket area is meaningful. */
export interface MapReviewDashboardBucket {
	key: string;
	label: string;
	count: number;
	area?: number;
}

/** One month of a series: "YYYY-MM" in the request's zone offset. */
export interface MapReviewDashboardPoint {
	month: string;
	count: number;
	area?: number;
}

/** One month of the usage-vs-catches alignment: how much was applied and how
 * much was caught in the same month. */
export interface MapReviewDashboardMonthPair {
	month: string;
	usage: number;
	catches: number;
}

/** One unit's total applied quantity under the spec. */
export interface MapReviewDashboardUnitTotal {
	unit: string;
	amount: number;
}

export interface MapReviewDashboardResponse {
	area_unit: string; // the district's preferred area unit's display name
	area_treated?: number; // preferred-unit sum over the kept treatments
	quantity_by_unit: MapReviewDashboardUnitTotal[];
	rate_verdicts: MapReviewDashboardBucket[];
	in_range_percent?: number; // absent when no treatment carried a verdict

	treatments_by_month: MapReviewDashboardPoint[];
	treatments_by_method: MapReviewDashboardBucket[];
	treatments_by_product: MapReviewDashboardBucket[];
	treatments_by_applicator: MapReviewDashboardBucket[];

	features_by_control_state: MapReviewDashboardBucket[];
	features_by_type: MapReviewDashboardBucket[];
	features_by_active: MapReviewDashboardBucket[];

	sites_by_kind: MapReviewDashboardBucket[];
	sites_by_owner_kind: MapReviewDashboardBucket[];

	observations_by_month: MapReviewDashboardPoint[];
	observations_by_reading: MapReviewDashboardBucket[];
	observations_by_technician: MapReviewDashboardBucket[];

	trap_positivity: MapReviewDashboardBucket[];
	catches_by_species: MapReviewDashboardBucket[];
	catches_by_month: MapReviewDashboardPoint[];

	usage_vs_catches: MapReviewDashboardMonthPair[];

	/** The question-led report over the same spec
	 * (doc/design/mvp/report-design.md): the wasted-effort headline, the cost
	 * proxy, the diagnosis of what is producing, the treatment effect and the
	 * allocation instrument. Absent from a payload built before the report
	 * existed, so the client falls back to the legacy panels alone. */
	report?: MapReviewDashboardReport;
}

// ---------------------------------------------------------------------------
// The Map Review map's control-pane legend counts
// (POST /api/feature-counts). The Feature points are drawn from the
// `feature` tile map, which carries no whole-set tallies, so the control pane's
// per-band, deactivated and pinned counts are read from the server instead of
// counted over a loaded Site list. Shapes must match
// `platform/map_review/dashboard.go`.
// ---------------------------------------------------------------------------

/** The feature-counts request: the control pane's due-soon horizon (the slider,
 * in days) and whether deactivated Features are being shown. It carries no spec
 * — the legend counts the district's whole Feature set. */
export interface MapReviewFeatureCountsRequest {
	horizon_days: number;
	include_inactive: boolean;
}

/** The control pane's legend counts: the four control bands against the
 * request's horizon (deactivated Features left out unless include_inactive),
 * plus the district's deactivated and pinned Feature totals. */
export interface MapReviewFeatureCountsResponse {
	expired: number;
	due_soon: number;
	controlled: number;
	unknown: number;
	inactive: number;
	pinned: number;
}

// ---------------------------------------------------------------------------
// The population question's read (`POST /api/map-review/population`): the trap
// catch's smoothed level and six-week slope per H3 cell, under the shared
// spec. Shapes must match `platform/map_review/population.go`. The server
// computes the estimator; the client draws it and never resolves a clause.
// ---------------------------------------------------------------------------

/** The population endpoint's POST body. Every dateRange clause on the trap
 * family crops the week axis; the estimator still uses up to six weeks to the
 * left of the crop. */
export interface MapReviewPopulationRequest {
	spec: MapReviewSpec;
	tz_offset_minutes?: number;
	/** The disease week the view is read at: the season whose traps are read and
	 * the week whose field, breaks and header readings are drawn. Absent (or
	 * zero) reads the season the traps ran at its last week that carries an
	 * analysed sample. Both are ignored while the spec carries a filter clause:
	 * a filtered set is read over its own span, so the week is not the operator's
	 * to set while a filter is in force. The names and types must match
	 * `PopulationRequest` in `platform/map_review/population.go`. */
	year?: number;
	week?: number;
	/** The breaks the view is already classing by. A class filter narrows the
	 * spec to that class's cells; the client sends the view's breaks so the
	 * server classes the narrowed subset the same way instead of re-thirding
	 * it. Absent means "compute the latest week's terciles in scope". */
	breaks?: { level: [number, number]; slope: [number, number] };
}

/** One H3 cell at the latest week in scope. `class` is the bivariate index
 * `sc * 3 + lc` (0 falling/low … 8 rising/high), null when the level or slope
 * is missing. `boundary` is a `[lon, lat]` ring. */
export interface MapReviewPopulationCell {
	h3: string;
	boundary: [number, number][];
	level: number | null;
	slope: number | null;
	class: number | null;
}

/** One trap: its raw weekly catch per analysed sample and its weekly analysed
 * sample count, both index-aligned to the view's `weeks`. The scope ladder's
 * 30-sample rule reads `samplesSeries`. */
export interface MapReviewPopulationStation {
	id: number;
	lon: number;
	lat: number;
	samples: number;
	series: (number | null)[];
	samplesSeries: number[];
}

/** One bivariate class's dissolved geometry — the MultiPolygon a class click
 * adds to the spec as a temporary zone. `index = slope * 3 + level`. */
export interface MapReviewPopulationClass {
	index: number;
	name: string;
	geometry: unknown;
}

/** The sampling days the drawn window covers, as calendar days (`YYYY-MM-DD`).
 * A day is null when the window carries no sample at that end — a week past the
 * season's data is honest and empty. */
export interface MapReviewPopulationRange {
	from: string | null;
	to: string | null;
}

/** The population question's one payload: the cell field, the traps, the latest
 * week's tercile breaks, the district boundary, the nine classes and the season
 * chart set, plus the window the view was read over. Shapes must match
 * `PopulationResponse` in `platform/map_review/population.go`. */
export interface MapReviewPopulationView {
	resolution: number;
	weeks: number[];
	lastWeek: number;
	breaks: { level: [number, number]; slope: [number, number] };
	boundary: { polygons: [number, number][][][] };
	cells: MapReviewPopulationCell[];
	stations: MapReviewPopulationStation[];
	classes: MapReviewPopulationClass[];
	surveillance: MapReviewPopulationSurveillance;
	/** The season actually read — the requested one, or the fallback the server
	 * resolved when that season holds no analysed sample. */
	year: number;
	/** The seasons that carry an analysed sample, ascending. The picker's season
	 * list is exactly these. */
	seasons: number[];
	/** The first and last week with an analysed sample in `year`; 0/0 when the
	 * season has none, which is the empty-season rule the week axis already
	 * follows. */
	seasonFirst: number;
	seasonLast: number;
	/** The first week of the drawn window: 1 for a whole season, the clamped
	 * lower crop bound when a date range narrows the axis. */
	firstWeek: number;
	/** Whether a filter clause overrode the week/year selection and the view
	 * covers the filtered set's own span instead. */
	filtered: boolean;
	/** The sampling days the drawn window reaches. */
	range: MapReviewPopulationRange;
}

/** One season's aggregate series over the chart axis, and its slope at the
 * latest week in scope, in percent per week. */
export interface MapReviewPopulationSeason {
	smoothed: (number | null)[];
	raw: (number | null)[];
	slope: number | null;
}

/** One prior season's series, index-aligned to the chart axis. */
export interface MapReviewPopulationPriorSeason {
	year: number;
	smoothed: (number | null)[];
	raw: (number | null)[];
}

/** The prior seasons' smoothed mean, minimum and maximum at each week. */
export interface MapReviewPopulationBand {
	mean: (number | null)[];
	lo: (number | null)[];
	hi: (number | null)[];
}

/** One season's rolling six-week slope, week by week. */
export interface MapReviewPopulationYearSeries {
	year: number;
	series: (number | null)[];
}

/** The trajectory: this season's rolling slope, the prior seasons' average at
 * the same week, and each prior season behind them. */
export interface MapReviewPopulationRolling {
	current: (number | null)[];
	priorAvg: (number | null)[];
	prior: MapReviewPopulationYearSeries[];
}

/** One species' small multiple: females per analysed sample, its five prior
 * seasons, its band and its slope badge. */
export interface MapReviewPopulationSpeciesTrend {
	id: number;
	name: string;
	current: (number | null)[];
	prior: MapReviewPopulationYearSeries[];
	band: MapReviewPopulationBand;
	slope: number | null;
}

/** The header's five readings. `percentile` is 0..100; `change4`, `slope` and
 * `priorAvgSlope` are percentages. */
export interface MapReviewPopulationSurveillanceStats {
	latest: number | null;
	percentile: number | null;
	change4: number | null;
	slope: number | null;
	priorAvgSlope: number | null;
}

/** The season chart set: the week axis, this season, its five predecessors, the
 * band, the trajectory, the species and the headline readings. Every series is
 * index-aligned to `weeks`; null is a week with no analysed sample, never a
 * zero. */
export interface MapReviewPopulationSurveillance {
	weeks: number[];
	current: MapReviewPopulationSeason;
	prior: MapReviewPopulationPriorSeason[];
	baseline: MapReviewPopulationBand;
	rolling: MapReviewPopulationRolling;
	species: MapReviewPopulationSpeciesTrend[];
	stats: MapReviewPopulationSurveillanceStats;
}

// ---------------------------------------------------------------------------
// The map workbench's waste question ("are we wasting effort?") — the visit
// outcome split, the weekly no-treatment share against the five prior seasons,
// the top Sites, the re-treatment buckets, the observers and the bivariate H3
// waste map, all under the shared spec (`POST /api/map-review/waste`). Shapes must
// match `platform/map_review/waste.go`.
// ---------------------------------------------------------------------------

/** The waste request: the shared spec, the viewer's zone offset (a visit is one
 * Site on one local day) and, while a bivariate class is filtering, the breaks
 * it was cut with. Re-cutting the narrowed subset's terciles would make the
 * same colours mean different levels, so the client freezes them exactly as the
 * population view does. */
export interface MapReviewWasteRequest {
	spec: MapReviewSpec;
	tz_offset_minutes: number;
	breaks?: MapReviewWasteBreaks;
}

/** Tercile breaks of the bivariate grid: visit volume per cell, and the treated
 * share in percent (a low share is the waste corner). */
export interface MapReviewWasteBreaks {
	visits: [number, number];
	treatedShare: [number, number];
}

/** The season's visit outcomes. `visits` is the whole, and the five outcomes
 * partition it: treated-found + treated-none = treated, and
 * checked-none + no-record + found-untreated = the visits no treatment
 * touched. */
export interface MapReviewWasteTotals {
	visits: number;
	treated: number;
	treatedFound: number;
	treatedNone: number;
	checkedNone: number;
	noRecord: number;
	foundUntreated: number;
}

/** One disease week's current-season reading against the same week of the five
 * prior seasons. The five outcome fields are that week's visit counts and sum
 * to `visits`; `priorMin`/`priorMax`/`priorMean` are those seasons' visit
 * COUNTS in the same week — the shaded band, in the bars' own unit. Nil when no
 * prior season reached the week. */
export interface MapReviewWasteWeek {
	week: number;
	visits: number;
	treatedFound: number;
	treatedNone: number;
	checkedNone: number;
	noRecord: number;
	foundUntreated: number;
	priorMin: number | null;
	priorMax: number | null;
	priorMean: number | null;
}

/** One Site's share of the season: the visits it took and the five outcomes
 * they split into, the same buckets the totals and the weekly stack use. The
 * label is the district's own, and `id` opens the Site's detail card. */
export interface MapReviewWasteSite {
	id: number;
	label: string;
	visits: number;
	treatedFound: number;
	treatedNone: number;
	noRecord: number;
	checkedNone: number;
	foundUntreated: number;
}

/** One re-treatment bucket: Features that took 1/2/3/4–5/6+ kept treatments
 * this season, how many of them had production recorded in the season, and the
 * Feature ids a click selects. `truncated` says the id list stopped at the
 * server's cap (`MapReviewWasteView.retreatmentCap`). */
export interface MapReviewWasteBucket {
	key: string;
	label: string;
	features: number;
	stillProducing: number;
	featureIds: number[];
	truncated: boolean;
}

/** One observer: the visits attributed to the person who recorded most of the
 * visit's observations, and how many of those visits ended in a treatment. */
export interface MapReviewWasteCreator {
	id: number | null;
	name: string;
	visits: number;
	treated: number;
}

/** One H3 cell of the waste map. `class` is `conversionRow * 3 + volumeColumn`
 * (row 0 high conversion … row 2 low conversion; column 0 few visits … column 2
 * many), or null for a cell carrying fewer than five visits — thin cells are
 * drawn pale and are never coloured. `boundary` is a `[lon, lat]` ring. */
export interface MapReviewWasteCell {
	h: string;
	lat: number;
	lon: number;
	boundary: [number, number][];
	visits: number;
	treated: number;
	class: number | null;
}

/** One bivariate class's dissolved geometry — the MultiPolygon a class click
 * adds to the spec as a temporary zone. */
export interface MapReviewWasteClass {
	index: number;
	name: string;
	geometry: unknown;
}

/** The waste map's cells and the boundary they are clipped to. */
export interface MapReviewWasteH3 {
	resolution: number;
	boundary: { polygons: [number, number][][][] };
	cells: MapReviewWasteCell[];
}

/** One clause the waste question deliberately does not honour: species, sex and
 * trap clauses have no meaning in a visit-level waste reading, so the server
 * ignores them and says so rather than pretending the number is narrowed. */
export interface MapReviewWasteIgnored {
	table: MapReviewTable;
	column?: string;
	op?: string;
}

/** The waste question's one payload: totals, weeks, sites, buckets, observers,
 * the H3 cells with their breaks and class geometry, and the ignored clauses. */
export interface MapReviewWasteView {
	year: number;
	totals: MapReviewWasteTotals;
	weeks: MapReviewWasteWeek[];
	sites: MapReviewWasteSite[];
	retreatment: MapReviewWasteBucket[];
	creators: MapReviewWasteCreator[];
	h3: MapReviewWasteH3;
	breaks: MapReviewWasteBreaks;
	classes: MapReviewWasteClass[];
	ignored: MapReviewWasteIgnored[];
	retreatmentCap: number;
}

// ---------------------------------------------------------------------------
// The report the dashboard's payload now carries under `report`: the same
// aggregates, folded into the questions that lead to a reduction in the
// population (doc/design/mvp/report-design.md). Shapes must match
// `platform/map_review/report.go`.
//
// Keys follow one rule throughout: a key is the raw value the ledger's own
// filter accepts, so a click can narrow the shared spec to exactly the rows the
// bucket counted. Two classes of key are therefore inert rather than clickable —
// a key of "" (the ledger's enum filters cannot select the empty value) and a
// Site or Trap Station id (an identifier is not filterable anywhere in the
// ledger). The report draws those as plain ranked tallies.
// ---------------------------------------------------------------------------

/** One key's single numeric measure: a share, a rate or a ratio. The value is
 * the number itself — a share of 0.15 is 15% — never a pre-multiplied
 * percentage, so the panel labels the unit it reads in. */
export interface MapReviewDashboardMeasure {
	key: string;
	label: string;
	value: number;
}

/** One key's cost proxy: how many treatments it took, the extent they covered
 * in the district's preferred area unit, and the applied quantity per recorded
 * unit. Quantities in different units are never summed — one `quantities` entry
 * per `treatment.quantity_unit`, because a litre and a pound are not
 * commensurable. */
export interface MapReviewDashboardSpend {
	key: string;
	label: string;
	treatments: number;
	area?: number; // absent when nothing recorded an area
	quantities: MapReviewDashboardUnitTotal[];
}

/** One Site's effort read directly beside its productivity — the instrument
 * that shows a place being serviced hard while producing little. `minutes` is
 * the recorded lower bound (only inspections and treatments carry a window).
 * The key is the Site id: displayed, never a filter clause. */
export interface MapReviewDashboardSiteEffort {
	key: string;
	label: string;
	visits: number;
	productive_visits: number;
	wasted_visits: number;
	treatments: number;
	area?: number;
	quantities: MapReviewDashboardUnitTotal[];
	minutes: number;
	features: number;
	productive_features: number;
	production_found: number;
}

/** The "treatment did not hold" fold: each kept treatment paired with the first
 * kept observation at its Feature after the treatment started. A treatment with
 * no later observation is `unconfirmed` — silence is never read as success. */
export interface MapReviewDashboardRebound {
	treated: number;
	observed: number; // treatments with a later observation to read
	returned: number; // that observation found production again
	suppressed: number; // it found none
	unconfirmed: number; // no inspection, or no later observation at all
	within_window_returns: number; // returned before the residual window expired
	after_window_returns: number; // returned after it expired — expected
	/** Days from residual expiry to the return, absent when nothing returned.
	 * Measured from expiry — when suppression lapses — so a negative value means
	 * production returned before the window lapsed. */
	median_days_to_return?: number;
	post_treatment_observations: number;
	post_treatment_positive: number;
	/** Post-treatment positivity: the positives ÷ the post-treatment
	 * observations × 100. Absent when there was none to read. Never a vector
	 * index — a true one needs the trap family's pool tables, unimported. */
	post_treatment_positive_percent?: number;
}

/** The wasted-effort headline and its decomposition. Waste is the operator's
 * definition — any action that does not lead to a reduction — measured at the
 * site visit: a visit on which no intervention was applied is waste, even where
 * an inspection found production. A site visit is one Site on one client-local
 * day carrying at least one observation, so `treated_visits + wasted_visits =
 * site_visits` and the share is the wasted term over all of them.
 *
 * `by_reading` is not the waste measure but what the visits turned up: the same
 * site visits banded by their observation check (production found, found none,
 * no inspection at all), which is the evidence beside the measure — a visit that
 * found production and applied nothing is waste and still reads as production
 * found here. `by_site` is the top 15 Sites by wasted site visits. */
export interface MapReviewDashboardWaste {
	site_visits: number;
	treated_visits: number;
	wasted_visits: number;
	/** wasted_visits ÷ site_visits × 100; absent when there was no visit. */
	wasted_percent?: number;
	by_reading: MapReviewDashboardBucket[];
	by_site: MapReviewDashboardBucket[];
}

/** Where are we: what the population is doing, against what reference, where it
 * is worst, and what dominates. */
export interface MapReviewDashboardOrientation {
	production_by_month: MapReviewDashboardPoint[];
	visits_by_month: MapReviewDashboardPoint[];
	abundance_by_month: MapReviewDashboardPoint[];
	production_by_site: MapReviewDashboardBucket[];
	catches_by_site: MapReviewDashboardBucket[];
	catches_by_station: MapReviewDashboardBucket[];
	inspections_by_stage: MapReviewDashboardBucket[];
	inspections_by_density: MapReviewDashboardBucket[];
}

/** Why is it where it is: what is producing, how much is unknown, what is
 * controlled, whether the adulticide holds, and whether the chemicals work. */
export interface MapReviewDashboardDiagnosis {
	features_by_production_state: MapReviewDashboardBucket[];
	/** The share of kept Features whose production is undetermined: never
	 * inspected, or inspected and still unknown. The gap the operation cannot be
	 * efficient over. */
	unknown_production_percent?: number;
	production_by_habitat: MapReviewDashboardBucket[];
	source_features_by_habitat: MapReviewDashboardBucket[];
	source_features_total: number;
	source_features_controlled: number;
	/** source_features_controlled ÷ source_features_total × 100. */
	source_control_percent?: number;
	rebound: MapReviewDashboardRebound;
	product_use_by_site: MapReviewDashboardBucket[];
	retreatments_by_feature: MapReviewDashboardBucket[];
	production_by_treatment_count: MapReviewDashboardBucket[];
	production_outside_control: MapReviewDashboardBucket[];
}

/** Is it working: the treatment outcome, and the share of visits that found
 * production, by site and by the technician who recorded them. */
export interface MapReviewDashboardEffect {
	treatment_outcomes: MapReviewDashboardBucket[];
	production_share_by_site: MapReviewDashboardMeasure[];
	production_share_by_applicator: MapReviewDashboardMeasure[];
}

/** What should change: the cost proxy, the marginal return, the effort beside
 * the productivity, and the sites the next unit of effort should buy. */
export interface MapReviewDashboardAllocation {
	spend_by_product: MapReviewDashboardSpend[];
	spend_by_site: MapReviewDashboardSpend[];
	/** Recorded field minutes over the kept inspections and treatments. A lower
	 * bound: an observation with no live inspection carries no window. */
	field_minutes: number;
	field_minutes_by_month: MapReviewDashboardPoint[];
	effort_by_site: MapReviewDashboardSiteEffort[];
	/** production_found ÷ site-visits, per Site. Where one more visit has
	 * historically been most likely to find production. */
	marginal_by_site: MapReviewDashboardMeasure[];
	/** Sites with production found and no Feature under current control,
	 * ordered by production found. The rule the report ranks by, not an
	 * optimum. */
	under_serviced_sites: MapReviewDashboardSiteEffort[];
	treatments_by_category: MapReviewDashboardBucket[];
}

/** The questions the district's schema cannot answer, named rather than
 * omitted: `unavailable` carries the question ids the server could not resolve
 * ("weather", "land_use", "regional"), and the report says what is missing
 * instead of drawing an empty axis. */
export interface MapReviewDashboardContext {
	complaints_by_month: MapReviewDashboardPoint[];
	complaints_by_type: MapReviewDashboardBucket[];
	unavailable: string[];
}

export interface MapReviewDashboardReport {
	orientation: MapReviewDashboardOrientation;
	diagnosis: MapReviewDashboardDiagnosis;
	effect: MapReviewDashboardEffect;
	allocation: MapReviewDashboardAllocation;
	context: MapReviewDashboardContext;
	waste: MapReviewDashboardWaste;
}

// ---------------------------------------------------------------------------
// The dashboard's surveillance section: the Map Review trap family's weekly
// abundance against a five-year baseline (doc/design/mvp/dashboard.md). Shapes
// must match `platform/map_review/surveillance.go`.
// ---------------------------------------------------------------------------

/** The surveillance request: the report year. Absent (or zero) means the
 * current year, resolved by the server. */
export interface MapReviewSurveillanceRequest {
	year?: number;
}

/** One disease week (MMWR — Sunday start) of a weekly series. `value` and every
 * `baseline_*` figure are absent when that week has no samples — read as a gap,
 * never as zero. `samples` is the analysed trap sample count: the series'
 * denominator, and the number the per-trap metric divides by. */
export interface MapReviewSurveillanceWeek {
	week: number;
	value?: number | null;
	baseline_mean?: number | null;
	baseline_min?: number | null;
	baseline_max?: number | null;
	samples: number;
}

/** One species' weekly series: females per trap, all 53 disease weeks of the
 * report year, with `samples` the shared per-trap denominator. */
export interface MapReviewSurveillanceSpecies {
	species_id: number;
	name: string;
	weeks: MapReviewSurveillanceWeek[];
}

export interface MapReviewSurveillanceResponse {
	year: number;
	baseline_years: number[]; // the five seasons immediately before `year`
	week_label: string; // the week axis' title, e.g. "Disease week (MMWR)"
	total_per_trap: MapReviewSurveillanceWeek[];
	species_per_trap: MapReviewSurveillanceSpecies[];
}

// ---------------------------------------------------------------------------
// Pesticide inventory — storage locations, units, stock, containers, checks,
// and custom mixes (doc/design/mvp/inventory.md). Shapes must match the
// `resource/inventory_*.go` response structs. Stock amounts are in the
// product's base unit; timestamps are UTC ISO strings — render them through
// the Time* components.
// ---------------------------------------------------------------------------

// The kind of district product row: an ordinary product or a custom mix.
export type ProductKind = "simple" | "mix";

// The dimension an inventory unit measures: weight, volume, or a count of
// discrete objects (briquets, tablets, dunks).
export type InventoryUnitKind = "weight" | "volume" | "count";

// How a stock figure was arrived at: a physical count, a weigh, or an estimate.
export type InventoryStockMethod = "counted" | "weighed" | "estimated";

// One entry of the append-only movement ledger: receive adds stock, subdivide
// splits a container, checkout/return move it, waste/dispose remove it, and
// adjust corrects it to a counted figure.
export type InventoryMovementKind =
	| "receive"
	| "subdivide"
	| "checkout"
	| "return"
	| "waste"
	| "dispose"
	| "adjust";

// One node of the district's storage-location tree; parent_id is null for a
// root, and retired is set once the location is closed to new stock.
export interface InventoryLocation {
	id: number;
	parent_id: number | null;
	name: string;
	path: string;
	retired: string | null;
}

// The district's chosen base unit for one catalog product.
export interface InventoryUnit {
	id: number;
	product_catalog_id: string;
	product_name: string;
	unit: string;
	kind: InventoryUnitKind;
}

// One product's stock at one storage location. unit is null when the product
// has no configured unit; updated is when the amount last changed.
export interface InventoryStock {
	id: number;
	product_catalog_id: string;
	product_name: string;
	unit: string | null;
	storage_location_id: number;
	location_name: string;
	location_path: string;
	amount: number;
	method: InventoryStockMethod;
	updated: string;
}

// One ledger entry. The product always carries a base-unit amount; the
// location and creator are absent on synthetic entries.
export interface InventoryMovement {
	id: number;
	product_catalog_id: string;
	product_name: string;
	storage_location_id: number | null;
	location_name: string | null;
	location_path: string | null;
	kind: InventoryMovementKind;
	amount: number;
	reason: string | null;
	occurred: string;
	creator_id: number | null;
}

// One reusable container type the district defines: how much of a product a
// container of this type holds, and in which unit.
export interface ContainerDefinition {
	id: number;
	product_catalog_id: string;
	product_name: string;
	name: string;
	measure_amount: number;
	measure_unit: string;
	retired: string | null;
}

// One physical container, its current amount, and the container it was drawn
// from when it came out of a subdivide.
export interface InventoryContainer {
	id: number;
	container_definition_id: number;
	definition_name: string;
	product_catalog_id: string;
	product_name: string;
	storage_location_id: number | null;
	location_name: string | null;
	amount: number;
	drawn_from_container_id: number | null;
}

// One inventory check (an audit pass) as a list row; line_count is how many
// products were counted.
export interface InventoryCheck {
	id: number;
	note: string | null;
	created: string;
	completed: string | null;
	line_count: number;
}

// One counted product in an inventory check. expected_amount is the stock read
// at the moment of counting; adjustment_movement_id is set when the count
// differed and an adjust movement corrected the stock.
export interface InventoryCheckLine {
	id: number;
	product_catalog_id: string;
	product_name: string;
	storage_location_id: number;
	location_name: string;
	counted_amount: number;
	expected_amount: number;
	reason: string | null;
	adjustment_movement_id: number | null;
	created: string;
}

// One check with all of its counted lines (GET /api/inventory/check/{id}).
export interface InventoryCheckDetail {
	check: InventoryCheck;
	lines: InventoryCheckLine[];
}

// One check line whose counted amount differed from the expected amount, with
// variance = counted - expected (GET /api/inventory/reconciliation).
export interface InventoryReconciliation {
	id: number;
	inventory_check_id: number;
	product_catalog_id: string;
	product_name: string;
	location_name: string;
	counted_amount: number;
	expected_amount: number;
	variance: number;
	reason: string | null;
	created: string;
}

// One component of a custom mix: either a catalog product (product_catalog_id
// set) or a named inert carrier (name set, is_inert true). amount is the
// component's quantity in the recipe, measured in unit.
export interface InventoryMixComponent {
	id: number;
	product_catalog_id: string | null;
	name: string;
	is_inert: boolean;
	amount: number;
	unit: string;
}

// One custom mix: a district product with kind "mix", the recipe header it is
// prepared to, and its full component set.
export interface InventoryMix {
	id: number;
	name: string;
	retired: string | null;
	prepared_amount: number;
	prepared_unit: string;
	components: InventoryMixComponent[];
}

// One non-inert component of a mix report, with its catalog registration
// numbers, its unit, and its share of the prepared amount. Inert carriers are
// omitted.
export interface InventoryMixReportComponent {
	product_catalog_id: string;
	product_name: string;
	epa_registration: string | null;
	dpr_registration_number: string | null;
	unit: string;
	share: number;
	quantity: number;
}

// The state-report shape of a mix for an applied amount: the recipe header it
// was prepared to, the amount applied, the fraction of the prepared batch that
// application used, and each non-inert component's quantity. The mix itself is
// never reported as a unit.
export interface InventoryMixReport {
	mix_id: number;
	mix_name: string;
	prepared_amount: number;
	prepared_unit: string;
	applied_amount: number;
	applied_unit: string;
	fraction: number;
	components: InventoryMixReportComponent[];
}

// InventorySdsBookGap is one brand whose book entry is incomplete: it has no
// label, or no safety data sheet. brand_id is null when the registry has no
// brand for the district row, in which case the name is the district's own
// product name. product_id is the district product page an operator follows to
// fill the gap.
export interface InventorySdsBookGap {
	name: string;
	brand_id: number | null;
	product_id: number;
	product_name: string;
}

// InventorySdsBookSummary is the book before it is generated
// (GET /api/inventory/sds-book/summary): the counts the front page will state
// and every brand that will be listed as missing a label or a safety data
// sheet. brands counts the units that contribute at least one document;
// documents is the count after identical bytes are included once.
export interface InventorySdsBookSummary {
	district: string;
	generated_at: string;
	brands: number;
	documents: number;
	missing_labels: InventorySdsBookGap[];
	missing_sds: InventorySdsBookGap[];
}

// ---------------------------------------------------------------------------
// Request bodies for the inventory endpoints (section 5 of the contract).
// ---------------------------------------------------------------------------

// POST /api/inventory/location — parent_id is null for a root location.
export interface LocationCreate {
	parent_id?: number | null;
	name: string;
}

// PUT /api/inventory/location/{id} — every field is optional; omitted fields
// are left unchanged.
export interface LocationUpdate {
	name?: string;
	parent_id?: number | null;
	retired?: boolean;
}

// PUT /api/inventory/unit/{product_catalog_id} — set a product's base unit.
export interface UnitSet {
	unit: string;
	kind: InventoryUnitKind;
}

// POST /api/inventory/stock/receive — add stock at a location and record the
// receive movement.
export interface ReceiveCreate {
	product_catalog_id: string;
	storage_location_id: number;
	amount: number;
	method: InventoryStockMethod;
	reason?: string;
}

// POST /api/inventory/container-definition — define a reusable container type.
export interface ContainerDefinitionCreate {
	product_catalog_id: string;
	name: string;
	measure_amount: number;
	measure_unit: string;
}

// POST /api/inventory/container — fill a new container.
export interface ContainerCreate {
	container_definition_id: number;
	storage_location_id: number;
	amount: number;
}

// POST /api/inventory/container/{id}/subdivide — split a container into
// targets, recording `waste` as a separate waste movement. The source must
// hold at least the sum of the targets plus the waste.
export interface ContainerSubdivide {
	targets: { container_definition_id: number; amount: number }[];
	waste: number;
	reason?: string;
}

// POST /api/inventory/check — start an inventory check.
export interface CheckCreate {
	note?: string;
}

// POST /api/inventory/check/{id}/count — record one product's counted amount
// at a location. A count that differs from the expected amount writes an
// adjust movement with the reason.
export interface CheckCount {
	product_catalog_id: string;
	storage_location_id: number;
	counted_amount: number;
	reason?: string;
}

// POST/PUT /api/inventory/mix — a mix's name, the recipe header it is prepared
// to, and its full replacement component set: an inert component carries a name
// and no catalog id, a non-inert one carries a catalog slug.
export interface MixWrite {
	name: string;
	prepared_amount: number;
	prepared_unit: string;
	components: {
		product_catalog_id?: string | null;
		name?: string | null;
		is_inert: boolean;
		amount: number;
		unit: string;
	}[];
}

/** The natural-language ask request: the operator's question and, when the
 * client knows it, the zone offset that anchors relative dates to the
 * operator's own days (see clientZoneOffsetMinutes). */
export interface MapReviewAskRequest {
	question: string;
	tz_offset_minutes?: number | null;
}

/** The ask response. `answered` is false when the question could not be
 * expressed as a filter over the eight tables — an out-of-scope aggregation, an
 * ambiguous name, or a question that named nothing — in which case `response`
 * is null and `explanation` says why in the operator's words. An unanswerable
 * question is never a server error. */
export interface MapReviewAskResponse {
	answered: boolean;
	explanation: string;
	/** The records the question is about; absent when it could not be expressed. */
	table?: MapReviewTable;
	spec: MapReviewSpec;
	/** The page the resolver returned for the generated spec, or null when the
	 * question was not answered. */
	response: MapReviewQueryResponse | null;
}

// --- Map lab ---------------------------------------------------------------
//
// The map lab's reads: the per-theme point collections
// (`QUERY /api/lab/points/{theme}` — the theme is the collection — with the
// theme's own window read beside it on the same path, exported for the exact
// busiest-cell aggregate the generic envelope has no member for), and the two
// dataset-level reads on their own paths
// (`/api/lab/datasets` and `/api/lab/h3-multi`). They exist so
// the lab labels every mode with the dataset it really draws, including the
// themes whose tables are empty, and so the one client-side aggregation left
// (clusters) only ever aggregates a bounded time window of real rows. The H3
// multi read describes the combined grid the multi-dataset hex modes paint over:
// the same population and resolution as the `h3-multi` tile, so a
// legend drawn from it matches the hexagons on screen.

/** One lab dataset's facts, counted server-side. `from`/`to` are null for a
 * standing set with no date column; `available` is false when the theme has no
 * rows at all, in which case `note` says why. */
export interface LabDataset {
	id: string;
	label: string;
	source: string;
	total: number;
	dated: boolean;
	from: string | null;
	to: string | null;
	available: boolean;
	note: string;
}

export interface LabDatasetsResponse {
	datasets: LabDataset[];
}

/** A bounded window of one theme's records. `positions` is a flat
 * `[lng,lat,...]` array rounded to 5 decimals; `kinds[i]` indexes
 * `kindLabels` for point `i`. `count` is the window's exact row count and
 * `maxCellCount` the busiest H3 resolution-9 cell in the window — both exact
 * even when the point payload is capped (`truncated`) or omitted (`points=0`),
 * so the legend's ramp domain never depends on the sampled points. */
export interface LabPointsResponse {
	theme: string;
	from: string | null;
	to: string | null;
	count: number;
	returned: number;
	truncated: boolean;
	limit: number;
	positions: number[];
	kinds: number[];
	kindLabels: string[];
	maxCellCount: number;
	note: string;
}

/** One drawn record as `QUERY /api/lab/points/{theme}` answers it
 * (doc/in-progress/query.md §11 Phase 4): where it is and the kind label it is
 * drawn with. `LabPointsResponse`'s flat `positions` and its `kinds`/
 * `kindLabels` pair are built from a page of these, so the pane and the tray
 * still read one payload whatever surface the points came from. */
export interface LabQueryPoint {
	lng: number;
	lat: number;
	kind: string;
}

/** One series' facts in the combined H3 grid's window. `total` is how many
 * records the series holds and `cells` over how many resolution-9 cells it is
 * spread — the two numbers tell apart "a lot everywhere" from "a lot in one
 * place". `maxCellCount` and the two terciles `p33`/`p67` are the quantiles a
 * sequential ramp (or the bivariate's class breaks) is normalised by, expressed
 * in the MEASURE's units; `measureTotal` is the series' window total in those
 * same units. `id` is a table dataset's own id, or `s0`..`s3` for the slot an
 * operator's series is assigned to. */
export interface LabH3Series {
	id: string;
	label: string;
	total: number;
	cells: number;
	maxCellCount: number;
	p33: number;
	p67: number;
	measureTotal: number;
}

/** One catalogue entry's weight in the window: its rows, and its measure total
 * in the measure's units. It is what lets the Series panel name a member as
 * empty before it is drawn. */
export interface LabH3CatalogueEntry {
	id: string;
	total: number;
	measureTotal: number;
}

/** One quantity unit the district's treatments actually carry. The units are
 * free text in the database, so the measure dropdown is built from THIS rather
 * than from a list somebody typed. */
export interface LabH3Unit {
	unit: string;
	rows: number;
	sum: number;
}

/** The combined H3 grid's window facts: one entry per series, plus
 * `maxCellTotal`, the busiest resolution-9 cell counting every drawn series at
 * once — the domain the total-activity encoding's ramp is drawn against (no
 * single series' `maxCellCount` is that number). `from`/`to` echo the window the
 * counts describe, both null for an unbounded one. `catalogue` (every entry's
 * weight) and `units` (the quantity units present) are present only when the
 * request asked for them. */
export interface LabH3MultiResponse {
	from: string | null;
	to: string | null;
	resolution: number;
	maxCellTotal: number;
	series: LabH3Series[];
	catalogue?: LabH3CatalogueEntry[];
	units?: LabH3Unit[];
}

// ── Saved H3 layers ────────────────────────────────────────────────────────
//
// The workbench's own saved H3 layers, beside the standing map: an org-scoped
// row whose DEFINITION is 1..3 VARIABLES, each one a series catalogue source
// plus a metric. Visibility (on/off) is per-user and device-local, so it is
// deliberately not part of the wire shape — a layer's definition is the
// district's, its place in the tray is the operator's.

/** One variable's measure. `records` counts the predicate's rows; `area` sums
 * their `area_treated` in the district's area unit; `quantity` sums their
 * `quantity` within ONE unit (a unit is a noun, not a scalar, so mixing two of
 * them would add nothing). The three are the lab's `SERIES_MEASURES`, by id. */
export type MapReviewH3Metric = "records" | "quantity" | "area";

/** One variable: the catalogue source it counts (`SERIES_CATALOGUE`'s `index`,
 * 0..15) and by which metric. `unit` is required (non-empty) iff the metric is
 * `quantity` and is `""` for the other two — `area` reads the district's
 * resolved area unit, which is not the layer's to store. `color` is the
 * `#rrggbb` the variable is drawn in, and carries no `#`-less or named form. */
export interface MapReviewH3Variable {
	source: number;
	metric: MapReviewH3Metric;
	unit: string;
	color: string;
}

/** A saved layer's definition, in DRAW ORDER: variable 0 is the fill (the whole
 * picture, a single sequential ramp, when it is alone), variable 1 upgrades the
 * fill to the bivariate, and variable 2 is drawn as the dissolved outline over
 * it. */
export interface MapReviewH3LayerDefinition {
	variables: MapReviewH3Variable[];
}

/** A saved layer as the API returns it. `created_by`/`updated_by` are user ids,
 * null when that user has since been deleted; `created`/`updated` are RFC 3339
 * timestamps. */
export interface MapReviewH3Layer {
	id: number;
	name: string;
	definition: MapReviewH3LayerDefinition;
	created_by: number | null;
	updated_by: number | null;
	created: string;
	updated: string;
}

/** The body of a create or update: the name and the definition. `POST` answers
 * with the stored row, so the client reads back the id and the timestamps
 * rather than assuming them. */
export interface MapReviewH3LayerRequest {
	name: string;
	definition: MapReviewH3LayerDefinition;
}

// ---------------------------------------------------------------------------
// Home — the situation-awareness launchpad at /_/dash.
//
// One payload carries the district's state in the shape the operator reads it:
// a card per destination workbench, the weather over the district, and every
// technician's last-known position. GET /api/home serves the weather, the wind,
// the boundary, the awaiting communications and the technician positions below;
// the other workbench cards and the roster are still fed from
// ts/components/home/placeholder.ts.
// ---------------------------------------------------------------------------

/** One channel's share of the awaiting communications. */
export interface HomeCommunicationChannel {
	/**
	 * The channel the exchange came through: a `communication.type_` value
	 * ("public_report", "text", "phone_call", …), or "unknown" for a row the
	 * district recorded without one.
	 */
	channel: string;
	count: number;
}

/**
 * Communications workbench: everything still awaiting an answer.
 *
 * `total` is the sum of `channels[].count`, so the sidebar badge's number —
 * the unreviewed public reports, which the `public_report` row carries — is
 * visibly one row of it rather than a figure that disagrees with the others.
 */
export interface HomeCommunicationsCard {
	/** Sum of `channels[].count`. */
	total: number;
	/** Channels with something waiting, busiest first. */
	channels: HomeCommunicationChannel[];
	/** Whole days the oldest awaiting item has waited; null when none is. */
	oldest_waiting_days: number | null;
}

/** Planning workbench: signals logged this week and leads still in play. */
export interface HomePlanningCard {
	new_signals_this_week: number;
	active_leads: number;
}

/**
 * Review workbench. Not yet developed: the panel is a placeholder and carries
 * no figure, so the field is explicit about its absence rather than zero.
 */
export interface HomeReviewCard {
	new_records_today: number | null;
}

/**
 * One Surveillance trend — this week's value set against each of the three
 * weeks before it. `reference` is the alarm line the measure is read against
 * (VI 80); it is null for a measure that has none.
 */
export interface HomeSurveillanceTrend {
	this_week: number;
	/** The three prior weeks, newest first. */
	prior_weeks: number[];
	/** Short labels for `prior_weeks`, index-aligned (e.g. "Sep 22"). */
	prior_week_labels: string[];
	reference: number | null;
}

/** Surveillance workbench: trap pressure and vector index, same-week basis. */
export interface HomeSurveillanceCard {
	trap_counts: HomeSurveillanceTrend;
	vector_index: HomeSurveillanceTrend;
	/** Human-readable basis for both trends, e.g. "this week vs the three prior weeks". */
	trend_basis: string;
}

/**
 * Current conditions at the district weather station. The dry-bulb
 * temperature is Fahrenheit on the wire, as is every temperature and the heat
 * index; the card converts to Celsius only for its secondary reading.
 */
export interface HomeWeatherCurrent {
	temperature_f: number;
	wind_kph: number;
	wind_direction: string;
	precipitation_mm: number;
	condition: string;
	observed_at: string;
}

/**
 * One day of the seven-day forecast strip. The highs and lows are Fahrenheit
 * on the wire, as is the heat index, which is a temperature.
 */
export interface HomeWeatherDay {
	day: string;
	high_f: number;
	low_f: number;
	precipitation_chance: number;
	condition: string;
	/** Bootstrap Icons class for the condition, e.g. "bi-cloud-rain". */
	icon: string;
	/**
	 * Peak heat index for the day, in °F — the wire unit for the temperature
	 * and the heat index alike. The heat index is dry-bulb temperature with
	 * humidity folded in — it is not the temperature, and it is the measure
	 * OSHA and Cal/OSHA gate field work on.
	 *
	 * The eventual NWS gridpoint response carries `heatIndex` directly, beside
	 * `relativeHumidity`, `apparentTemperature` and `heatRisk`, so the real
	 * value is read, never computed here.
	 */
	heat_index_f: number;
}

/** One vector species' egg-to-adult requirement, and the days it implies. */
export interface HomeEggToAdultSpecies {
	species: string;
	/** Required degree days to complete egg → adult, in DD °C. */
	degree_days: number;
	/** `degree_days` divided by the rolling 7-day mean daily rate. */
	days: number;
}

/**
 * Days from egg to adult. The rate is the rolling 7-day mean daily degree days
 * at base 10 °C, average method, biofix Jan 1; the cumulative season total is
 * not part of this payload.
 */
export interface HomeEggToAdult {
	/** Rolling 7-day mean daily degree days, in DD °C/day. */
	daily_degree_day_rate: number;
	/** Mean of `species[].days`. */
	average_days: number;
	species: HomeEggToAdultSpecies[];
}

/** The district's weather, as the Home screen reads it. */
export interface HomeWeatherCard {
	current: HomeWeatherCurrent;
	forecast: HomeWeatherDay[];
	egg_to_adult: HomeEggToAdult;
}

/** Today's assignment load for one technician, in the client's local day. */
export interface HomeTechnicianAssignments {
	/** The day's assignments in the terminal `completed` state. */
	completed: number;
	/** The day's assignments still live: draft, dispatched, in_progress or deferred. */
	open: number;
}

/**
 * One technician of the district: their user record, where their latest Nidus
 * Notes breadcrumb last placed them, and today's assignment load.
 *
 * The roster carries every non-deleted user of the organisation, so `location`
 * and `last_seen` are null for a technician who has left no breadcrumb. That
 * null position is also what makes them not `active_today` — and what the map
 * omits as a marker while the roster still lists them.
 */
export interface HomeTechnician {
	user: User;
	/** Null when the technician has left no breadcrumb on any note. */
	location: Location | null;
	/**
	 * True when the technician left a Nidus Notes breadcrumb today. "Active
	 * today" is exactly this test — not a shift roster, not a login.
	 */
	active_today: boolean;
	/** When the breadcrumb was left; null exactly when `location` is. */
	last_seen: string | null;
	assignments_today: HomeTechnicianAssignments;
}

/**
 * One wind streamline over the district: a short polyline carrying the speed
 * along it. The line shows where the wind is; the arrowheads at its downstream
 * end show which way it is going.
 *
 * The eventual source is the same NWS gridpoint product the forecast comes
 * from (u/v wind components per gridpoint), interpolated to a streamline field.
 */
export interface HomeWindLine {
	/** Speed along the streamline, km/h. */
	speed_kph: number;
	/** [lng, lat] pairs, start to end. */
	coordinates: [number, number][];
}

/**
 * One downstream arrowhead on a streamline: a filled triangle whose apex
 * points where the wind is going. `ring` is a three-point, unclosed triangle
 * of [lng, lat] pairs; the map closes it for the fill.
 */
export interface HomeWindArrow {
	/** Speed at the head, km/h. */
	speed_kph: number;
	/** A three-point, unclosed triangle ring of [lng, lat] pairs. */
	ring: [number, number][];
}

/**
 * The district's service-area boundary. Null in the payload when the
 * organisation has no service area, so the map draws no boundary line rather
 * than a default one.
 */
export interface HomeBoundary {
	geojson: GeoJSON.Geometry;
}

/** The district's weather: conditions now and the seven-day strip. */
export interface HomeWeather {
	current: HomeWeatherCurrent;
	forecast: HomeWeatherDay[];
}

/** The wind over the district: streamlines plus the arrowheads that carry direction. */
export interface HomeWind {
	lines: HomeWindLine[];
	arrows: HomeWindArrow[];
}

/**
 * GET /api/home — the Home screen's single payload.
 *
 * `weather`, `wind` and `communications` are best-effort: a source that could
 * not be read is null here, and its section name is listed in `unavailable`
 * (empty when every section read), so the screen serves the rest rather than
 * failing whole. `boundary` and `technicians` are required — their failure
 * fails the request.
 */
export interface HomePayload {
	boundary: HomeBoundary | null;
	/** Sections that could not be read, e.g. ["weather", "wind"]. */
	unavailable: string[];
	weather: HomeWeather | null;
	wind: HomeWind | null;
	communications: HomeCommunicationsCard | null;
	technicians: HomeTechnician[];
}

// §7's Assignment and §8's Route, as the sync wire presents them (platform/
// types/sync_api.go). The office workbench reads both through these shapes and
// writes through the request bodies below, so the camelCase row the device
// syncs and the snake_case body the create doors take are stated side by side
// rather than translated ad hoc at each call site.

/** The closed kind vocabulary (§7 extension, operator decision 1): what work the
 * Assignment directs. */
export type AssignmentKind =
	| "set_trap"
	| "collect_trap"
	| "inspect"
	| "abate"
	| "treat"
	| "custom";

/** §7's state machine states. `completed` and `cancelled` are terminal and carry
 * no outgoing edge; `deferred` returns to `in_progress`. */
export type AssignmentState =
	| "draft"
	| "dispatched"
	| "in_progress"
	| "deferred"
	| "completed"
	| "cancelled";

/** How an Assignment came to exist. Only a `self_assignment` carries a
 * justification (§7 System Rule 4). */
export type AssignmentCreationMode = "planned" | "self_assignment";

/** §8's Route states. `active` is reached implicitly by the first member
 * entering In Progress as well as by an operator. */
export type RouteState =
	| "draft"
	| "dispatched"
	| "active"
	| "completed"
	| "cancelled";

/** One Assignment as `GET /api/assignment`, `GET /api/assignment/{id}`,
 * `PUT /api/assignment/{id}/state` and a QUERY page answer with, and the shape
 * the sync engine pulls. Exactly one target is non-null — a Site, a Feature, a
 * Facilitator or a geometry — because an Assignment is bound to exactly one
 * "specific place" (§7). `kind` is nullable for a row that predates the
 * vocabulary, and the workbench prints such a row as Unclassified. */
export interface SyncAssignment {
	id: number;
	organizationId: number;
	technicianId: number;
	creationMode: AssignmentCreationMode;
	justification: string | null;
	kind: AssignmentKind | null;
	instructions: string | null;
	approvedById: number | null;
	/** The Route holding this Assignment — absent while it belongs to none. */
	routeId: number | null;
	/** The member's slot in the Route's sequence; null when the Route has not
	 * sequenced it yet (operator decision 10). */
	routePosition: number | null;
	state: AssignmentState;
	/** The recorded reason for a move into `deferred` or `cancelled`. */
	stateReason: string | null;
	targetSiteId: number | null;
	targetFeatureId: number | null;
	targetFacilitatorId: number | null;
	/** The drawn target, when the Assignment's target is a geometry. */
	targetGeometry: GeoJSON.Geometry | null;
	/** Only a `set_trap` Assignment may name one (the configuration is that
	 * kind's instruction). */
	trapConfigurationId: number | null;
	startAt: string | null;
	endAt: string | null;
	/** Planned time on site, overriding the district's per-kind default; null
	 * means the district default applies, and a zero-minute stop is stated as
	 * "no time estimate" rather than guessed. A Nidus extension on §7
	 * Assignment, like work kind. */
	serviceMinutes: number | null;
	/** Set only by the transition that completed the Assignment. */
	completedAt: string | null;
	/** Provenance of an imported row (e.g. `mapvision`); null for work authored
	 * in Nidus. */
	sourceSystem: string | null;
	sourceId: string | null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
}

/** One membership row of a Route: the Assignment and the position it holds in
 * the technician's sequence. A null position is a member the Route has not
 * sequenced yet, and such members sort after every sequenced one. */
export interface SyncRouteMember {
	assignmentId: number;
	position: number | null;
}

/** One Route as both route reads and every route write answer with. */
export interface SyncRoute {
	id: number;
	organizationId: number;
	technicianId: number;
	state: RouteState;
	/** The operational window — arbitrary, not a calendar day (decision 9). */
	windowStart: string;
	windowEnd: string;
	/** The membership in sequence order; unsequenced members follow. */
	assignmentIds: number[];
	members: SyncRouteMember[];
	createdAt: string;
	dispatchedAt: string | null;
	completedAt: string | null;
	updatedAt: string;
	deletedAt: string | null;
}

/** The body of `POST /api/assignment`. The workbench sends `creation_mode:
 * "planned"` and states no `state`: everything this door writes is born draft
 * and reaches `completed` only through the evidence gate (decision 3).
 * `target_geometry` is a GeoJSON string rather than an object, exactly as
 * platform/types/assignment.go takes it. `lead_id` and `work_order_id` are the
 * Assignment's provenance, at most one of them (§7): the door links the Lead, or
 * converts an open Work Order, in the same transaction that writes the row. */
export interface AssignmentCreate {
	technician_id: number;
	creation_mode?: AssignmentCreationMode;
	justification?: string | null;
	kind: AssignmentKind;
	instructions?: string | null;
	trap_configuration_id?: number | null;
	target_site_id?: number | null;
	target_feature_id?: number | null;
	target_facilitator_id?: number | null;
	target_geometry?: string;
	start_time?: string | null;
	end_time?: string | null;
	service_minutes?: number | null;
	lead_id?: number | null;
	work_order_id?: number | null;
}

/** The body of `PUT /api/assignment/{id}/schedule`: the access window and the
 * service estimate, each optional and each present key replacing the stored
 * value (`null` clears it). Refused unless the Assignment is draft and, when it
 * has a Route, that Route is draft — dispatched and settled work keeps its
 * windows. */
export interface AssignmentScheduleUpdate {
	start_at?: string | null;
	end_at?: string | null;
	service_minutes?: number | null;
}

/** The body of `PUT /api/assignment/{id}/state`. `reason` is required exactly
 * where §7 demands one (into deferred or cancelled) and ignored elsewhere. */
export interface AssignmentStateUpdate {
	to: AssignmentState;
	reason?: string | null;
}

/** One Assignment `POST /api/route` authors alongside its Route, stated rather
 * than named: the work the operator composes with the Route in the same write.
 * A spec inherits the Route's `technician_id`, so it states none of its own —
 * the field exists only so a caller that does state one is checked against the
 * Route's, never to select a second technician. `state` may say `"draft"` or
 * nothing because creation cannot skip the lifecycle. Exactly one target is
 * named, as on `POST /api/assignment`; `target_geometry` is the same GeoJSON
 * string, not a second encoding of it. */
export interface AssignmentSpec {
	kind: AssignmentKind;
	instructions?: string | null;
	creation_mode?: AssignmentCreationMode;
	justification?: string | null;
	state?: "draft" | "";
	trap_configuration_id?: number | null;
	target_site_id?: number | null;
	target_feature_id?: number | null;
	target_facilitator_id?: number | null;
	target_geometry?: string;
	start_time?: string | null;
	end_time?: string | null;
	technician_id?: number | null;
	service_minutes?: number | null;
	/** Provenance, at most one (§7): the Lead the work came from, or the open
	 * Work Order the write converts. Carried by the Work queue's Route door, so a
	 * Route built from checked rows records where each assignment came from. */
	lead_id?: number | null;
	work_order_id?: number | null;
}

/** The body of `POST /api/route`: the technician, the window, and the work the
 * Route is created holding. Membership is the existing Assignments named in
 * `assignment_ids`, in their given order, then the ones authored inline in
 * `assignments`, in theirs. Either set may be empty: a Route may be created and
 * held in draft with no Assignments (§8 Rule 1, as amended), because a draft
 * Route is an authored plan container that may precede its first member. It may
 * not be dispatched with none — that gate is the platform's, not this body's. */
export interface RouteCreate {
	technician_id: number;
	window_start: string;
	window_end: string;
	assignment_ids: number[];
	assignments: AssignmentSpec[];
}

/** The body of `PUT /api/route/{id}/state`: §8's table is the set of legal
 * edges. */
export interface RouteStateUpdate {
	to: RouteState;
}

/** The body of `PUT /api/route/{id}/assignment`. A null or absent position is
 * an unsequenced member, which is a legal membership. */
export interface RouteMembershipUpdate {
	assignment_id: number;
	position?: number | null;
}

/** The body of `PUT /api/route/{id}/order`: the Route's complete membership in
 * the sequence wanted, first to last. */
export interface RouteReorderRequest {
	assignment_ids: number[];
}

/** Where a stop's arrival landed against its access window. `late` is flagged,
 * never blocked; `unroutable` means the engine could not snap the point;
 * `no_point` means the Assignment names no target location. */
export type RoutePlanArrivalState =
	| "ok"
	| "waited"
	| "late"
	| "unroutable"
	| "no_point";

/** `ok` is a usable plan; `partial` is usable but some stops could not be
 * placed; `unavailable` is the engine being down; `failed` is the engine
 * answering with an error document. The two failure states carry no path. */
export type RoutePlanStatus = "ok" | "partial" | "unavailable" | "failed";

/** A district's base point as the plan used it. */
export interface RoutePlanBase {
	lng: number;
	lat: number;
	label: string | null;
}

/** One stop of the drive plan: its sequence slot, its target point, and when
 * the technician arrives and leaves. `legSeconds`/`legMeters` are the travel
 * that reached it — absent for the first stop when no base is configured. */
export interface RoutePlanStop {
	assignmentId: number;
	position: number;
	/** [lng, lat], or null when the Assignment names no target location. */
	point: [number, number] | null;
	arrivalState: RoutePlanArrivalState;
	dwellSeconds: number;
	legSeconds: number | null;
	legMeters: number | null;
	arriveAt: string | null;
	departAt: string | null;
	waitSeconds: number;
}

/** One Route's drive plan (`POST /api/route/{id}/plan`). A derived reading:
 * never a property of the Route, never an input to ordering or dispatch. */
export interface RoutePlan {
	routeId: number;
	status: RoutePlanStatus;
	costing: string;
	base: RoutePlanBase | null;
	stops: RoutePlanStop[];
	/** Members left out of the path because the Route has not sequenced them. */
	unsequenced: number;
	distanceMeters: number | null;
	travelSeconds: number | null;
	serviceSeconds: number | null;
	waitSeconds: number | null;
	totalSeconds: number | null;
	startsAt: string | null;
	finishesAt: string | null;
	/** GeoJSON MultiLineString, or null when there is no line to draw. */
	path: GeoJSON.MultiLineString | null;
	engineVersion: string | null;
	tilesetModified: string | null;
	error: string | null;
	computedAt: string;
}

/** One stop of the current order as the suggestion found it. */
export interface OrderSuggestionStop {
	assignmentId: number;
	position: number;
	arriveAt: string;
}

/** A stop the proposed order cannot serve inside its window, and why. */
export interface OrderSuggestionConflict {
	assignmentId: number;
	reason: string;
}

/** The body of `POST /api/route/{id}/order/suggestion`: a read. Accepting it is
 * the existing `PUT /api/route/{id}/order` door. `improved` is false when the
 * proposal equals the current order. */
export interface OrderSuggestion {
	assignmentIds: number[];
	travelSeconds: number;
	serviceSeconds: number;
	waitSeconds: number;
	totalSeconds: number;
	finishesAt: string;
	stopsBefore: OrderSuggestionStop[];
	conflicts: OrderSuggestionConflict[];
	improved: boolean;
}

/** A Route the day board left out, and why (a cancelled Route is not planned). */
export interface OperationsPlanSkipped {
	routeId: number;
	reason: string;
}

/** `POST /api/operations/plans`: the day board — every Route overlapping the
 * window with its plan, plus the ones passed over. */
export interface OperationsPlans {
	plans: RoutePlan[];
	skipped: OperationsPlanSkipped[];
}

/** `GET /api/assignment`: one page plus the district's full count. */
export interface AssignmentListEnvelope {
	assignments: SyncAssignment[];
	total: number;
}

/** `GET /api/route`: one page plus the district's full count. */
export interface RouteListEnvelope {
	routes: SyncRoute[];
	total: number;
}

// Catalog revisions and the registry imports queue.
//
// product_catalog carries a monotonically increasing revision; a district
// product records the revision it last accepted from. "Behind" is that stored
// comparison, never a name or date guess. Admins see the registry-versus-catalog
// pending changes; districts see what the catalog now says against their row.

export interface PesticideCatalogPendingChange {
	field: string;
	catalog_value: string | null;
	registry_value: string | null;
	registry_retrieved_at: string;
	detected_at: string;
	resolved_at: string | null;
	resolution: string | null;
	resolved_by: string | null;
	dismiss_reason: string | null;
}

export interface PesticideCatalogChangeLogEntry {
	revision: number;
	field: string;
	old_value: string | null;
	new_value: string | null;
	accepted_by: string;
	accepted_at: string;
}

// CatalogStalenessHolder is one district holding a catalog entry and behind its
// revision. lag_seconds is computed server-side from the district's
// catalog_accepted_at (the moment it last accepted a revision).
export interface CatalogStalenessHolder {
	organization_id: number;
	organization_name: string;
	owner_name: string | null;
	owner_email: string | null;
	district_product_id: number;
	district_product_name: string;
	catalog_revision: number;
	district_revision: number | null;
	revisions_behind: number;
	lag_seconds: number;
	lag_since: string;
	current_values: Record<string, string>;
}

export interface OrganizationStaleness {
	organization_id: number;
	organization_name: string;
	owner_name: string | null;
	owner_email: string | null;
	behind_products: number;
	oldest_lag_seconds: number;
	most_lagged_product: string;
	most_lagged_catalog_id: number;
	most_lagged_revisions_behind: number;
}

export interface CatalogImportRow {
	ca_reg_no: string;
	registration_no: string | null;
	epa_reg_no: string | null;
	dpr_registrant_firm_no: string | null;
	dpr_registrant_name: string | null;
	epa_registrant_firm_no: string | null;
	epa_registrant_name: string | null;
	product_name: string;
	product_status: string | null;
	registration_type: string | null;
	formulation: string | null;
	state: "pending" | "in_catalog" | "promoted" | "dismissed";
	source: string;
	source_retrieved_at: string;
	label_accepted_date: string | null;
	promoted_catalog_id: number | null;
	promoted_at: string | null;
	promoted_by: string | null;
	dismissed_at: string | null;
	dismissed_by: string | null;
	dismiss_reason: string | null;
	/** The catalog row the registration already resolves to. */
	catalog_match_id: number | null;
	catalog_match_name: string | null;
	/** How many catalog rows share the registration; 0 when it has none. */
	catalog_match_count: number;
}

export interface CatalogImportSiteUse {
	site_code: number;
	site_name: string;
}

export interface CatalogImportIngredient {
	dpr_chem_code: string | null;
	pc_code: string | null;
	cas_number: string | null;
	name: string;
	percent: number | null;
	source: string;
}

export interface CatalogImportLabel {
	id: number;
	epa_reg_no: string;
	ppls_document_url: string;
	document_url: string | null;
	stored_path: string;
	sha256: string;
	byte_size: number;
	ppls_accepted_date: string | null;
	retrieved_at: string;
	is_current: boolean;
}

export interface CatalogImportDiscrepancy {
	kind: "cdpr_only" | "ppls_only";
	detail: string;
	observed_at: string;
}

export interface CatalogImportSuggested extends PesticideCatalogFieldsRequest {
	category: string;
}

export interface CatalogImportDetail extends CatalogImportRow {
	registration_type_code: string | null;
	formulation_code: string | null;
	product_status_code: string | null;
	product_status_date: string | null;
	registered_on: string | null;
	cancel_flag: boolean | null;
	restricted_use: boolean;
	restricted_use_source: string | null;
	site_uses: CatalogImportSiteUse[];
	ingredients: CatalogImportIngredient[];
	labels: CatalogImportLabel[];
	discrepancies: CatalogImportDiscrepancy[];
	promotion: {
		catalog_product_id: number;
		catalog_product_slug: string;
		promoted_by: string;
		promoted_at: string;
		note: string | null;
	} | null;
	catalog_match: CatalogImportCatalogMatch | null;
	/** The other catalog rows the registration names, when it is ambiguous. */
	catalog_match_alternatives: CatalogImportCatalogMatch[];
	suggested: CatalogImportSuggested;
}

export interface CatalogImportCatalogMatch {
	id: number;
	product_id: string;
	name: string;
	category: string;
	epa_registration: string | null;
	revision: number;
	retired: string | null;
}

export interface CatalogImportListResponse {
	imports: CatalogImportRow[];
	total: number;
	counts: Record<string, number>;
}

export interface CatalogUpdateField {
	field: string;
	catalog_value: string;
	district_value: string;
	accepted_baseline: string;
	district_modified: boolean;
	changed: boolean;
}

export interface CatalogUpdatePreview {
	product_id: number;
	catalog_product_id: number | null;
	catalog_slug: string | null;
	catalog_name: string;
	catalog_revision: number;
	district_revision: number | null;
	behind: boolean;
	revisions_behind: number;
	lag_seconds: number;
	lag_since: string | null;
	fields: CatalogUpdateField[];
}

// POST /api/pesticide-catalog/requests — a district's request for a product the
// shared catalog does not carry. Every identity field is optional: a district
// may not know a registration, and the request is still a request. The server
// never matches these into the catalog; a curator reads them and decides.
export interface PesticideCatalogRequestCreate {
	product_text: string;
	reason: string;
	epa_reg_no?: string;
	ca_reg_no?: string;
	brand_name?: string;
	distributor?: string;
}

// The registry record a supplied registration names, with the catalog
// resolution the imports queue would show for it. It is evidence for the
// curator, not a resolution: null when no registration was supplied or the
// registry does not carry it.
export interface PesticideCatalogRequestCandidate {
	ca_reg_no: string;
	product_name: string;
	registration_no: string | null;
	epa_reg_no: string | null;
	registrant_name: string | null;
	// state is the imports-queue vocabulary: pending, in_catalog, promoted or
	// dismissed.
	state: string;
	promotion: {
		catalog_product_id: number;
		catalog_product_slug: string;
		promoted_by: string;
		promoted_at: string;
		note: string | null;
	} | null;
	catalog_match: CatalogImportCatalogMatch | null;
	// catalog_match_count counts the catalog rows sharing the registration; a
	// value above one means the resolution was ambiguous.
	catalog_match_count: number;
}

// One catalog request as the district submitted it and the curator resolved it.
export interface PesticideCatalogRequest {
	id: number;
	created: string;
	organization_id: number;
	organization_name: string;
	user_id: number;
	user_name: string;
	product_text: string;
	reason: string;
	epa_reg_no: string | null;
	ca_reg_no: string | null;
	brand_name: string | null;
	distributor: string | null;
	state: "open" | "fulfilled" | "declined";
	resolved_at: string | null;
	resolved_by: string | null;
	resolution_reason: string | null;
	// The catalog entry a fulfilment promoted or reused; null while open or
	// when declined. A repeated request for an entry that already exists names
	// the same id — fulfilment never creates a second row.
	catalog_product_id: number | null;
	catalog_product_slug: string | null;
	candidate: PesticideCatalogRequestCandidate | null;
}

// GET /api/sudo/catalog-requests — the curator queue with state counts.
export interface PesticideCatalogRequestListResponse {
	requests: PesticideCatalogRequest[];
	total: number;
	counts: Record<string, number>;
}

// POST /api/sudo/catalog-requests/{id}/fulfil — the catalog fields used when
// the matched registry record still needs promoting. They are ignored when the
// catalog already carries the registration. category is required only in the
// promoting case.
export interface PesticideCatalogRequestFulfil {
	category?: string;
	product_id?: string;
	name?: string;
	note?: string;
}

// ---------------------------------------------------------------------------
// Site tagging and symbology (doc/in-progress/site-tagging-and-symbology.md):
// the district's tag vocabulary, the symmetric symbol library, and the catalog
// the client resolves once per session into the Site layers' paint.

/** The marker vocabulary a symbol draws with (§1 step 1, `symbology_marker_shape`). */
export type SymbolShape =
	| "circle"
	| "square"
	| "triangle"
	| "diamond"
	| "pin"
	| "hexagon"
	| "star";

/** One of the district's tags (GET /api/site-tag, §7.1).
 *
 * `slug` is the server's stable spelling of `name` — the ledger and the filter
 * address a tag by id, the slug is the part a human reads — and `priority` is
 * §4.1's rank: among the tags a Site carries, the highest priority wins the
 * symbol. `retired` restates `retired_at IS NOT NULL` as the boolean every list,
 * chip and picker branches on; `retired_at` is carried beside it so the detail
 * screen renders the stamp without a second read. `site_count` ("used by N
 * Sites", §8.2) is a join, so it rides on the list response only and a write's
 * echo leaves it off. */
export interface SiteTag {
	id: number;
	name: string;
	slug: string;
	description: string;
	color: string;
	icon_uuid?: string | null;
	priority: number;
	retired: boolean;
	retired_at?: string | null;
	created: string;
	updated: string;
	site_count?: number;
}

/** One reusable style in the symbology library (GET /api/symbol, §7.2).
 *
 * `owner` names the library the symbol lives in: "district" symbols are the
 * shared ones a binding may name (§4.2 arms 2 and 4), "user" ones are private to
 * their creator and reachable only through that person's own overrides (arms 1
 * and 3). Every paint attribute is nullable on purpose — a symbol that states
 * only an outline is legal, and §4.3 falls each null attribute to the kind's
 * product default. */
export interface Symbol {
	id: number;
	name: string;
	description: string;
	owner: "district" | "user";
	retired: boolean;
	shape: SymbolShape;
	fill_color: string | null;
	stroke_color: string | null;
	fill_opacity: number;
	stroke_opacity: number;
	stroke_width: number;
	size: number | null;
	icon_uuid: string | null;
	created: string;
	updated: string;
	/** How many bindings and overrides name this symbol — the library's "in use
	 * by N" and the reason a delete can answer 409. */
	usage_count: number;
}

/** A style as the catalog resolves it (§7.3): a symbol's paint attributes plus
 * the identity of the row they came from.
 *
 * `symbol_id` and `name` are null for the product default, which is no symbol's
 * row. `source` is the §4.2 arm that won — `user` / `district` / `builtin` — and
 * it is what the override surfaces' badge reads: the client never re-derives the
 * precedence order, it displays the server's answer. */
export interface SymbolStyle {
	symbol_id: number | null;
	name: string | null;
	source: "user" | "district" | "builtin";
	shape: SymbolShape;
	fill_color: string | null;
	stroke_color: string | null;
	fill_opacity: number;
	stroke_opacity: number;
	stroke_width: number;
	size: number | null;
	icon_uuid: string | null;
}

/** One subject kind's arm set from the catalog (§7.3).
 *
 * `builtin` is §4.2 arm 5, the product default the chain bottoms out in;
 * `district` is arm 4, the district's kind binding; and `default` is arms 3→5
 * *pre-merged for the caller* — the row a Site with no winning tag draws with,
 * which is why ts/lib/symbology.ts' `symbolPaint` can name it as one `match`
 * default branch. `taggable` says whether the tag arms (1 and 2) are consulted
 * for this kind at all; only `site` is taggable in v1. */
export interface SymbologyKind {
	code: string;
	label: string;
	taggable: boolean;
	builtin: SymbolStyle;
	district: SymbolStyle;
	default: SymbolStyle;
}

/** One tag's entry in the catalog: the tag's own identity — the chip's colour
 * and icon come from here, never from the symbol — plus the style arms 1/2
 * resolved to. */
export interface SymbolTagStyle {
	tag_id: number;
	name: string;
	slug: string;
	color: string;
	icon_uuid: string | null;
	priority: number;
	retired: boolean;
	style: SymbolStyle;
}

/** GET /api/symbology/catalog (§7.3) — everything the client needs to draw the
 * district's Sites, resolved server side once per session (§4.5): the kinds with
 * their three arms, the tags with the style each draws with (`tags` is every
 * live tag plus any retired tag a live assignment still names, so a Site tagged
 * with a since-retired tag still gets its style), the symbol library the pickers
 * offer, and the icons those symbols may reference. `updated` is the newest
 * `updated` among the rows the catalog was built from, so a caller can tell a
 * stale catalog from a fresh one. */
export interface SymbologyCatalog {
	updated: string;
	kinds: SymbologyKind[];
	tags: SymbolTagStyle[];
	symbols: Symbol[];
	icons: { uuid: string; content_type: string }[];
}

/** GET /api/site-tag/usage — the district's Site split and every tag's usage in
 * one read (§7.1, computed in SQL from the same ranking §4.1 states). It is what
 * the symbology pane's "applies to untagged Sites and to tags the district has
 * not styled (N sites)" is built from: `sites_untagged` is exactly the set a
 * kind default reaches, because the ranked tag set R only carries district-bound
 * tags, so a Site whose tags are all unbound has no winner and counts here.
 * `leads` is where the tag wins a Site; `assignments` is how many Sites carry it
 * at all (the difference is what a higher-priority tag shadows). */
export interface SiteTagUsage {
	sites_total: number;
	sites_untagged: number;
	tags: { tag_id: number; leads: number; assignments: number }[];
}

/** POST /api/site-tag/assign — the bulk tag write on the ledger (§7.4, §8.2).
 *
 * `spec` IS the selection: the same spec the ledger, counts and map resolve, so
 * the write lands on exactly the Sites on screen and never on the loaded page.
 * `add` and `remove` are tag ids; the same id in both lists is a 400, and a
 * batch that would push any kept Site past its 12-tag cap is refused whole with
 * the Sites named (§7.5). */
export interface SiteTagAssignRequest {
	spec: MapReviewSpec;
	selection?: MapReviewBulkSelection;
	add: number[];
	remove: number[];
}

/** POST /api/site-tag/assign's answer: the (Site, tag) pairs created and
 * removed, and how many Sites the spec kept. Pairs, not Sites, because a Site
 * carrying the tag already contributes no `assigned` row. */
export interface SiteTagAssignResult {
	assigned: number;
	removed: number;
	sites: number;
}

/** A member as the technician-name card shows them. */
export interface TechnicianNameMember {
	avatar: string | null;
	displayName: string;
	id: number;
	isActive: boolean;
	uri: string;
	username: string;
}

/**
 * One FieldSeeker technician value and how it currently resolves
 * (doc/design/integrations/fieldseeker/technician-identity.md).
 */
export interface TechnicianName {
	aliasId: number | null;
	ignored: boolean;
	/** Distinct source records carrying the value: the sort key. */
	records: number;
	user: TechnicianNameMember | null;
	value: string;
}

/** What an assign, create or ignore reports back about one value. */
export interface TechnicianNameAction {
	aliasId: number | null;
	ignored: boolean;
	previousUser: TechnicianNameMember | null;
	/** Rows the previous owner is still credited with. */
	remainingRecords: number;
	user: TechnicianNameMember | null;
	value: string;
}

/** One source spelling a member owns. */
export interface UserAlias {
	created: string;
	id: number;
	origin: string;
	sourceSystem: string;
	value: string;
}

/** What a merge moves, both as a preview and as its result. */
export interface UserMergeCounts {
	aliases: number;
	records: number;
}

/** One row of GET /api/pronunciation/sessions, the root-only field-feedback
 * list (doc/in-progress/nidus-notes/product-pronunciation-feedback.md §5.5).
 * Every organization's sessions are visible here; `take_count` counts the takes
 * advertised in the manifest and `uploaded_count` those whose WAV landed. */
export interface PronunciationReviewSessionSummary {
	id: number;
	uuid: string;
	organization_id: number;
	organization_name: string;
	user_id: number | null;
	username: string | null;
	app_version: string | null;
	build: string | null;
	os_version: string | null;
	hardware: string | null;
	locale: string | null;
	status: string;
	review_status: string;
	started_at: string | null;
	finished_at: string | null;
	created: string;
	take_count: number;
	uploaded_count: number;
	training_excluded: boolean;
	raw_deleted_at: string | null;
}

/** GET /api/pronunciation/sessions: one page plus the unpaged total. */
export interface PronunciationReviewSessionList {
	sessions: PronunciationReviewSessionSummary[];
	total: number;
	limit: number;
	offset: number;
}

/** How closely an expected form sits to a transcript's best window. `matched`
 * is the server's verdict; the distances are evidence for a near miss. */
export interface PronunciationProductMatchMetric {
	matched: boolean;
	matched_form: string | null;
	nearest_window: string;
	distance: number;
	compact_distance: number;
	expected_normalized: string;
	heard_normalized: string;
	word_error_rate: number;
}

/** One partial hypothesis and whether the expected product was heard in it. */
export interface PronunciationPartialMatchMetric {
	index: number;
	/** Seconds into the take; absent when the partial carried no timestamp. */
	at?: number;
	text: string;
	matched: boolean;
	distance: number;
	compact_distance: number;
	nearest_window: string;
	is_first_hit: boolean;
}

/** The deterministic product match written into each analysis's metrics. */
export interface PronunciationTakeMetrics {
	product_name: string;
	is_custom_mix: boolean;
	context_id: string;
	prompt: string;
	expected_forms: string[];
	transcript_present: boolean;
	final: PronunciationProductMatchMetric;
	partial_count: number;
	partials: PronunciationPartialMatchMetric[];
	partial_matched: boolean;
	/** The product was heard mid-utterance but lost by the final transcript. */
	partial_only: boolean;
	best_partial_index: number | null;
	best_partial_at: number | null;
	best_partial_window: string;
	best_partial_distance: number;
	first_partial_hit_at: number | null;
	/** Server-suggested cause (`none`, `no-speech`, `acoustic-or-alias`, ...);
	 * a hint for the reviewer, never a stored classification. */
	failure_hint: string;
}

/** The `metrics` column's stored shape: the match plus server-side counters. */
export interface PronunciationAnalysisMetrics {
	engine: string;
	analyzed_at: string;
	product_match: PronunciationTakeMetrics;
	partial_only: boolean;
	failure_hint: string;
	server_asr_note?: string;
}

/** One engine's run over a take's WAV. */
export interface PronunciationReviewAnalysis {
	id: number;
	take_id: number;
	engine: string;
	model_version: string | null;
	config: unknown;
	transcript: string | null;
	segments: unknown;
	partials: unknown;
	metrics: PronunciationAnalysisMetrics | null;
	created: string;
}

/** One recorded take of a session's detail read. */
export interface PronunciationReviewTake {
	id: number;
	take_uuid: string;
	prompt_index: number;
	retry_index: number;
	product_name: string;
	district_product_id: number | null;
	catalog_product_id: string | null;
	is_custom_mix: boolean;
	context_id: string | null;
	prompt: string;
	expected_forms: string[] | null;
	audio_path: string | null;
	audio_sha256: string | null;
	audio_bytes: number | null;
	uploaded_at: string | null;
	transcript: string | null;
	segments: unknown;
	partials: unknown;
	speech: unknown;
	failure_class: string | null;
	reviewer_note: string | null;
	created: string;
	analyses: PronunciationReviewAnalysis[];
}

/** A device's remote-deletion request and its resolution. */
export interface PronunciationReviewDeletion {
	id: number;
	status: string;
	reason: string | null;
	requested_by_user_id: number | null;
	requested_at: string;
	raw_deleted_at: string | null;
	training_excluded_at: string | null;
	resolved_at: string | null;
	resolved_by_user_id: number | null;
	note: string | null;
}

/** GET /api/pronunciation/sessions/{uuid}: the whole trace for review. */
export interface PronunciationReviewSession {
	id: number;
	uuid: string;
	organization_id: number;
	organization_name: string;
	user_id: number | null;
	username: string | null;
	device_id: number | null;
	app_version: string | null;
	build: string | null;
	os_version: string | null;
	hardware: string | null;
	audio_route: string | null;
	locale: string | null;
	notice_version: string | null;
	notice_shown_at: string | null;
	custom_language_model_version: string | null;
	config: unknown;
	manifest: unknown;
	status: string;
	review_status: string;
	reviewed_by_user_id: number | null;
	reviewed_at: string | null;
	reviewer_notes: string | null;
	training_excluded: boolean;
	training_excluded_at: string | null;
	raw_deleted_at: string | null;
	started_at: string | null;
	finished_at: string | null;
	created: string;
	updated: string;
	takes: PronunciationReviewTake[];
	deletions: PronunciationReviewDeletion[];
}

/** PATCH /api/pronunciation/sessions/{uuid}/review. */
export interface PronunciationReviewUpdateRequest {
	review_status: "reviewed" | "unreviewed";
	notes?: string | null;
}

/** The reviewer classifications POST /api/pronunciation/takes/{id}/finding
 * accepts; `null` clears the recorded finding. */
export type PronunciationFailureClass =
	| "acoustic"
	| "finalization-regression"
	| "alias-gap"
	| "context-not-applicable"
	| "no-speech"
	| "technical"
	| "other";

/** POST /api/pronunciation/takes/{id}/finding. */
export interface PronunciationTakeFindingRequest {
	failure_class: PronunciationFailureClass | null;
	note?: string | null;
}

/** POST /api/site-tag/assign — the bulk tag write on the ledger (§7.4, §8.2).
 *
 * `spec` IS the selection: the same spec the ledger, counts and map resolve, so
 * the write lands on exactly the Sites on screen and never on the loaded page.
 * `add` and `remove` are tag ids; the same id in both lists is a 400, and a
 * batch that would push any kept Site past its 12-tag cap is refused whole with
 * the Sites named (§7.5). */
export interface SiteTagAssignRequest {
	spec: MapReviewSpec;
	add: number[];
	remove: number[];
}

/** POST /api/site-tag/assign's answer: the (Site, tag) pairs created and
 * removed, and how many Sites the spec kept. Pairs, not Sites, because a Site
 * carrying the tag already contributes no `assigned` row. */
export interface SiteTagAssignResult {
	assigned: number;
	removed: number;
	sites: number;
}

// ---------------------------------------------------------------------------
// Planning loop (doc/in-progress/planning-loop-implementation-plan.md)
// ---------------------------------------------------------------------------

/** One Signal as the Planning Evidence rail reads it. */
export interface PlanningSignal {
	addressed?: string | null;
	addressor?: number | null;
	created: string;
	creator: number;
	effect?: "promotive" | "suppressive" | "contextual" | null;
	id: number;
	location: Location;
	pools: Pool[];
	provenance_class?:
		| "production"
		| "nuisance"
		| "environmental"
		| "administrative"
		| null;
	report?: PublicReport | null;
	site?: Site | null;
	species?: string | null;
	type: string;
	weight?: number | null;
}

export interface LeadSignalDetail {
	signal_id: number;
	effect: "promotive" | "suppressive" | "contextual";
	role: "composition" | "context";
}

export interface LeadClosureEvidence {
	kind: "observation" | "communication";
	id: number;
}

export interface LeadAssignmentDetail {
	id: number;
	state: string;
	kind: string | null;
	technician_id: number;
	route_id: number | null;
	completed_at: string | null;
}

export interface LeadDetail {
	assignments: LeadAssignmentDetail[];
	cells: string[];
	closed_at: string | null;
	closed_by: number | null;
	closure_evidence: LeadClosureEvidence[];
	created: string;
	due_at: string | null;
	h3_resolution: number | null;
	id: number;
	name: string | null;
	priority: "high" | "normal" | "low";
	rationale: string | null;
	signals: LeadSignalDetail[];
	site_id: number | null;
	state: "open" | "closed";
	updated: string;
}

export interface WorkOrderDetail {
	cancel_reason: string | null;
	cancelled_at: string | null;
	cells: string[];
	created: string;
	description: string;
	due_at: string | null;
	h3_resolution: number | null;
	id: number;
	origin:
		| "manual"
		| "lead"
		| "assignment"
		| "review"
		| "mosquito_control_plan"
		| "situation";
	origin_assignment_id: number | null;
	origin_lead_id: number | null;
	priority: "high" | "normal" | "low";
	requirements: string | null;
	state: "open" | "converted" | "cancelled";
	updated: string;
}

export interface PlanningDecisionDetail {
	created: string;
	creator: number;
	id: number;
	kind: "no_lead";
	rationale: string;
	signals: number[];
}

export interface LeadEvidenceCandidate {
	assignment_id: number | null;
	at: string;
	id: number;
	kind: "observation" | "communication";
	note: string | null;
}

export interface LeadEvidence {
	communications: LeadEvidenceCandidate[];
	observations: LeadEvidenceCandidate[];
}

export interface OperationsWorkRow {
	due_at: string | null;
	id: number;
	origin: "lead" | "work_order" | "direct";
	priority: "high" | "normal" | "low" | null;
	state: string;
	target_summary: string;
	title: string;
}

/** One technician's last known fix, as GET /api/operations/technician-positions
 * returns it: the newest valid Traccar position across the member's
 * non-revoked devices. `avatar` is "" when they have none, and the map then
 * draws `initials` instead. A member with no device, or no valid fix, is absent
 * — the map cannot place them. */
export interface OperationsTechnicianPosition {
	user_id: number;
	display_name: string;
	initials: string;
	avatar: string;
	latitude: number;
	longitude: number;
	/** The fix's own timestamp (`device_position.device_time`), which is what
	 * the staleness ramp measures — not `server_time`, when it was accepted. */
	fix_time: string;
	server_time: string;
	accuracy_meters: number | null;
}

// ---------------------------------------------------------------------------
// Traccar-compatible tracking enrolment (doc/in-progress/traccar.md §9.1): the
// operator surface that mints the ingest credential a phone's Traccar client
// uploads positions with. Shapes mirror platform/types/traccar.go.
// ---------------------------------------------------------------------------

// TrackingDevice is one enrolled ingest credential. `uniqueId` is the value the
// client types in as its device identifier, and `revokedAt` is set once the
// credential is withdrawn — a revoked device can no longer upload.
export interface TrackingDevice {
	id: number;
	label: string;
	uniqueId: string;
	createdAt: string;
	lastSeenAt: string | null;
	revokedAt: string | null;
}

// TrackingDeviceEnrolRequest is the enrolment body: `label` is required; an
// optional `username` enrols on another member's behalf, which the
// account-owner role is required for; and an optional `uniqueId` pastes the
// identifier the app generated on first launch — the server generates one in
// the same 8-digit shape when it is absent.
export interface TrackingDeviceEnrolRequest {
	label: string;
	username?: string;
	uniqueId?: string;
}

// TrackingDeviceEnrolResponse is the one time the server states the token:
// `serverUrl` is the ready-to-paste Traccar "Server URL" and `token` is the
// credential inside it. The token cannot be retrieved again. `configuration` is
// the exact string the app's QR scanner expects (server URL plus identifier and
// tracking settings), and `qrCode` is that string as a PNG data URI.
export interface TrackingDeviceEnrolResponse {
	device: TrackingDevice;
	token: string;
	serverUrl: string;
	configuration: string;
	qrCode: string;
}

// GET /api/sudo/valhalla, /api/sudo/valhalla/builds — the Valhalla routing surface.
export interface ValhallaEngineStatus {
	url: string;
	reachable: boolean;
	version: string;
	has_tiles: boolean;
	has_admins: boolean;
	has_timezones: boolean;
	tileset_last_modified: number;
	bbox: GeoJSON.FeatureCollection | null;
	actions: string[];
	error: string;
}

export interface ValhallaBuild {
	id: string;
	run_id: number;
	/** The district that requested the build, or null: a build is
	 * deployment-wide and crops every served district's service area. */
	organization_id: number | null;
	organization_name: string;
	status: "queued" | "running" | "succeeded" | "failed";
	stage: string;
	error: string;
	started_at: string | null;
	completed_at: string | null;
	bytes: number;
	tile_count: number;
	source_url: string;
	bbox: number[];
}

export interface ValhallaCoverage {
	organization_id: number;
	name: string;
	slug: string;
	bbox: number[];
	covered: boolean;
	live_build_id: string;
}

/** A district the tile set will not cover, and why: it holds no active Nidus
 * core license, or has no service area to crop to. A build serves the licensed
 * districts only, so this is the rest of the organizations in the database. */
export interface ValhallaOmittedDistrict {
	organization_id: number;
	name: string;
	slug: string;
	reason: string;
}

export interface ValhallaOverview {
	engine: ValhallaEngineStatus;
	live_build_id: string;
	data_dir: string;
	builds: ValhallaBuild[];
	/** The licensed districts, with whether the live tile set covers each. */
	coverage: ValhallaCoverage[];
	/** The organizations the tile set leaves out. */
	omitted: ValhallaOmittedDistrict[];
}

export interface ValhallaBuildDetail {
	build: ValhallaBuild;
	log: string;
}

export interface ValhallaPromoteResponse {
	build_id: string;
	promoted: boolean;
}
